//opt/projects/saas_odonto/frontend/src/services/api.ts

import axios, { AxiosError, AxiosResponse } from 'axios';
import type { MessageDeliveryError, TemplateMessageSnapshot } from '../types/templateMessage.ts';
import { conversationContactId, conversationEventKey } from '../utils/conversationIdentity.ts';
import { formatChatTimestamp } from '../utils/date.ts';
import { convertWahaUrlToProxy } from '../utils/wahaProxy.ts';
import type { TagResponse } from './apiTags.ts';
import { resolveApiUrl } from './apiBaseUrl.ts';
import {
  acknowledgePlatformPendingCommands,
  checkPlatformAvailability,
  configurePlatformAvailability,
  getPlatformAvailabilityIdentity,
  getPlatformAvailabilitySnapshot,
  isPlatformAvailabilityError,
  isPlatformCommand,
  isSafePlatformQuery,
  PlatformAvailabilityError,
  type PlatformAbortSignal,
  reportPlatformInterruption,
  reportPlatformPendingCommand,
  waitForPlatformAvailability,
} from './platformAvailability.ts';

declare var process: {
  env: {
    [key: string]: string | undefined;
  };
};

export interface TelnyxNumber {
  id: string;
  phone_number: string;
  status: string;
  connection_name: string;
  assigned_to?: { clinic_id: number; clinic_name: string };
  tags?: string[];
}

export interface WhatsAppNumber {
  id: string;
  display_phone_number: string;
  verified_name: string;
  quality_rating: string;
  assigned_to?: { clinic_id: number; clinic_name: string };
}

export const API_URL = resolveApiUrl(
  process.env.REACT_APP_API_URL,
  typeof window === 'undefined' ? undefined : window.location
);

// Função auxiliar para determinar MIME type baseado na extensão
function getMimeType(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop() || '';
  const mimeTypes: { [key: string]: string } = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'mp4': 'video/mp4',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',
    'mkv': 'video/x-matroska',
    'webm': 'video/webm',
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'oga': 'audio/ogg',
    'ogg': 'audio/ogg',
    'opus': 'audio/opus',
    'mpga': 'audio/mpeg'
  };
  return mimeTypes[ext] || 'application/octet-stream';
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    Accept: 'application/json',
  },
  withCredentials: true,
  timeout: 240000,
});

let platformSessionRevision = 0;

type PlatformApiConfig = {
  url?: string;
  baseURL?: string;
  method?: string;
  params?: any;
  signal?: PlatformAbortSignal;
  _skipPlatformAvailability?: boolean;
  /** Não trata falha desta chamada como interrupção global da plataforma. */
  _skipPlatformInterruption?: boolean;
  _platformIdentity?: string;
  _platformRetry?: number;
  _platformPendingRecorded?: boolean;
};

function isOwnPlatformRequest(config?: PlatformApiConfig): boolean {
  if (!config?.url || config._skipPlatformAvailability) return false;
  try {
    const url = new URL(config.url, config.baseURL || API_URL);
    const origins = [new URL(API_URL).origin];
    if (typeof window !== 'undefined' && window.location.hostname === 'members.sminy.ai') {
      origins.push(window.location.origin);
    }
    return origins.includes(url.origin);
  } catch { return false; }
}

configurePlatformAvailability({
  identity: () => `${platformSessionRevision}:${getStoredBrowserIdentity() || ''}`,
  probe: async () => {
    const config = {
      _skipPlatformAvailability: true,
      _skipAuthRefresh: true,
      timeout: 3_000,
      validateStatus: () => true,
      params: { _t: Date.now() },
      ...(typeof window !== 'undefined' && window.location.hostname === 'members.sminy.ai'
        ? { baseURL: window.location.origin } : {}),
    };
    const response = await api.get('/api/platform/availability', config);
    if (response.status === 404) {
      // Compatibilidade com o backend anterior durante a publicação gradual.
      // Este fallback confirma transporte, não prontidão completa da plataforma.
      const members = typeof window !== 'undefined' && window.location.hostname === 'members.sminy.ai';
      const legacy = await api.get(members ? '/api/members/me' : '/auth/browser/session', config);
      const contentType = String(legacy.headers?.['content-type'] || '');
      const json = /\bapplication\/(?:json|problem\+json)\b/i.test(contentType);
      const sessionShape = members
        ? typeof legacy.data?.id === 'number' && typeof legacy.data?.csrf_token === 'string'
        : legacy.data?.user && typeof legacy.data.user === 'object' && typeof legacy.data.csrf_token === 'string';
      const unauthorizedShape = typeof legacy.data?.detail === 'string' || typeof legacy.data?.detail?.code === 'string';
      if (!json || !((legacy.status === 200 && sessionShape) || (legacy.status === 401 && unauthorizedShape))) {
        throw new Error('Transporte indisponível');
      }
      return { state: 'available', ready: true, legacy: true };
    }
    const data = response.data;
    if (data?.contract_version !== 1 || !['available', 'paused', 'unavailable'].includes(data.state)) {
      throw new Error('Resposta de prontidão indisponível');
    }
    return {
      state: data.state,
      ready: response.status === 200 && data.ready === true,
      retryAfterSeconds: data.retry_after_seconds,
    };
  },
});

function platformFailureKind(error: any): 'planned' | 'edge' | 'candidate' | null {
  if (!axios.isAxiosError(error) || !isOwnPlatformRequest(error.config)) return null;
  if ((error.config as PlatformApiConfig)._skipPlatformInterruption) return null;
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  if (status === 503 && detail?.code === 'platform_maintenance' && detail?.request_processed === false) return 'planned';
  // Falhas com contrato de negócio (ex.: Clinicorp incerto) permanecem intactas.
  if (detail && typeof detail === 'object' && detail.code) return null;
  if (!error.response && ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNREFUSED'].includes(error.code || '')) return 'edge';
  if (status && status >= 520 && status <= 524) return 'edge';
  if (![502, 503, 504].includes(status || 0)) return null;
  const body = typeof error.response?.data === 'string' ? error.response.data : typeof detail === 'string' ? detail : '';
  if (/cloudflare|<html|<!doctype html/i.test(body)) return 'edge';
  return 'candidate';
}

async function recoverPlatformRequest(error: any): Promise<{ handled: false } | { handled: true; response: any }> {
  const kind = platformFailureKind(error);
  if (!kind) return { handled: false };
  const config = error.config as PlatformApiConfig;
  if (kind !== 'candidate') reportPlatformInterruption(kind === 'planned');
  const availability = await checkPlatformAvailability();
  if (kind === 'candidate' && availability.ready) return { handled: false };
  if (config._platformIdentity && config._platformIdentity !== getPlatformAvailabilityIdentity()) {
    throw new PlatformAvailabilityError('platform_unavailable');
  }
  const safeQuery = isSafePlatformQuery(config);
  if (!safeQuery) {
    if (kind === 'planned') throw new PlatformAvailabilityError('platform_maintenance', false);
    if (!isPlatformCommand(config) || String(config.url || '').startsWith('/auth/browser/')) {
      throw new PlatformAvailabilityError('platform_unavailable');
    }
    if (!config._platformPendingRecorded) {
      config._platformPendingRecorded = true;
      reportPlatformPendingCommand();
    }
    throw new PlatformAvailabilityError('platform_result_unknown');
  }
  if ((config._platformRetry || 0) >= 1) throw new PlatformAvailabilityError('platform_unavailable');
  const requestIdentity = config._platformIdentity ?? getPlatformAvailabilityIdentity();
  await waitForPlatformAvailability({ signal: config.signal, identity: requestIdentity });
  await new Promise(resolve => setTimeout(resolve, Math.floor(Math.random() * 250)));
  if (requestIdentity !== getPlatformAvailabilityIdentity() || config.signal?.aborted) {
    throw new PlatformAvailabilityError('platform_unavailable', false);
  }
  config._platformRetry = (config._platformRetry || 0) + 1;
  return { handled: true, response: await api(config as any) };
}

const crmListInFlightRequests = new Map<string, Promise<unknown>>();

async function getCrmList<T>(requestUrl: string): Promise<T> {
  const requestKey = requestUrl;
  const inFlightRequest = crmListInFlightRequests.get(requestKey) as Promise<T> | undefined;

  if (inFlightRequest) {
    return inFlightRequest;
  }

  const requestPromise = api.get<T>(requestUrl).then((response) => response.data);

  crmListInFlightRequests.set(requestKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    crmListInFlightRequests.delete(requestKey);
  }
}

interface RegisterResponse {
  message: string;
}

export interface AgendamentoCreate {
  lead_id: number;
  nome?: string;
  phone?: string;
  consulta_data?: string;  // se o backend espera string "DD/MM/YYYY HH:mm"
  midia?: string;
  interesse?: string;
  status?: string;         // se desejar permitir já definir status
  // Campos de auditoria serão preenchidos automaticamente pelo backend
}

// Interface para mensagens recebidas e enviadas
export type OptimizedMessageContent =
  | string
  | {
      url?: string;
      mediaPath?: string;
      needsLoading?: boolean;
      fallbackText?: string;
      error?: string;
      expired?: boolean;
      marker?: string;
      retentionPolicy?: string;
      expiredMediaType?: string;
      expiredAt?: string;
      duration?: number;
      caption?: string;
      mimeType?: string;
      nps_data?: NPSMessageData;
    };

export interface OptimizedMessageReply {
  providerMessageId?: string | null;
  messageId?: string | number | null;
  content?: string | null;
  type?: string | null;
  senderName?: string | null;
}

export interface OptimizedMessageReaction {
  actorId: string;
  actorName?: string | null;
  emoji: string;
  fromMe?: boolean;
  timestamp?: string | null;
}

export interface OptimizedMessage {
  id: string;
  type: 'text' | 'audio' | 'video' | 'image' | 'file' | 'document' | 'nps' | 'template' | 'location' | 'comment';
  content: OptimizedMessageContent;
  sender: {
    phone: string;
    name: string;
    photo: string;
  };
  timestamp: string;
  timestampNumber: number;
  fromMe: boolean;
  sequenceNumber?: number;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  ack?: number | null;
  templateSnapshot?: TemplateMessageSnapshot | null;
  deliveryError?: MessageDeliveryError | null;
  deliveryStatusAt?: string | null;
  source_channel?: 'waha' | 'official_whatsapp' | string;
  providerMessageId?: string | null;
  replyTo?: OptimizedMessageReply | null;
  reactions?: OptimizedMessageReaction[];
  author_type?: 'patient' | 'platform_human' | 'connected_whatsapp_human' | 'agent' | 'automation' | null;
  author_id?: number | string | null;
  author_name?: string | null;
  author_evidence?: string | null;
  agent_run_id?: string | null;
  /** Permissão calculada pelo backend; null explícito impede o aprendizado. */
  learning_action?: 'correction' | 'instruction' | null;
  /** ID numérico persistido, distinto do identificador do provedor. */
  databaseMessageId?: number | null;
}

export interface NPSMessageData {
  question: string;
  nps_id?: number;
  message_id?: string;
  score?: number;
  status: 'sent' | 'answered';
  answered_at?: string;
  campaign_name?: string;
}

// Interfaces para NPS Dashboard
export interface NPSDashboardMetrics {
  period: {
    start_date: string;
    end_date: string;
  };
  metrics: {
    total_sent: number;
    total_answered: number;
    response_rate: number;
    average_score: number;
    nps_score: number;
    distribution: {
      promoters: number;
      passives: number;
      detractors: number;
    };
  };
  score_distribution: {
    "1": number;
    "2": number;
    "3": number;
    "4": number;
    "5": number;
  };
  daily_evolution: Array<{
    date: string;
    sent: number;
    answered: number;
    avg_score: number;
    response_rate: number;
  }>;
  campaigns: Array<{
    name: string;
    total: number;
    answered: number;
    response_rate: number;
    avg_score: number;
  }>;
}

// Interface para paginação de mensagens
export interface MessagePagination {
  totalCount: number;
  hasMore: boolean;
  nextId?: string;
  nextTimestamp?: number;
}

// Resposta da API de mensagens paginadas
export interface PagedMessagesResponse {
  messages: OptimizedMessage[];
  pagination: MessagePagination;
}

// interfaces para UPDATE (com 'id?' opcional)
export interface FollowUpMessageUpdate {
  id?: number;        // <-- opcional
  type: string;
  content: string;
}

export interface FollowUpStepUpdate {
  id?: number;        // <-- opcional
  step_number: number;
  send_after: number;
  send_after_unit: string;
  messages: FollowUpMessageUpdate[];
}

export interface FollowUpSequenceUpdate {
  client_id: string;  // ou number, se for assim no seu backend
  clinic_id: number;
  name: string;
  description: string;
  steps: FollowUpStepUpdate[];
}

// PeriodTimeConfig: define se está habilitado, e qual o intervalo de tempo
export interface PeriodTimeConfig {
  enabled: boolean;
  start: string;  // ex.: "08:00"
  end: string;    // ex.: "12:00"
}

// Para cada dia, temos 4 períodos (morning, afternoon, night, dawn)
export interface DayTimeConfig {
  morning: {
    enabled: boolean;
    start: string;
    end: string;
  };
  afternoon: {
    enabled: boolean;
    start: string;
    end: string;
  };
  night: {
    enabled: boolean;
    start: string;
    end: string;
  };
  dawn: {
    enabled: boolean;
    start: string;
    end: string;
  };
}

export interface AIResponseWindowsCreate {
  clinic_id: number;
  timezone: string;
  time_windows: Record<string, DayTimeConfig>;
}

export interface AIResponseWindowsUpdate {
  timezone?: string;
  time_windows?: Record<string, DayTimeConfig>;
}

export interface AIResponseWindowsData {
  id: number;
  clinic_id: number;
  timezone: string;
  time_windows: {
    [day: string]: {
      morning: { enabled: boolean; start: string; end: string };
      afternoon: { enabled: boolean; start: string; end: string };
      night: { enabled: boolean; start: string; end: string };
      dawn: { enabled: boolean; start: string; end: string };
    }
  }
}

/**
 * Interface que define o payload enviado ao marcar um no-show.
 * Aqui, apenas 'observacao', pois 'nome', 'phone', 'data_agendada'
 * serão preenchidos no backend a partir do Agendamento.
 */
export interface NoShowCreatePayload {
  observacao?: string;
}

/**
 * Interface representando um registro de NoShowEvent retornado pelo backend.
 * Se no backend temos (nome, phone, data_agendada, observacao, etc.),
 * listamos todos aqui:
 */
export interface NoShowEvent {
  id: number;
  client_id: number;
  clinic_id: number;
  lead_id: number;
  agendamento_id: number;
  nome?: string;                // snapshot do agendamento
  phone?: string;               // snapshot do agendamento
  photo?: string;
  is_patient?: boolean;
  midia?: string;
  source_id?: string;
  thumbnail_url?: string;
  media_editable?: boolean;
  data_agendada?: string;       // ex.: "2025-02-18T10:00:00Z"
  marcado_em: string;           // data/hora em que foi marcado no-show
  observacao?: string;          // campo texto
  created_by_type?: string;
  created_by_user_id?: number;
  created_by_client_id?: number;
  created_via?: string;
  created_by_agent?: boolean;
  updated_by_type?: string;
  updated_by_user_id?: number;
  updated_by_client_id?: number;
  updated_via?: string;
  updated_at?: string;
  creator_name?: string;
}

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  clinic_id: number;
  client_id: number;
  team_id?: number;
  team?: {
    id: number;
    name: string;
    code: string;
  };
  is_active: boolean;
  created_at: string;
}

export interface UserCreate {
  email: string;
  password: string;
  confirm_password: string; // Agora existe
  name: string;
  role: string;
  clinic_id: number;
  team_id?: number;
}

export interface UserUpdate {
  email?: string;
  name?: string;
  role?: string;
  clinic_id?: number;
  team_id?: number;
  is_active?: boolean;
}

// Interfaces para equipes
export type ContactOwnershipVisibility = 'own' | 'unassigned' | 'own_and_unassigned' | 'all';
export interface Team {
  contact_ownership_visibility?: ContactOwnershipVisibility;
  mcp_enabled?: boolean;
  id: number;
  clinic_id: number;
  name: string;
  code: string;
  description?: string;
  created_at: string;
  updated_at: string;
  user_count?: number;
  permissions?: TeamPermission[];
  menu_permissions?: string[];
  menu_permissions_mode?: 'legacy' | 'granular';
  contact_funnel_stages?: string[];
}

export interface TeamPermission {
  id: number;
  resource: string;
  permission: string;
  filter_criteria?: any;
}

export interface TeamCreateRequest {
  contact_ownership_visibility?: ContactOwnershipVisibility;
  mcp_enabled?: boolean;
  name: string;
  description?: string;
  menu_permissions?: string[];
  contact_funnel_stages?: string[];
}

export interface TeamUpdateRequest {
  contact_ownership_visibility?: ContactOwnershipVisibility;
  mcp_enabled?: boolean;
  name?: string;
  description?: string;
  menu_permissions?: string[];
  contact_funnel_stages?: string[];
}

export interface TeamPermissionsRequest {
  contact_ownership_visibility?: ContactOwnershipVisibility;
  menu_permissions: string[];
  contact_funnel_stages: string[];
}

export interface MyTeamPermissionsResponse {
  contact_ownership_visibility?: ContactOwnershipVisibility;
  is_master: boolean;
  team?: Team | null;
  menu_permissions: string[];
  menu_permissions_mode?: 'legacy' | 'granular';
  contact_funnel_stages: string[];
}

interface FlowProgress {
  current_step: number;
  total_steps: number;
  status: 'SCHEDULED' | 'PROCESSING' | 'SUCCESS' | 'FAILED' | 'CANCELED';
  next_scheduled?: string;
}

interface Contact {
  id: number;
  phone: string;
  name: string;
  photo: string;
  lastMessage?: string;
  lastMessageType?: string | null;
  lastMessageStatus?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  lastMessageFromMe?: boolean;
  lastMessageProviderId?: string | null;
  lastMessageSourceChannel?: 'waha' | 'official_whatsapp' | string | null;
  lastMessageDeliveryError?: MessageDeliveryError | null;
  lastMessageDeliveryStatusAt?: string | null;
  timestamp?: string;
  timestampNumber: number;
  unreadCount: number;
  human_mode?: boolean;
  last_message_at?: Date | string;
  user_session_id?: number | null;
  active_channel?: 'waha' | 'official_whatsapp' | 'instagram' | null;
  instagram_identity_id?: number | null;
  instagram_username?: string | null;
  instagram_last_inbound_at?: string | null;
  source_id?: string;
  thumbnail_url?: string;
  sender_lid?: string;
  lead_id?: number | null;
  possible_duplicate?: boolean;
  duplicate_contact_id?: number | null;
  archived?: boolean;
  // Tags do contato
  tags?: TagResponse[];
  // Novos campos do funil
  funnel_stage?: 'contato' | 'lead' | 'agendado' | 'compareceu' | 'faltou' | 'venda' | 'paciente' | 'perdido';
  funnel_status?: {
    agendamento_id?: number;
    /** Data/hora local da clínica, serializada para o modal de reagendamento. */
    agendamento_consulta_data?: string;
    comparecimento_id?: number;
    venda_id?: number;
    no_show_id?: number;
  };
  // Progresso dos fluxos
  flow_progress?: {
    follow_up?: FlowProgress;
    confirmation?: FlowProgress;
    noshow?: FlowProgress;
    pos_consulta?: FlowProgress;
    pos_venda?: FlowProgress;
  };
}

export interface LeadCreate {
  client_id?: string;
  name?: string;
  phone?: string;
  created_at?: string;
  data_entrada?: string; // "2025-01-18 10:00:00", por exemplo
  source_id?: string;
  thumbnail_url?: string;
  sender_lid?: string;
  follow_up_sequence_id?: number;
}

export interface LeadUpdate {
  name?: string;
  phone?: string;
  expected_phone?: string | null;
  created_at?: string;
  data_entrada?: string;
  source_id?: string;
  thumbnail_url?: string;
  sender_lid?: string;
  follow_up_sequence_id?: number;
}

export interface CrmPhoneVerification {
  status: 'verified' | 'not_connected';
  provider: 'waha' | null;
}

// Interface para o retorno de Leads (já existe em seu arquivo, mas fica aqui p/ referência):
export interface Lead {
  id: number;
  client_id?: string;
  name?: string;
  phone?: string;
  phone_verification?: CrmPhoneVerification | null;
  photo?: string;
  is_patient?: boolean;
  created_at?: string;
  clinic_id?: number;
  data_entrada?: string;
  source_id?: string;
  thumbnail_url?: string;
  media_editable?: boolean;
  sender_lid?: string;
  follow_up_sequence_id?: number;
}

// 1) Defina as interfaces de retorno:

/**
 * Resposta de /metrics/funnel_by_source
 * Exemplo de item retornado:
 * {
 *   fonte: "Meta Ads",
 *   totalLeads: 42
 * }
 */
export interface FunnelBySourceItem {
  fonte: string;       // "Meta Ads", "Orgânico", ou o valor de source_id
  totalLeads: number;
}

/**
 * Resposta de /metrics/timeline
 * Exemplo de item:
 * {
 *   entity_id: 123,
 *   event_date: "2025-03-01T10:00:00Z",
 *   event_type: "novo_lead",
 *   descricao: "João da Silva",
 *   photo: "https://..."
 * }
 */
export interface TimelineEvent {
  entity_id: number;
  event_date: string;
  event_type: string;   // "novo_lead", "agendamento", "comparecimento", etc.
  descricao: string;    // alguma descrição, ex.: nome do lead/paciente
  photo?: string | null;
}

/**
 * Resposta de /metrics/projections
 * Exemplo:
 * {
 *   mes: "2025-03",
 *   diasNoMes: 31,
 *   diaAtual: 2,
 *   leadsSoFar: 20,
 *   leadsProjection: 310,
 *   agendamentosSoFar: 10,
 *   agendamentosProjection: 155,
 *   comparecimentosSoFar: 5,
 *   comparecimentosProjection: 77,
 *   vendasSoFar: 2,
 *   vendasProjection: 31,
 *   faturadoSoFar: 1500,
 *   faturadoProjection: 23250.5
 * }
 */
export interface ProjectionsResponse {
  mes: string;
  diasNoMes: number;
  diaAtual: number;

  leadsSoFar: number;
  leadsProjection: number;

  agendamentosSoFar: number;
  agendamentosProjection: number;

  comparecimentosSoFar: number;
  comparecimentosProjection: number;

  vendasSoFar: number;
  vendasProjection: number;

  faturadoSoFar: number;
  faturadoProjection: number;
}

/**
 * Interface que o backend retorna em /api/metrics/time_between_stages
 */
export interface TimeBetweenStagesResponse {
  leadToAgendamento: number;
  leadToComparecimento: number;
  leadToVenda: number;
}

// Representa o que o endpoint /api/metrics/funnels retorna
export interface FunnelMetricsResponse {
  totalLeads: number;
  totalAgendamentos: number;
  percentAgendamentos: number;
  totalComparecimentos: number;
  percentComparecimentos: number;
  totalVendas: number;
  percentVendas: number;
  valorFaturado: number;
  valorPago: number;          // <-- novo campo
  valorOrcado: number;
  ticketMedio: number;
  boasPraticas: string[];     // array de mensagens de "boas práticas"
}

// Parâmetros de filtro para o funil
export interface FunnelMetricsParams {
  clinicId?: number;
  startDate?: string; // 'YYYY-MM-DD'
  endDate?: string;   // 'YYYY-MM-DD'
  fonte?: string;     // Filtro por fonte de mídia
  tagIds?: number[];  // Filtro por tags do lead
  createdByType?: string;
  createdByUserId?: number;
}

function appendCreatorMetricsParams(
  qs: URLSearchParams,
  createdByType?: string,
  createdByUserId?: number
): void {
  if (createdByType) {
    qs.append("created_by_type", createdByType);
  }
  if (createdByUserId !== undefined) {
    qs.append("created_by_user_id", createdByUserId.toString());
  }
}

// Defina a interface para o Agendamento (ajuste conforme a estrutura real)
export interface AgendamentoResponse {
  id: number;
  client_id: number;
  clinic_id?: number;
  lead_id: number;
  agendamento_realizado_em: string; // ou Date, dependendo de como você manipula datas
  nome?: string;
  phone?: string;
  photo?: string;
  is_patient?: boolean;
  consulta_data?: string; // ou Date
  midia?: string;
  source_id?: string;
  thumbnail_url?: string;
  media_editable?: boolean;
  interesse?: string;
  patient_id?: string;
  id_agendamento?: string;
  event_id?: string;
  status?: string;

  // Novos campos para os status de sincronização
  google_sync_status?: string; // SYNCED, FAILED, CANCELLED, CANCEL_FAILED, NOT_APPLICABLE
  clinicorp_sync_status?: string; // PENDING, SYNCED, SYNC_UNKNOWN, FAILED, CANCELLED, CANCEL_FAILED, NOT_APPLICABLE

  // Campos de auditoria
  created_by?: number;
  updated_by?: number;
  original_created_by?: number;
  had_original_user?: boolean;
  updated_at?: string; // ISO date string
  created_by_type?: string;
  created_by_user_id?: number;
  created_by_client_id?: number;
  created_via?: string;
  created_by_agent?: boolean;
  updated_by_type?: string;
  updated_by_user_id?: number;
  updated_by_client_id?: number;
  updated_via?: string;

  // Nomes dos usuários (opcional, para facilitar frontend)
  creator_name?: string;
  updater_name?: string;
  original_creator_name?: string;
}

// Constantes para os estados de sincronização do Google Calendar
export const GOOGLE_SYNC_STATUS = {
  SYNCED: "SYNCED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
  CANCEL_FAILED: "CANCEL_FAILED",
  NOT_APPLICABLE: "NOT_APPLICABLE"
};

// Constantes para os estados de sincronização do Clinicorp
export const CLINICORP_SYNC_STATUS = {
  SYNCED: "SYNCED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
  CANCEL_FAILED: "CANCEL_FAILED",
  NOT_APPLICABLE: "NOT_APPLICABLE"
};

// Se precisar de um tipo para o payload de update:
export interface AgendamentoUpdate {
  nome?: string;
  phone?: string;
  consulta_data?: string; // se o backend espera string "DD/MM/YYYY HH:mm"
  midia?: string;
  interesse?: string;
  status?: string;
  // Campos de auditoria serão preenchidos automaticamente pelo backend
}

export interface Lead {
  id: number;
  client_id?: string;
  name?: string;
  phone?: string;
  phone_verification?: CrmPhoneVerification | null;
  photo?: string;
  is_patient?: boolean;
  created_at?: string;
  clinic_id?: number;
  data_entrada?: string;       // ou Date, se preferir converter
  source_id?: string;
  thumbnail_url?: string;
  media_editable?: boolean;
  sender_lid?: string;
  follow_up_sequence_id?: number;
  created_by_type?: string;
  created_by_user_id?: number;
  created_by_client_id?: number;
  created_via?: string;
  created_by_agent?: boolean;
  updated_by_type?: string;
  updated_by_user_id?: number;
  updated_by_client_id?: number;
  updated_via?: string;
  updated_at?: string;
  creator_name?: string;
}

// Interface completa para leitura (retorno da API)
export interface Comparecimento {
  id: number;
  // Estes campos vêm do banco, mas não podem ser alterados via formulário
  client_id: number;
  clinic_id?: number;
  lead_id: number;

  agendamento_id: number;
  compareceu_em: string; // ou Date
  nome?: string;
  phone?: string;
  photo?: string;
  is_patient?: boolean;
  midia?: string;
  source_id?: string;
  thumbnail_url?: string;
  media_editable?: boolean;
  interesse?: string;
  tratamento_orcado?: string;
  valor_orcamento?: number;
  created_by_type?: string;
  created_by_user_id?: number;
  created_by_client_id?: number;
  created_via?: string;
  created_by_agent?: boolean;
  updated_by_type?: string;
  updated_by_user_id?: number;
  updated_by_client_id?: number;
  updated_via?: string;
  updated_at?: string;
  creator_name?: string;
}

// Interface de criação
// Note que NÃO incluímos client_id, clinic_id e NÃO *fazemos update* de lead_id
// pois em geral eles são determinados pela URL ou outro fluxo de negócio.
export interface ComparecimentoCreate {
  // se lead_id for realmente necessário durante a criação,
  // mantenha-o — caso ele também não seja editável pelo usuário
  // mas sim determinável pela rota/URL, então retire daqui
  lead_id: number;
  compareceu_em: string; // ou Date
  agendamento_id: number;
  nome?: string;
  phone?: string;
  midia?: string;
  interesse?: string;
  tratamento_orcado?: string;
  valor_orcamento?: number;
}

// Interface de atualização
// Note que aqui NÃO incluímos client_id, clinic_id e lead_id 
// pois não queremos permitir que sejam alterados
export interface ComparecimentoUpdate {
  agendamento_id?: number;
  nome?: string;
  phone?: string;
  midia?: string;
  interesse?: string;
  tratamento_orcado?: string;
  valor_orcamento?: number;
}

/**
 * Interface de Venda, de acordo com seu backend (vendas_routes.py)
 */
export interface Venda {
  id: number;
  client_id: number;
  clinic_id?: number;
  lead_id: number;
  comparecimento_id: number;
  venda_data: string; // ou Date, se preferir converter no front
  nome?: string;
  phone?: string;
  photo?: string;
  is_patient?: boolean;
  midia?: string;
  source_id?: string;
  thumbnail_url?: string;
  media_editable?: boolean;
  tratamento_fechado?: string;
  valor_faturado?: number;
  valor_pago?: number;
  created_by_type?: string;
  created_by_user_id?: number;
  created_by_client_id?: number;
  created_via?: string;
  created_by_agent?: boolean;
  updated_by_type?: string;
  updated_by_user_id?: number;
  updated_by_client_id?: number;
  updated_via?: string;
  updated_at?: string;
  creator_name?: string;
}

/**
 * Interface para criar uma nova venda (POST)
 */
export interface VendaCreate {
  lead_id: number;
  comparecimento_id: number;
  nome?: string;
  phone?: string;
  tratamento_fechado?: string;
  valor_faturado?: number;
  valor_pago?: number;
  venda_data?: string;  // <== agora podemos enviar a data/hora, caso o backend aceite
}

/**
 * Interface para atualizar uma venda (PUT)
 */
export interface VendaUpdate {
  lead_id?: number;
  comparecimento_id?: number;
  nome?: string;
  phone?: string;
  midia?: string;
  tratamento_fechado?: string;
  valor_faturado?: number;
  valor_pago?: number;
  venda_data?: string;  // <== idem, se a rota de atualização permitir alterar a data
}

export type CrmLossStage = 'leads' | 'appointments' | 'noShows' | 'attendances';
export type CrmStage = CrmLossStage | 'sales';
export const CRM_LOSS_REASONS = ['Sem interesse', 'Sem retorno', 'Preço', 'Escolheu outra clínica', 'Outro'] as const;
export interface CrmLoss {
  id: number;
  lead_id: number;
  name?: string;
  phone?: string;
  source_id?: string;
  thumbnail_url?: string;
  lost_at: string;
  from_stage: CrmLossStage;
  source_entity_id?: number | null;
  actor_type: string;
  actor_user_id?: number | null;
  actor_client_id?: number | null;
  actor_name: string;
  reason: string;
  observation?: string | null;
  reopened_at?: string | null;
  reopened_by_name?: string | null;
  reopen_justification?: string | null;
  reopened_stage?: CrmStage | null;
}
export interface CrmLossHistory { items: CrmLoss[]; total: number; reopen_stage: CrmStage | null; reopen_error?: string | null; }
export interface CrmLossMutation { loss: CrmLoss; stage: CrmStage | 'lost'; }
export type CrmEntityKey = CrmStage | 'lost';

export interface CrmTotals {
  lost?: number;
  leads: number;
  appointments: number;
  noShows: number;
  attendances: number;
  sales: number;
}

export interface CrmInitialResponse {
  losses_enabled?: boolean;
  lost?: CrmLoss[];
  clinicTimezone: string | null;
  leads: Lead[];
  appointments: AgendamentoResponse[];
  noShows: NoShowEvent[];
  attendances: Comparecimento[];
  sales: Venda[];
  totals: CrmTotals;
}

export interface CrmEntityItemMap {
  lost: CrmLoss;
  leads: Lead;
  appointments: AgendamentoResponse;
  noShows: NoShowEvent;
  attendances: Comparecimento;
  sales: Venda;
}

export interface CrmPageResponse<TItem> {
  items: TItem[];
  total: number;
}

export interface CrmListParams {
  includeReopened?: boolean;
  lossStage?: string;
  lossActor?: string;
  lossReason?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  source?: string;
  tagIds?: number[];
  isPatient?: boolean;
  currentStageOnly?: boolean;
}

export function buildCrmQueryParams(
  limit: number,
  params: CrmListParams = {},
  offset?: number
): string {
  const queryParams = new URLSearchParams({ limit: String(limit) });

  if (typeof offset === 'number') {
    queryParams.set('offset', String(offset));
  }

  const search = params.search?.trim();
  if (search) {
    queryParams.set('search', search);
  }

  if (params.startDate) {
    queryParams.set('start_date', params.startDate);
  }

  if (params.endDate) {
    queryParams.set('end_date', params.endDate);
  }

  if (params.source) {
    queryParams.set('source', params.source);
  }

  if (params.tagIds && params.tagIds.length > 0) {
    queryParams.set('tag_ids', params.tagIds.join(','));
  }

  if (typeof params.isPatient === 'boolean') {
    queryParams.set('is_patient', String(params.isPatient));
  }

  if (params.currentStageOnly) {
    queryParams.set('current_stage_only', 'true');
  }

  if (params.includeReopened) queryParams.set('include_reopened', 'true');
  if (params.lossStage) queryParams.set('loss_stage', params.lossStage);
  if (params.lossActor) queryParams.set('loss_actor', params.lossActor);
  if (params.lossReason) queryParams.set('loss_reason', params.lossReason);
  return queryParams.toString();
}

// [NO-SHOW FOLLOW-UP] Interfaces

export interface NoShowMessageUpdate {
  id?: number;        // ID opcional
  type: string;
  content: string;
}

export interface NoShowStepUpdate {
  id?: number;        // ID opcional
  step_number: number;
  send_after: number;
  send_after_unit: string; // "minutes", "hours", "days"
  messages: NoShowMessageUpdate[];
}

// Mensagem de No-Show
export interface NoShowMessageCreate {
  type: string;    // "text" | "image" | "audio" | "video", etc.
  content: string; // texto ou caminho/URL do arquivo
}

// Passo (Step) do No-Show
export interface NoShowStepCreate {
  step_number: number;
  send_after: number;        // 1, 2, 3 ...
  send_after_unit: string;   // "minutes" | "hours" | "days"
  messages: NoShowMessageCreate[];
}

// Criação de uma sequência de No-Show
export interface NoShowFollowUpSequenceCreate {
  clinic_id: number;
  name: string;
  description: string;
  steps: NoShowStepCreate[];
}

export interface NoShowFollowUpSequenceUpdate {
  clinic_id: number;
  name: string;
  description: string;
  steps: NoShowStepUpdate[];
}

// Resposta do backend no PUT
export interface NoShowFollowUpSequenceResponse {
  message: string;
  sequence_id: number;
}

// Detalhes de uma sequência de No-Show (retorno do GET)
export interface NoShowFollowUpSequenceDetail {
  id: number;
  clinic_id: number;
  name: string;
  description: string;
  steps: Array<{
    id: number;
    step_number: number;
    send_after: number;
    send_after_unit: string;
    messages: Array<{
      id: number;
      type: string;
      content: string;
    }>;
  }>;
}

// =========== SCHEDULE CONFIG ===========

export interface DailyRangeNoShow {
  enabled: boolean;
  start: string;
  end: string;
}

export interface NoShowScheduleData {
  [key: string]: DailyRangeNoShow;
}

// Criação de config
export interface NoShowScheduleCreate {
  schedule_data: NoShowScheduleData;
}

// Atualização de config
export interface NoShowScheduleUpdate {
  schedule_data: NoShowScheduleData;
}

// Retorno do GET
export interface NoShowScheduleConfig {
  id: number;
  clinic_id: number;
  noshow_follow_up_sequence_id: number;
  schedule_data: NoShowScheduleData;
}

interface ApiErrorDetail {
  message?: string;
  code?: string;
  reason?: string;
  requested_slot?: string;
  alternatives?: unknown;
  existing_contact_id?: number;
  existing_lead_id?: number;
  existing_patient_id?: number;
  existing_phone?: string;
  contact_id?: number;
  lead_id?: number;
  patient_id?: number;
  duplicate_contact_id?: number;
  phone?: string;
  entities?: Array<{ type?: string; id?: number }>;
  [key: string]: unknown;
}

interface ApiError {
  detail: string | ApiErrorDetail;
}

interface MirrorWebhookResponse {
  mirror_webhook_url: string | null;
}

// Models para o TS (exemplo)
// Ajuste conforme o que seu backend espera/retorna

export interface ConfirmationMessageCreate {
  type: string;
  content: string;
  position?: number;
}

export interface ConfirmationStepCreate {
  step_number: number;
  send_after: number;
  send_after_unit: string; // "minutes", "hours", "days"
  send_at_time?: string | null;
  messages: ConfirmationMessageCreate[];
}

export interface ConfirmationSequenceCreate {
  client_id: string; // ou number, se for o caso
  clinic_id: number;
  name: string;
  description: string;
  steps: ConfirmationStepCreate[];
}

export interface ConfirmationSequenceResponse {
  message: string;
  sequence_id: number;
  version?: number;
}

export interface ConfirmationSequenceDetail {
  id: number;
  client_id: string; // ou number
  clinic_id: number;
  name: string;
  description: string;
  version: number;
  is_active: boolean;
  steps: Array<{
    id: number;
    step_number: number;
    send_after: number;
    send_after_unit: string;
    send_at_time?: string | null;
    messages: Array<{
      id: number;
      type: string;
      content: string;
      position: number;
    }>;
  }>;
}

// Para UPDATE (PUT):
export interface ConfirmationMessageUpdate {
  id?: number;        // <--- agora é opcional
  type: string;
  content: string;
  position?: number;
}

export interface ConfirmationStepUpdate {
  id?: number;        // <--- opcional
  step_number: number;
  send_after: number;
  send_after_unit: string; // "minutes", "hours", "days"
  send_at_time?: string | null;
  messages: ConfirmationMessageUpdate[];
}

export interface ConfirmationSequenceUpdate {
  client_id: string | number;  // depende do backend
  clinic_id: number;
  name: string;
  description: string;
  steps: ConfirmationStepUpdate[];
}

export interface DailyRange {
  start: string; // ex: "08:00"
  end: string;   // ex: "18:00"
}

export interface ConfirmationScheduleData {
  [key: string]: {
    enabled: boolean;
    start: string;
    end: string;
  };
}

export interface ConfirmationScheduleCreate {
  schedule_data: ConfirmationScheduleData;
}

export interface ConfirmationScheduleUpdate {
  schedule_data: ConfirmationScheduleData;
}

export interface ConfirmationScheduleConfig {
  id: number;
  clinic_id: number;
  confirmation_sequence_id: number;
  schedule_data: ConfirmationScheduleData;
}

export interface ScheduleDayItem {
  enabled: boolean;
  start: string;
  end: string;
}

export interface ScheduleData {
  [day: string]: ScheduleDayItem;
  // "monday" | "tuesday" | ... se quiser algo mais restrito
}

// Payload para criar uma nova configuração
export interface FollowUpScheduleCreate {
  //follow_up_sequence_id: number; // se necessário
  schedule_data: ScheduleData;
}

// Payload para atualizar configuração existente
export interface FollowUpScheduleUpdate {
  schedule_data: ScheduleData;
}

// Resposta ao obter a configuração
export interface FollowUpScheduleConfig {
  id: number;
  clinic_id: number;
  follow_up_sequence_id: number;
  schedule_data: ScheduleData;
}

// Tipos base
export interface FollowUpMessageCreate {
  type: string;      // "text" | "image" | "audio" | "video" | "template"
  content: string;   // texto ou path do arquivo
}

export interface FollowUpStepCreate {
  step_number: number;
  send_after: number;
  send_after_unit: string; // "days" | "hours" | "minutes"
  messages: FollowUpMessageCreate[];
}

export interface FollowUpSequenceCreate {
  client_id: string;  // ou number, dependendo do backend
  clinic_id: number;
  name: string;
  description: string;
  steps: FollowUpStepCreate[];
}

// Resposta do backend (simplificada)
export interface FollowUpSequenceResponse {
  sequence_id: number;
  message: string;
}

export interface MessageCreate {
  type: string;
  content: string;
}

export interface StepCreate {
  step_number: number;
  send_after: number;
  send_after_unit: string;
  messages: MessageCreate[];
}

export interface GetFollowUpSequenceDetail {
  id: number;
  client_id: string;
  clinic_id: number;
  name: string;
  description: string;
  steps: Array<{
    id: number;
    step_number: number;
    send_after: number;
    send_after_unit: string;
    messages: Array<{
      id: number;
      type: string;
      content: string;
    }>;
  }>;
}

export interface Clinic {
  id: number | string;
  name: string;         // "Razão Social" original?
  cnpj?: string;        // se você quiser deixá-lo opcional
  name_clinic?: string; // alias para nome customizável
  logo_url?: string;
  mirror_webhook_url?: string;

  // se quiser, inclua mais propriedades que já existem no seu backend
  created_at?: string;
  updated_at?: string;
}

export interface MediaFile {
  id: string;
  file_name: string;
  file_type: string;
  mime_type: string;
  file_size: number;
  relative_path: string;
  created_at: string;
  metadata?: Record<string, any>; // se tiver metadados adicionais
}

export interface SendTextParams {
  phone: string;
  message: string;
  localMessageId?: string;
  replyToMessageId?: string;
  contact_id?: number;
}

export interface SendImageParams {
  phone: string;
  image: string;        // URL ou base64
  caption?: string;
  localMessageId?: string;
  viewOnce?: boolean;
  messageId?: string;
  delayMessage?: number;
  contact_id?: number;
}

export interface SendAudioParams {
  phone: string;
  audio: string;        // URL ou base64
  viewOnce?: boolean;
  localMessageId?: string;
  delayMessage?: number;
  delayTyping?: number;
  contact_id?: number;
}

export interface SendVideoParams {
  phone: string;
  video: string;        // URL ou base64
  caption?: string;
  localMessageId?: string;
  viewOnce?: boolean;
  messageId?: string;
  delayMessage?: number;
  delayTyping?: number;
  asyncUpload?: boolean;
  contact_id?: number;
}

// Tipos de Provider WhatsApp
export type WhatsAppProvider = 'zapi' | 'waha' | 'evolution' | 'wppconnect';

export interface WhatsAppProviderInfo {
  provider: WhatsAppProvider;
  config: any;
}

export interface MessageConfig {
  type: 'text' | 'image' | 'audio' | 'video';
  content: string;
}

export interface FollowUpConfig {
  sendAfter: number;        // em dias, horas ou minutos
  sendAfterUnit: 'days' | 'hours' | 'minutes';
  waitBetween: number;
  waitBetweenUnit: 'days' | 'hours' | 'minutes';
  messages: MessageConfig[]; // limita a 3 msgs/dia, 5 dias total
}

export interface BrowserSession {
  name?: string;
  email?: string;
  photoUrl?: string | null;
  mustChangePassword?: boolean;
  temporaryPasswordExpiresAt?: string | null;
  clinicId?: number;
  clientId?: number;
  userType?: string;
  isPlatformAdmin?: boolean;
  userId?: number;
  userTeam?: string;
  businessType?: string;
  settings?: Record<string, unknown>;
}

interface BrowserSessionResponse {
  user?: {
    name?: string;
    email?: string;
    photo_url?: string | null;
    must_change_password?: boolean;
    temporary_password_expires_at?: string | null;
    clinic_id?: number;
    client_id?: number;
    user_type?: string;
    is_platform_admin?: boolean;
    user_id?: number;
    user_team?: string;
    business_type?: string;
    settings?: Record<string, unknown>;
  };
  csrf_token?: string;
}

const SESSION_METADATA_STORAGE_KEYS = [
  'user_type',
  'user_team',
  'user_id',
  'client_id',
  'master_client_id',
  'clinic_id',
  'business_type',
  'settings',
  'menu_permissions',
  'menu_permissions_mode',
  'contact_funnel_stages',
  'clinic_plan',
] as const;

const LEGACY_BROWSER_AUTH_STORAGE_KEYS = [
  'token',
  'refresh_token',
  'admin_token',
  'api_key',
  // Versoes antigas podiam persistir tokens dos providers neste objeto.
  'clinic_whatsapp_config',
] as const;

export const BROWSER_SESSION_EXPIRED_EVENT = 'sminy:browser-session-expired';
export const BROWSER_SESSION_ROTATED_EVENT = 'sminy:browser-session-rotated';

export type BrowserSessionSignalType =
  | 'logout'
  | 'session-expired'
  | 'session-rotated';

interface BrowserSessionSignal {
  version: 1;
  type: BrowserSessionSignalType;
  source: string;
  timestamp: number;
}

const BROWSER_SESSION_CHANNEL_NAME = 'sminy-browser-session';
const BROWSER_SESSION_SIGNAL_STORAGE_KEY = 'sminy:browser-session-signal';

let browserCsrfToken: string | null = null;
let browserBootstrapPromise: Promise<BrowserSession | null> | null = null;
let browserRefreshPromise: Promise<BrowserSession> | null = null;
let browserCsrfRecoveryPromise: Promise<BrowserSession> | null = null;
let browserSessionChannel: BroadcastChannel | null = null;
let browserSessionStorageListenerAttached = false;
let browserSessionCoordinationSubscribers = 0;

const browserSessionSignalSource = (() => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
})();

function getStoredBrowserIdentity(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const values = [
    localStorage.getItem('user_type'),
    localStorage.getItem('user_id'),
    localStorage.getItem('client_id'),
    localStorage.getItem('clinic_id'),
  ];

  return values.some(Boolean) ? values.map((value) => value || '').join(':') : null;
}

function getBrowserIdentity(session: BrowserSession): string {
  return [
    session.userType || '',
    session.userId || '',
    session.clientId || '',
    session.clinicId || '',
  ].join(':');
}

function normalizeBrowserSession(data: BrowserSessionResponse): BrowserSession {
  const user = data?.user;
  if (!user || typeof user !== 'object') {
    throw new Error('Resposta de sessão inválida.');
  }

  return {
    name: user.name,
    email: user.email,
    photoUrl: user.photo_url,
    mustChangePassword: user.must_change_password,
    temporaryPasswordExpiresAt: user.temporary_password_expires_at,
    clinicId: user.clinic_id,
    clientId: user.client_id,
    userType: user.user_type,
    isPlatformAdmin: user.is_platform_admin === true,
    userId: user.user_id,
    userTeam: user.user_team,
    businessType: user.business_type,
    settings: user.settings,
  };
}

function updateBrowserCsrfToken(data?: BrowserSessionResponse): void {
  if (typeof data?.csrf_token === 'string' && data.csrf_token.trim()) {
    browserCsrfToken = data.csrf_token;
  }
}

function setStoredValue(key: string, value: unknown): void {
  if (value === undefined || value === null || value === '') {
    localStorage.removeItem(key);
    return;
  }

  localStorage.setItem(
    key,
    typeof value === 'object' ? JSON.stringify(value) : String(value),
  );
}

export function purgeLegacyMessageCacheStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const keysToRemove = new Set<string>([
    'chat_lru_list',
    'chat_messages_lru_list',
  ]);

  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith('chat_messages_')) {
      keysToRemove.add(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

function clearBrowserSensitiveRuntimeState(): void {
  platformSessionRevision += 1;
  acknowledgePlatformPendingCommands();
  messageCacheManager.clearAllCache();
  unifiedWebSocketManager.disconnect();
  clearCachedClinicWhatsAppConfig();
  purgeLegacyMessageCacheStorage();
}

function applyLocalBrowserSessionRotation(): void {
  messageCacheManager.clearAllCache();
  unifiedWebSocketManager.reconnectAfterSessionRotation();
}

export function persistBrowserSession(session: BrowserSession): void {
  if (typeof window === 'undefined') {
    return;
  }

  const previousIdentity = getStoredBrowserIdentity();
  const nextIdentity = getBrowserIdentity(session);
  if (previousIdentity && previousIdentity !== nextIdentity) {
    clearBrowserSensitiveRuntimeState();
  }

  setStoredValue('user_type', session.userType);
  setStoredValue('user_team', session.userTeam);
  setStoredValue('user_id', session.userId);
  setStoredValue('client_id', session.clientId);
  setStoredValue('clinic_id', session.clinicId);
  setStoredValue('business_type', session.businessType);
  // Configuracoes arbitrarias da clinica permanecem apenas em memoria.
  localStorage.removeItem('settings');

  if (session.userType === 'user' && session.clientId) {
    setStoredValue('master_client_id', session.clientId);
  } else {
    localStorage.removeItem('master_client_id');
  }
}

export function clearBrowserSessionMetadata(): void {
  if (typeof window === 'undefined') {
    return;
  }

  SESSION_METADATA_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

export function clearLegacyBrowserAuthStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  LEGACY_BROWSER_AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

function isBrowserSessionSignal(value: unknown): value is BrowserSessionSignal {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const signal = value as Partial<BrowserSessionSignal>;
  return (
    signal.version === 1 &&
    (signal.type === 'logout' ||
      signal.type === 'session-expired' ||
      signal.type === 'session-rotated') &&
    typeof signal.source === 'string' &&
    signal.source.length > 0 &&
    typeof signal.timestamp === 'number' &&
    Number.isFinite(signal.timestamp)
  );
}

function receiveBrowserSessionSignal(value: unknown): void {
  if (!isBrowserSessionSignal(value) || value.source === browserSessionSignalSource) {
    return;
  }

  // Ignore sinais antigos restaurados por extensões ou snapshots do navegador.
  if (Math.abs(Date.now() - value.timestamp) > 60_000) {
    return;
  }

  if (value.type === 'session-rotated') {
    window.dispatchEvent(new CustomEvent(BROWSER_SESSION_ROTATED_EVENT));
    return;
  }

  clearAuthStorage();
  window.dispatchEvent(new CustomEvent(BROWSER_SESSION_EXPIRED_EVENT));
}

const handleBrowserSessionStorageEvent = (event: StorageEvent): void => {
  if (event.key !== BROWSER_SESSION_SIGNAL_STORAGE_KEY || !event.newValue) {
    return;
  }

  try {
    receiveBrowserSessionSignal(JSON.parse(event.newValue));
  } catch {
    // Sinais inválidos são ignorados e nunca afetam a sessão atual.
  }
};

function ensureBrowserSessionCoordinationTransport(): void {
  if (typeof window === 'undefined' || browserSessionChannel || browserSessionStorageListenerAttached) {
    return;
  }

  try {
    if (typeof globalThis.BroadcastChannel === 'function') {
      browserSessionChannel = new BroadcastChannel(BROWSER_SESSION_CHANNEL_NAME);
      browserSessionChannel.onmessage = (event: MessageEvent<unknown>) => {
        receiveBrowserSessionSignal(event.data);
      };
      return;
    }
  } catch {
    browserSessionChannel = null;
  }

  window.addEventListener('storage', handleBrowserSessionStorageEvent);
  browserSessionStorageListenerAttached = true;
}

export function startBrowserSessionCoordination(): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  browserSessionCoordinationSubscribers += 1;
  ensureBrowserSessionCoordinationTransport();
  let stopped = false;

  return () => {
    if (stopped) {
      return;
    }
    stopped = true;
    browserSessionCoordinationSubscribers = Math.max(
      0,
      browserSessionCoordinationSubscribers - 1,
    );

    if (browserSessionCoordinationSubscribers > 0) {
      return;
    }

    if (browserSessionChannel) {
      browserSessionChannel.close();
      browserSessionChannel = null;
    }
    if (browserSessionStorageListenerAttached) {
      window.removeEventListener('storage', handleBrowserSessionStorageEvent);
      browserSessionStorageListenerAttached = false;
    }
  };
}

function publishBrowserSessionSignal(type: BrowserSessionSignalType): void {
  if (typeof window === 'undefined') {
    return;
  }

  const signal: BrowserSessionSignal = {
    version: 1,
    type,
    source: browserSessionSignalSource,
    timestamp: Date.now(),
  };

  if (browserSessionCoordinationSubscribers > 0) {
    ensureBrowserSessionCoordinationTransport();
  }
  if (browserSessionChannel) {
    browserSessionChannel.postMessage(signal);
    return;
  }

  try {
    if (typeof globalThis.BroadcastChannel === 'function') {
      const outboundChannel = new BroadcastChannel(BROWSER_SESSION_CHANNEL_NAME);
      outboundChannel.postMessage(signal);
      setTimeout(() => outboundChannel.close(), 0);
      return;
    }
  } catch {
    // Usa o evento de storage como fallback abaixo.
  }

  try {
    localStorage.setItem(BROWSER_SESSION_SIGNAL_STORAGE_KEY, JSON.stringify(signal));
    localStorage.removeItem(BROWSER_SESSION_SIGNAL_STORAGE_KEY);
  } catch {
    // A aba atual continua segura mesmo quando o storage está indisponível.
  }
}

function getApiDetailMessage(detail: ApiError['detail'] | undefined): string | null {
  if (!detail) {
    return null;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  if (typeof detail.message === 'string' && detail.message.trim()) {
    return detail.message;
  }
  return null;
}

export interface BrazilianPhoneValidation {
  isValid: boolean;
  normalized: string;
  localDigits: string;
  error?: string;
}

const BRAZILIAN_PHONE_ERROR = 'Informe um telefone com DDD e 10 ou 11 dígitos.';

export function validateBrazilianWhatsAppPhone(rawPhone: string): BrazilianPhoneValidation {
  const digits = String(rawPhone || '').replace(/\D/g, '');
  const localDigits =
    digits.startsWith('55') && (digits.length === 12 || digits.length === 13)
      ? digits.slice(2)
      : digits;
  const hasValidLength = localDigits.length === 10 || localDigits.length === 11;
  const hasDdd = /^[1-9][0-9]/.test(localDigits);

  if (!hasValidLength || !hasDdd) {
    return {
      isValid: false,
      normalized: '',
      localDigits,
      error: BRAZILIAN_PHONE_ERROR,
    };
  }

  return {
    isValid: true,
    normalized: `55${localDigits}`,
    localDigits,
  };
}

export function formatBrazilianPhoneInput(rawPhone: string): string {
  const rawValue = String(rawPhone || '');
  const trimmedValue = rawValue.trim();

  if (trimmedValue === '+' || trimmedValue === '+5' || trimmedValue === '+55') {
    return trimmedValue;
  }

  const digits = rawValue.replace(/\D/g, '');
  const hasBrazilCountryCode =
    /^\s*\+55/.test(rawValue) ||
    (digits.startsWith('55') && digits.length > 11);
  const localDigits = (hasBrazilCountryCode ? digits.slice(2) : digits).slice(0, 11);

  if (!localDigits) {
    return '';
  }
  if (localDigits.length <= 2) {
    return `(${localDigits}`;
  }

  const ddd = localDigits.slice(0, 2);
  const subscriberNumber = localDigits.slice(2);
  const isMobile = localDigits.length === 11 || subscriberNumber.startsWith('9');
  const firstGroupLength = isMobile ? 5 : 4;

  if (subscriberNumber.length <= firstGroupLength) {
    return `(${ddd}) ${subscriberNumber}`;
  }

  return `(${ddd}) ${subscriberNumber.slice(0, firstGroupLength)}-${subscriberNumber.slice(firstGroupLength)}`;
}

export function formatBrazilianPhone(rawPhone: string): string {
  const validation = validateBrazilianWhatsAppPhone(rawPhone);
  const digits = validation.localDigits || String(rawPhone || '').replace(/\D/g, '');

  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return rawPhone;
}

export interface ParsedApiError {
  message: string;
  code?: string;
  status?: number;
  requestProcessed?: false;
  reason?: string;
  requestedSlot?: string;
  alternatives?: string[];
  existingContactId?: number;
  existingLeadId?: number;
  existingPatientId?: number;
  existingPhone?: string;
}

interface EnrichedApiError extends Error, ParsedApiError {
  apiDetail?: ApiError['detail'];
}

const readPositiveNumber = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return undefined;
};

const normalizeIdentityErrorCode = (code?: string): string | undefined => {
  const aliases: Record<string, string> = {
    invalid_whatsapp_phone: 'invalid_phone',
    whatsapp_phone_invalid: 'invalid_phone',
    whatsapp_number_invalid: 'invalid_phone',
    whatsapp_identity_conflict: 'whatsapp_identity_conflict',
    duplicate_whatsapp_identity: 'whatsapp_identity_conflict',
    ambiguous_whatsapp_identity: 'whatsapp_identity_ambiguous',
  };

  return code ? aliases[code] || code : undefined;
};

const readStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const strings = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  return strings.length ? strings : undefined;
};

export function parseApiError(error: unknown, defaultMessage = 'Não foi possível concluir a operação.'): ParsedApiError {
  if (isPlatformAvailabilityError(error)) {
    const platformError = error as PlatformAvailabilityError;
    return { message: platformError.message, code: platformError.code, status: platformError.status, requestProcessed: platformError.requestProcessed };
  }
  const axiosError = axios.isAxiosError<ApiError>(error) ? error : null;
  const enrichedError = error instanceof Error ? (error as Partial<EnrichedApiError>) : null;
  const responseDetail = axiosError?.response?.data?.detail;
  const objectError = typeof error === 'object' && error !== null ? error as Record<string, unknown> : null;
  const rawDetail =
    responseDetail ??
    enrichedError?.apiDetail ??
    objectError?.detail ??
    (typeof error === 'string' ? error : undefined);
  const detail = typeof rawDetail === 'object' && rawDetail !== null
    ? rawDetail as ApiErrorDetail
    : undefined;
  const entities = Array.isArray(detail?.entities) ? detail.entities : [];
  const entityId = (type: string) => entities.find((entity) => entity?.type === type)?.id;
  const message =
    (typeof rawDetail === 'string' && rawDetail.trim() ? rawDetail : undefined) ??
    (typeof detail?.message === 'string' && detail.message.trim() ? detail.message : undefined) ??
    (typeof detail?.reason === 'string' && detail.reason.trim() ? detail.reason : undefined) ??
    (!axiosError ? enrichedError?.message : undefined) ??
    defaultMessage;

  return {
    message,
    code: normalizeIdentityErrorCode(
      typeof detail?.code === 'string' ? detail.code : enrichedError?.code
    ),
    status: axiosError?.response?.status ?? enrichedError?.status,
    reason: typeof detail?.reason === 'string' ? detail.reason : undefined,
    requestedSlot: typeof detail?.requested_slot === 'string' ? detail.requested_slot : undefined,
    alternatives: readStringArray(detail?.alternatives),
    existingContactId: readPositiveNumber(
      detail?.existing_contact_id,
      detail?.duplicate_contact_id,
      detail?.contact_id,
      entityId('contact'),
      enrichedError?.existingContactId,
    ),
    existingLeadId: readPositiveNumber(
      detail?.existing_lead_id,
      detail?.lead_id,
      entityId('lead'),
      enrichedError?.existingLeadId,
    ),
    existingPatientId: readPositiveNumber(
      detail?.existing_patient_id,
      detail?.patient_id,
      entityId('patient'),
      enrichedError?.existingPatientId,
    ),
    existingPhone:
      (typeof detail?.existing_phone === 'string' && detail.existing_phone) ||
      (typeof detail?.phone === 'string' && detail.phone) ||
      enrichedError?.existingPhone,
  };
}

export function shouldClearAuthForError(status?: number, _requestUrl?: string): boolean {
  return status === 401;
}

function isBillingSuspendedError(error: unknown): boolean {
  if (!axios.isAxiosError<ApiError>(error)) {
    return false;
  }

  const detail = error.response?.data?.detail;
  return (
    error.response?.status === 402 &&
    typeof detail === 'object' &&
    detail?.code === 'billing_suspended'
  );
}

export function clearAuthStorage(): void {
  clearBrowserSessionMetadata();
  clearLegacyBrowserAuthStorage();
  browserCsrfToken = null;
  clearBrowserSensitiveRuntimeState();
}

function handleBillingSuspended(error: unknown) {
  if (typeof window === 'undefined') {
    return;
  }

  const detail = axios.isAxiosError<ApiError>(error) ? error.response?.data?.detail : undefined;
  const message = getApiDetailMessage(detail) || 'Acesso suspenso. Regularize a assinatura para continuar.';

  clearBrowserSessionMetadata();
  localStorage.setItem('auth_error_message', message);

  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
}

// Função helper para tratar erros
const handleApiError = (error: unknown, defaultMessage: string): never => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    console.error("Detalhes do erro:");

    const parsed = parseApiError(axiosError, defaultMessage);
    const enrichedError = new Error(parsed.message) as EnrichedApiError;
    Object.assign(enrichedError, parsed, {
      apiDetail: axiosError.response?.data?.detail,
    });
    throw enrichedError;
  }
  console.error("Erro não esperado:");
  const parsed = parseApiError(error, defaultMessage);
  const enrichedError = new Error(parsed.message) as EnrichedApiError;
  Object.assign(enrichedError, parsed);
  throw enrichedError;
};

export async function registerUser(
  email: string,
  password: string,
  clinicName: string,
  clinicCnpj: string,
  businessTypeId: number = 1,
  planId?: number
): Promise<string> {
  try {
    const payload = {
      email,
      password,
      clinic: {
        cnpj: clinicCnpj,
        razao_social: clinicName,
        business_type_id: businessTypeId,
        ...(planId && { plan_id: planId }),
      },
    };

    const response = await api.post<RegisterResponse>('/auth/register', payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    console.log("Resposta registro:");
    return response.data.message;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || 'Erro ao registrar usuário';
      throw new Error(message);
    }
    throw error;
  }
}

export async function login(
  email: string,
  password: string
): Promise<BrowserSession> {
  const formData = new URLSearchParams();
  formData.append('username', email);
  formData.append('password', password);

  try {
    const resp = await api.post<BrowserSessionResponse>('/auth/browser/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      _skipAuthRefresh: true,
    } as any);
    updateBrowserCsrfToken(resp.data);

    // O cookie HttpOnly é a autoridade. Confirmamos que foi gravado antes de
    // remover qualquer credencial legada que ainda permita recuperação.
    const session = await getConfirmedBrowserSession();
    clearLegacyBrowserAuthStorage();
    applyLocalBrowserSessionRotation();
    publishBrowserSessionSignal('session-rotated');
    return session;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = getApiDetailMessage(error.response?.data?.detail);
      if (message) {
        const loginError = new Error(message);
        (loginError as Error & { code?: string }).code = error.response?.data?.detail?.code;
        throw loginError;
      } else if (error.response?.status === 401) {
        throw new Error('Credenciais inválidas. Verifique seu email e senha.');
      }
    }
    throw error;
  }
}

function readLegacyBrowserCredentials(): {
  accessToken: string | null;
  refreshToken: string | null;
} {
  if (typeof window === 'undefined') {
    return { accessToken: null, refreshToken: null };
  }

  return {
    accessToken:
      localStorage.getItem('token') || localStorage.getItem('admin_token'),
    refreshToken: localStorage.getItem('refresh_token'),
  };
}

async function getConfirmedBrowserSession(): Promise<BrowserSession> {
  const response = await api.get<BrowserSessionResponse>('/auth/browser/session', {
    _skipAuthRefresh: true,
  } as any);
  updateBrowserCsrfToken(response.data);
  const session = normalizeBrowserSession(response.data);
  persistBrowserSession(session);
  return session;
}

export async function bootstrapBrowserSession(): Promise<BrowserSession | null> {
  purgeLegacyMessageCacheStorage();

  if (browserBootstrapPromise) {
    return browserBootstrapPromise;
  }

  browserBootstrapPromise = (async () => {
    try {
      const session = await getConfirmedBrowserSession();
      clearLegacyBrowserAuthStorage();
      return session;
    } catch (sessionError) {
      if (!axios.isAxiosError(sessionError) || sessionError.response?.status !== 401) {
        throw sessionError;
      }
    }

    const legacy = readLegacyBrowserCredentials();
    if (!legacy.accessToken) {
      clearBrowserSessionMetadata();
      browserCsrfToken = null;
      clearBrowserSensitiveRuntimeState();
      return null;
    }

    try {
      const migration = await api.post<BrowserSessionResponse>(
        '/auth/browser/migrate',
        legacy.refreshToken ? { refresh_token: legacy.refreshToken } : {},
        {
          headers: { Authorization: `Bearer ${legacy.accessToken}` },
          _skipAuthRefresh: true,
        } as any,
      );
      updateBrowserCsrfToken(migration.data);

      const session = await getConfirmedBrowserSession();
      clearLegacyBrowserAuthStorage();
      applyLocalBrowserSessionRotation();
      publishBrowserSessionSignal('session-rotated');
      return session;
    } catch (migrationError) {
      if (axios.isAxiosError(migrationError) && migrationError.response?.status === 409) {
        try {
          const session = await getConfirmedBrowserSession();
          clearLegacyBrowserAuthStorage();
          applyLocalBrowserSessionRotation();
          publishBrowserSessionSignal('session-rotated');
          return session;
        } catch (confirmationError) {
          if (
            axios.isAxiosError(confirmationError) &&
            confirmationError.response?.status === 401
          ) {
            clearBrowserSessionMetadata();
            browserCsrfToken = null;
            clearBrowserSensitiveRuntimeState();
            return null;
          }
          throw confirmationError;
        }
      }

      if (axios.isAxiosError(migrationError) && migrationError.response?.status === 401) {
        clearBrowserSessionMetadata();
        browserCsrfToken = null;
        clearBrowserSensitiveRuntimeState();
        return null;
      }
      throw migrationError;
    }
  })();

  try {
    return await browserBootstrapPromise;
  } finally {
    browserBootstrapPromise = null;
  }
}

export async function refreshBrowserSession(): Promise<BrowserSession> {
  if (browserRefreshPromise) {
    return browserRefreshPromise;
  }

  browserRefreshPromise = (async () => {
    try {
      const response = await api.post<BrowserSessionResponse>(
        '/auth/browser/refresh',
        undefined,
        { _skipAuthRefresh: true } as any,
      );
      updateBrowserCsrfToken(response.data);
      const session = normalizeBrowserSession(response.data);
      persistBrowserSession(session);
      applyLocalBrowserSessionRotation();
      publishBrowserSessionSignal('session-rotated');
      return session;
    } catch (refreshError) {
      if (axios.isAxiosError(refreshError) && refreshError.response?.status === 401) {
        // Outra aba pode ter rotacionado o cookie compartilhado enquanto este
        // refresh estava em voo. Revalida a sessão uma única vez antes de expirar.
        return getConfirmedBrowserSession();
      }
      throw refreshError;
    }
  })();

  try {
    return await browserRefreshPromise;
  } finally {
    browserRefreshPromise = null;
  }
}

export async function logoutBrowserSession(): Promise<void> {
  await api.post('/auth/browser/logout', undefined, {
    _skipAuthRefresh: true,
  } as any);
  clearAuthStorage();
  publishBrowserSessionSignal('logout');
}

async function recoverBrowserSessionCsrf(): Promise<BrowserSession> {
  if (browserCsrfRecoveryPromise) {
    return browserCsrfRecoveryPromise;
  }

  browserCsrfRecoveryPromise = getConfirmedBrowserSession();
  try {
    return await browserCsrfRecoveryPromise;
  } finally {
    browserCsrfRecoveryPromise = null;
  }
}

export async function synchronizeBrowserSessionAfterRotation(): Promise<BrowserSession | null> {
  messageCacheManager.clearAllCache();
  unifiedWebSocketManager.suspendForSessionRotation();
  try {
    const session = await recoverBrowserSessionCsrf();
    unifiedWebSocketManager.resumeAfterSessionRotation();
    return session;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      notifyBrowserSessionExpired(false);
      return null;
    }
    unifiedWebSocketManager.disconnect();
    throw error;
  }
}

function notifyBrowserSessionExpired(broadcastSignal = true): void {
  clearAuthStorage();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(BROWSER_SESSION_EXPIRED_EVENT));
  }
  if (broadcastSignal) {
    publishBrowserSessionSignal('session-expired');
  }
}

export function endBrowserSessionAfterPasswordChange(): void {
  notifyBrowserSessionExpired(true);
}

function isUnsafeHttpMethod(method?: string): boolean {
  return ['post', 'put', 'patch', 'delete'].includes(String(method || '').toLowerCase());
}

function isPreActionBrowserCsrfRejection(error: unknown): boolean {
  if (!axios.isAxiosError<ApiError>(error) || error.response?.status !== 403) {
    return false;
  }

  const detail = error.response.data?.detail;
  return (
    typeof detail === 'object' &&
    detail !== null &&
    detail.code === 'browser_csrf_rejected' &&
    detail.message === 'Token CSRF inválido ou ausente' &&
    detail.request_processed === false
  );
}

// Members utiliza sua própria sessão e token CSRF. O admin mantém a sessão CRM.
export function isMembersSessionRequest(url?: string): boolean {
  const path = String(url || '').split('?')[0];
  return path.startsWith('/api/members/') && !path.startsWith('/api/members/admin/');
}

export function isPasswordSetupRequest(url?: string): boolean {
  const path = String(url || '').split('?')[0];
  return path === '/api/users/password-setup'
    || /^\/(?:api\/)?auth\/browser\/password-recovery\/(?:request|verify|reset)$/.test(path);
}

// O navegador autentica todas as chamadas pela sessão HttpOnly. O token CSRF
// fica apenas em memória e nunca é persistido em storage ou cookie legível.
api.interceptors.request.use(
  (config) => {
    const platformConfig = config as PlatformApiConfig;
    if (isOwnPlatformRequest(platformConfig)) {
      platformConfig._platformIdentity ??= getPlatformAvailabilityIdentity();
      if (!getPlatformAvailabilitySnapshot().ready) {
        if (!isSafePlatformQuery(platformConfig)) {
          throw new PlatformAvailabilityError('platform_maintenance', false);
        }
        return waitForPlatformAvailability({
          signal: platformConfig.signal,
          identity: platformConfig._platformIdentity,
        }).then(async () => {
          await new Promise(resolve => setTimeout(resolve, Math.floor(Math.random() * 250)));
          if (platformConfig._platformIdentity !== getPlatformAvailabilityIdentity() || platformConfig.signal?.aborted) {
            throw new PlatformAvailabilityError('platform_unavailable', false);
          }
          return config;
        });
      }
    }
    if (!isMembersSessionRequest(config.url) && !isPasswordSetupRequest(config.url) && browserCsrfToken && isUnsafeHttpMethod(config.method) && config.headers) {
      config.headers['X-CSRF-Token'] = browserCsrfToken;
    }

    return config;
  },
  (error) => {
    console.error("Erro no interceptor de requisição:");
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (/^\/api\/members\/(?:login|logout|register)$/.test(String(response.config?.url || '').split('?')[0])) {
      platformSessionRevision += 1;
      acknowledgePlatformPendingCommands();
    }
    return response;
  },
  async (error) => {
    if (isPlatformAvailabilityError(error) || isPasswordSetupRequest(error.config?.url)) return Promise.reject(error);
    if (platformFailureKind(error)) {
      const recovered = await recoverPlatformRequest(error);
      if (recovered.handled) return recovered.response;
    }
    if (isMembersSessionRequest(error.config?.url)) return Promise.reject(error);
    if (isBillingSuspendedError(error)) {
      handleBillingSuspended(error);
      return Promise.reject(error);
    }

    const originalRequest = error.config as
      | (typeof error.config & {
          _retry?: boolean;
          _skipAuthRefresh?: boolean;
          _csrfRetry?: boolean;
        })
      | undefined;

    if (
      originalRequest &&
      !originalRequest._csrfRetry &&
      isUnsafeHttpMethod(originalRequest.method) &&
      isPreActionBrowserCsrfRejection(error)
    ) {
      originalRequest._csrfRetry = true;

      try {
        await recoverBrowserSessionCsrf();
        return api(originalRequest);
      } catch (recoveryError) {
        if (axios.isAxiosError(recoveryError) && recoveryError.response?.status === 401) {
          notifyBrowserSessionExpired();
        }
        return Promise.reject(recoveryError);
      }
    }

    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest._skipAuthRefresh
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      await refreshBrowserSession();
      return api(originalRequest);
    } catch (refreshError) {
      if (axios.isAxiosError(refreshError) && refreshError.response?.status === 401) {
        notifyBrowserSessionExpired();
      }
      return Promise.reject(refreshError);
    }
  }
);

/**
 * Retorna a URL do WebSocket para o chat. A autenticação ocorre pelo cookie
 * HttpOnly enviado automaticamente no handshake do navegador.
 *
 * Se 'phone' não for passado, usaremos "__global__" como phone,
 * permitindo que o backend (e seu manager) tratem como conexão global
 * para receber mensagens de contatos novos ou gerais.
 */
export function getChatWebSocketUrl(phone?: string): string {
  const rawClinicId = localStorage.getItem('clinic_id');
  if (!rawClinicId) {
    throw new Error('clinic_id ausente no localStorage. Não é possível prosseguir.');
  }

  const clinicId = parseInt(rawClinicId, 10);
  if (!clinicId) {
    throw new Error(`clinic_id inválido (${rawClinicId}).`);
  }

  const finalPhone = phone && phone.trim() !== '' ? phone.trim() : '__global__';

  const protocol = API_URL.startsWith('https') ? 'wss' : 'ws';
  const domain = API_URL.replace(/^https?:\/\//, ''); // remove "http://" ou "https://"

  return (
    `${protocol}://${domain}/ws/chat` +
    `?clinic_id=${encodeURIComponent(String(clinicId))}` +
    `&phone=${encodeURIComponent(finalPhone)}`
  );
}

// Exemplo adaptado para getMessageHistory()
export async function getMessageHistory(contact_phone: string): Promise<OptimizedMessage[]> {
  try {
    const rawClinicId = localStorage.getItem('clinic_id');
    const clinicId = rawClinicId ? parseInt(rawClinicId, 10) : null;
    let url = `/webhook/history?contact_phone=${encodeURIComponent(contact_phone)}`;

    if (clinicId) {
      url += `&clinic_id=${clinicId}`;
    }

    const resp = await api.get(url);
    // resp.data.messages => array de mensagens, cada uma com type, content, etc.

    // Precisamos transformar as mensagens
    const processed = await Promise.all(resp.data.messages.map(async (m: any) => {
      const messageDate = new Date(m.timestamp);

      let finalContent: string | { url: string; mimeType?: string } = m.content;

      // Se for mídia (imagem, vídeo, áudio) E o backend não trouxer a URL completa,
      // chamamos algo como getMessageMedia() ou fetchFileBlob() para converter path -> BlobURL
      if (m.type === 'image' || m.type === 'video' || m.type === 'audio') {
        // Exemplo: se seu backend exige GET /media/messages/{clientId}/{clinicId}/{arquivo}
        // e você tem a função getMessageMedia(content, fromMe)
        try {
          const blobUrl = await getMessageMedia(m.content, m.fromMe);
          finalContent = { url: blobUrl };
        } catch (err) {
          console.error("Erro ao obter mídia do histórico:");
        }
      }

      // Para mensagens NPS, processar o conteúdo específico
      if (m.type === 'nps') {
        try {
          if (typeof m.content === 'string') {
            const npsContent = JSON.parse(m.content);
            finalContent = npsContent;
          } else {
            finalContent = m.content;
          }
        } catch (err) {
          console.error("Erro ao processar conteúdo NPS:");
          finalContent = m.content;
        }
      }

      return {
        id: m.id.toString(),
        type: m.type,
        content: finalContent,
        sender: {
          phone: m.sender.phone,
          name: m.sender.name,
          photo: m.sender.photo || '',
        },
        timestamp: messageDate.toLocaleTimeString(),
        timestampNumber: messageDate.getTime(),
        fromMe: m.fromMe,
        source_channel: m.source_channel,
        status: m.status,
        ack: m.ack,
        providerMessageId: m.providerMessageId || m.messageId || null,
        templateSnapshot: m.templateSnapshot,
        deliveryError: m.deliveryError,
        deliveryStatusAt: m.deliveryStatusAt,
        author_type: m.author_type,
        author_id: m.author_id,
        author_name: m.author_name,
        author_evidence: m.author_evidence,
        agent_run_id: m.agent_run_id,
        learning_action: m.learning_action,
        databaseMessageId: Number.isInteger(Number(m.id)) && Number(m.id) > 0 ? Number(m.id) : null
      };
    }));

    return processed;
  } catch (error) {
    console.error("Erro ao obter histórico de mensagens:");
    throw error;
  }
}

interface ContactsFilters {
  channel?: 'instagram' | 'whatsapp';
  conversation_state?: 'open' | 'closed' | 'archived';
  assignment?: 'mine' | 'unassigned' | 'all';
  limit?: number;
  offset?: number;
  search?: string;
  unread_only?: boolean;
  show_archived?: boolean;
  archived_only?: boolean;
  funnel_stages?: string[];
  active_flows?: string[];
  lightweight?: boolean;
}

interface PagedContactsResponse {
  contacts: Contact[];
  total: number;
  has_more: boolean;
}

const contactsInFlightRequests = new Map<string, Promise<PagedContactsResponse>>();

function normalizeContactSearchParam(search?: string): string | undefined {
  const trimmedSearch = search?.trim();
  if (!trimmedSearch) return undefined;

  const digitsOnly = trimmedSearch.replace(/\D/g, '');
  const hasLetters = /[A-Za-zÀ-ÿ]/.test(trimmedSearch);

  return digitsOnly.length >= 8 && !hasLetters ? digitsOnly : trimmedSearch;
}

export async function getContacts(filters: ContactsFilters = {}): Promise<PagedContactsResponse> {
  try {
    const rawOwnClientId = localStorage.getItem('client_id');
    const clinicId = localStorage.getItem('clinic_id');

    // Para usuários staff (user), sempre usar o próprio client_id
    // O backend já trata as permissões corretamente
    let finalClientId = rawOwnClientId;

    // Construir query parameters com paginação
    const params = new URLSearchParams({
      client_id: finalClientId || '',
      clinic_id: clinicId || '',
      limit: (filters.limit || 50).toString(),
      offset: (filters.offset || 0).toString()
    });

    if (filters.conversation_state) params.append('conversation_state', filters.conversation_state);
    if (filters.assignment) params.append('assignment', filters.assignment);
    if (filters.channel) params.append('channel', filters.channel);
    const normalizedSearch = normalizeContactSearchParam(filters.search);
    if (normalizedSearch) {
      params.append('search', normalizedSearch);
    }
    if (filters.unread_only) {
      params.append('unread_only', 'true');
    }
    if (filters.show_archived) {
      params.append('show_archived', 'true');
    }
    if (filters.archived_only) {
      params.append('archived_only', 'true');
    }
    if (filters.funnel_stages && filters.funnel_stages.length > 0) {
      params.append('funnel_stages', filters.funnel_stages.join(','));
    }
    if (filters.active_flows && filters.active_flows.length > 0) {
      params.append('active_flows', filters.active_flows.join(','));
    }
    if (filters.lightweight) {
      params.append('lightweight', 'true');
    }

    const requestUrl = `/webhook/contacts?${params.toString()}`;
    const inFlightRequest = contactsInFlightRequests.get(requestUrl);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const requestPromise = api.get(requestUrl).then((resp) => {
      const rawContacts = Array.isArray(resp.data.contacts) ? resp.data.contacts : [];

      console.log("[API] Contatos recebidos:");

      const contacts = rawContacts.map((c: any) => {
        const lastMessageAt = c.last_message_at ? new Date(c.last_message_at) : null;
        const timestampNumber = lastMessageAt ? lastMessageAt.getTime() : 0;

        return {
          id: c.id,
          phone: c.phone || '',
          instagram_identity_id: c.instagram_identity_id ?? null,
          instagram_username: c.instagram_username ?? null,
          instagram_last_inbound_at: c.instagram_last_inbound_at ?? null,
          name: c.name,
          photo: c.photo || '',
          lastMessage: c.last_message || '',
          lastMessageType: c.last_message_type ?? null,
          lastMessageStatus: c.last_message_status || undefined,
          lastMessageFromMe: c.last_message_from_me === true,
          lastMessageProviderId: c.last_message_provider_id || null,
          lastMessageSourceChannel: c.last_message_source_channel || null,
          lastMessageDeliveryError: c.last_message_delivery_error,
          lastMessageDeliveryStatusAt: c.last_message_delivery_status_at,
          unreadCount: c.unread_count ?? 0,
          timestamp: lastMessageAt ? formatChatTimestamp(timestampNumber) : '',
          timestampNumber,
          human_mode: c.human_mode ?? false,
          last_message_at: c.last_message_at,
          source_id: c.source_id,
          thumbnail_url: c.thumbnail_url,
          sender_lid: c.sender_lid,
          lead_id: c.lead_id ?? null,
          possible_duplicate: c.possible_duplicate === true,
          duplicate_contact_id: c.duplicate_contact_id ?? null,
          archived: c.archived === true,
          funnel_stage: c.funnel_stage || 'lead',
          funnel_status: c.funnel_status || {},
          flow_progress: c.flow_progress || null,
          tags: c.tags || [],
          active_channel: c.active_channel
        };
      });

      return {
        contacts,
        total: resp.data.total || contacts.length,
        has_more: resp.data.has_more || false
      };
    });

    contactsInFlightRequests.set(requestUrl, requestPromise);

    try {
      return await requestPromise;
    } finally {
      contactsInFlightRequests.delete(requestUrl);
    }
  } catch (error) {
    console.error("Erro ao obter contatos:");
    throw error;
  }
}

// Função de compatibilidade para não quebrar código existente
export async function getContactsLegacy(): Promise<Contact[]> {
  const response = await getContacts({ limit: 1000 }); // Carregar mais para compatibilidade
  return response.contacts;
}

// Funções de arquivamento
export async function archiveContact(phone: string, clinicId: number, reason?: string): Promise<any> {
  try {
    const contactId = conversationContactId(phone);
    const resource = contactId ? `by-id/${contactId}` : encodeURIComponent(phone);
    const response = await api.put(`/webhook/contacts/${resource}/archive`, {
      reason
    }, {
      params: { clinic_id: clinicId }
    });

    console.log("✅ CHECKPOINT: Contato arquivado com sucesso:");
    return response.data;
  } catch (error) {
    console.error("❌ Erro ao arquivar contato:");
    throw error;
  }
}

export async function unarchiveContact(phone: string, clinicId: number): Promise<any> {
  try {
    const contactId = conversationContactId(phone);
    const resource = contactId ? `by-id/${contactId}` : encodeURIComponent(phone);
    const response = await api.put(`/webhook/contacts/${resource}/unarchive`, {}, {
      params: { clinic_id: clinicId }
    });

    console.log("✅ CHECKPOINT: Contato desarquivado com sucesso:");
    return response.data;
  } catch (error) {
    console.error("❌ Erro ao desarquivar contato:");
    throw error;
  }
}

export async function getContactAuditLog(clinicId: number, filters?: {
  contact_id?: number;
  action_type?: string;
  limit?: number;
  offset?: number;
}): Promise<any> {
  try {
    const params = new URLSearchParams({
      clinic_id: clinicId.toString()
    });

    if (filters) {
      if (filters.contact_id) params.append('contact_id', filters.contact_id.toString());
      if (filters.action_type) params.append('action_type', filters.action_type);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());
    }

    const response = await api.get(`/webhook/contacts/audit-log?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error("❌ Erro ao buscar logs de auditoria:");
    throw error;
  }
}

export interface CrmAuditEvent {
  id: number;
  entity_type: 'lead' | 'agendamento' | 'comparecimento' | 'venda' | 'noshow' | string;
  entity_id: number;
  action: 'created' | 'updated' | 'deleted' | 'marked_no_show' | string;
  actor_type: 'user' | 'master' | 'client' | 'ai_agent' | 'system' | 'webhook' | string;
  actor_user_id?: number | null;
  actor_client_id?: number | null;
  created_via?: string | null;
  metadata?: Record<string, any>;
  created_at: string;
  actor_user_name?: string | null;
  actor_user_email?: string | null;
  actor_client_email?: string | null;
}

export async function getCrmAuditEvents(
  clinicId: number,
  phone: string,
  limit: number = 200,
  contactId?: number,
): Promise<{ events: CrmAuditEvent[]; total: number }> {
  try {
    const params = new URLSearchParams({
      clinic_id: clinicId.toString(),
      phone,
      limit: limit.toString(),
    });
    if (contactId) {
      params.set('contact_id', String(contactId));
      if (!phone) params.delete('phone');
    }
    const response = await api.get(`/webhook/contacts/crm-audit-events?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error("❌ Erro ao buscar eventos CRM:");
    return { events: [], total: 0 };
  }
}

export async function getContactsNoHistory(filters: ContactsFilters = {}): Promise<PagedContactsResponse> {
  try {
    const rawOwnClientId = localStorage.getItem('client_id');
    const clinicId = localStorage.getItem('clinic_id');

    // Para usuários staff (user), sempre usar o próprio client_id
    // O backend já trata as permissões corretamente
    let finalClientId = rawOwnClientId;

    // Construir query parameters com paginação para contatos sem histórico
    const params = new URLSearchParams({
      client_id: finalClientId || '',
      clinic_id: clinicId || '',
      limit: (filters.limit || 50).toString(),
      offset: (filters.offset || 0).toString()
    });

    if (filters.conversation_state) params.append('conversation_state', filters.conversation_state);
    if (filters.assignment) params.append('assignment', filters.assignment);
    const normalizedSearch = normalizeContactSearchParam(filters.search);
    if (normalizedSearch) {
      params.append('search', normalizedSearch);
    }

    // Fazemos a requisição GET para o endpoint específico de contatos sem histórico
    const resp = await api.get(`/webhook/contacts/no-history?${params.toString()}`);

    const rawContacts = Array.isArray(resp.data.contacts) ? resp.data.contacts : [];

    console.log("[API] Contatos sem histórico recebidos:");

    // Mapeamos cada contato retornado
    const contacts = rawContacts.map((c: any) => {
      return {
        id: c.id,
        phone: c.phone,
        name: c.name,
        photo: c.photo || '',
        lastMessage: c.last_message || '',
          lastMessageType: c.last_message_type ?? null,
        unreadCount: c.unread_count ?? 0,
        timestamp: '',
        timestampNumber: 0, // Contatos sem histórico têm timestamp 0
        human_mode: c.human_mode ?? false,
        last_message_at: c.last_message_at,
        source_id: c.source_id,
        thumbnail_url: c.thumbnail_url,
        sender_lid: c.sender_lid,
        lead_id: c.lead_id ?? null,
        possible_duplicate: c.possible_duplicate === true,
        duplicate_contact_id: c.duplicate_contact_id ?? null,
        archived: c.archived === true,
        funnel_stage: c.funnel_stage || 'lead',
        funnel_status: c.funnel_status || {},
        active_channel: c.active_channel
      };
    });

    return {
      contacts,
      total: resp.data.total || contacts.length,
      has_more: resp.data.has_more || false
    };
  } catch (error) {
    console.error("Erro ao obter contatos sem histórico:");
    throw error;
  }
}

/**
 * Marca o contato como lido no backend (unread_count = 0).
 */
export async function markContactAsRead(phone: string): Promise<void> {
  try {
    // Descobrir se é 'user' ou 'master'
    const rawOwnClientId = localStorage.getItem('client_id');
    const clinicId = localStorage.getItem('clinic_id');

    if (!clinicId) {
      throw new Error("Clinic ID não encontrado no localStorage");
    }

    // Para usuários staff (user), sempre usar o próprio client_id
    // O backend já trata as permissões corretamente
    let finalClientId = rawOwnClientId;

    // Montar a URL
    // Ex: PUT /webhook/contacts/5521999999999/read?client_id=6&clinic_id=1
    const url = `/webhook/contacts/${phone}/read?client_id=${finalClientId}&clinic_id=${clinicId}`;
    await api.put(url);
    window.dispatchEvent(new Event('contact-read-changed'));

  } catch (error) {
    console.error("[markContactAsRead] Erro ao marcar como lido:");
    throw error;
  }
}

/**
 * Converte um contato para paciente
 */
export async function convertContactToPatient(contactId: number): Promise<void> {
  try {
    await api.post(`/webhook/contacts/${contactId}/convert-to-patient`);

  } catch (error) {
    console.error("[convertContactToPatient] Erro ao converter para paciente:");
    throw error;
  }
}

// Contact Tasks API
export interface TaskCreate {
  contact_id: number;
  task_type: 'message' | 'call' | 'email' | 'custom';
  title: string;
  description?: string;
  scheduled_for: string;
  reminder_minutes?: number;
  assigned_to?: number;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  tags?: string[];
  metadata?: any;
}

export interface TaskUpdate {
  task_type?: 'message' | 'call' | 'email' | 'custom';
  title?: string;
  description?: string;
  scheduled_for?: string;
  reminder_minutes?: number;
  assigned_to?: number;
  status?: 'pending' | 'in_progress' | 'completed' | 'canceled';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  tags?: string[];
  metadata?: any;
}

export interface Task {
  id: number;
  contact_id: number;
  contact_name: string;
  contact_phone: string;
  task_type: 'message' | 'call' | 'email' | 'custom';
  title: string;
  description?: string;
  scheduled_for: string;
  reminder_minutes: number;
  status: 'pending' | 'in_progress' | 'completed' | 'canceled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags?: string[];
  metadata?: any;
  created_at: string;
  updated_at: string;
  created_by: {
    id: number;
    name: string;
    email: string;
  };
  assigned_to?: {
    id: number;
    name: string;
    email: string;
  };
  completed_at?: string;
  completed_by?: {
    id: number;
    name: string;
    email: string;
  };
  comments_count: number;
}

// Get tasks for a contact
export async function getContactTasks(
  contactId: number,
  filters?: {
    status?: string;
    priority?: string;
    task_type?: string;
    assigned_to_me?: boolean;
  }
): Promise<Task[]> {
  try {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.task_type) params.append('task_type', filters.task_type);
    if (filters?.assigned_to_me) params.append('assigned_to_me', 'true');

    const response = await api.get(`/api/contacts/${contactId}/tasks?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching contact tasks:");
    handleApiError(error, 'Erro ao buscar tarefas do contato');
    return [];
  }
}

// Create a new task
export async function createTask(contactId: number, taskData: TaskCreate): Promise<Task> {
  try {
    const response = await api.post(`/api/contacts/${contactId}/tasks`, taskData);
    return response.data;
  } catch (error) {
    console.error("Error creating task:");
    handleApiError(error, 'Erro ao criar tarefa');
    throw error;
  }
}

// Update a task
export async function updateTask(taskId: number, taskData: TaskUpdate): Promise<Task> {
  try {
    const response = await api.put(`/api/tasks/${taskId}`, taskData);
    return response.data;
  } catch (error) {
    console.error("Error updating task:");
    handleApiError(error, 'Erro ao atualizar tarefa');
    throw error;
  }
}

// Complete a task
export async function completeTask(taskId: number): Promise<Task> {
  try {
    const response = await api.post(`/api/tasks/${taskId}/complete`);
    return response.data;
  } catch (error) {
    console.error("Error completing task:");
    handleApiError(error, 'Erro ao concluir tarefa');
    throw error;
  }
}

// Delete a task
export async function deleteTask(taskId: number): Promise<void> {
  try {
    await api.delete(`/api/tasks/${taskId}`);
  } catch (error) {
    console.error("Error deleting task:");
    handleApiError(error, 'Erro ao excluir tarefa');
    throw error;
  }
}

// Get upcoming tasks
export async function getUpcomingTasks(params?: {
  limit?: number;
  include_overdue?: boolean;
  assigned_to_me?: boolean;
}): Promise<Task[]> {
  try {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.include_overdue !== undefined) queryParams.append('include_overdue', params.include_overdue.toString());
    if (params?.assigned_to_me) queryParams.append('assigned_to_me', 'true');

    const response = await api.get(`/api/tasks/upcoming?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching upcoming tasks:");
    handleApiError(error, 'Erro ao buscar próximas tarefas');
    return [];
  }
}

// Notes API Functions
export interface Note {
  id: number;
  contact_id: number;
  contact_name: string;
  contact_phone: string;
  content: string;
  created_at: string;
  updated_at: string;
  created_by: {
    id: number;
    name: string;
    email: string;
    type: string;
  };
}

export interface NoteCreate {
  content: string;
}

export interface NoteUpdate {
  content: string;
}

// Get notes for a contact
export async function getContactNotes(contactPhone: string): Promise<Note[]> {
  try {
    const contactId = conversationContactId(contactPhone);
    const resource = contactId ? `by-id/${contactId}` : encodeURIComponent(contactPhone);
    const response = await api.get(`/api/contacts/${resource}/notes`);
    return response.data;
  } catch (error) {
    console.error("Error fetching notes:");
    handleApiError(error, 'Erro ao buscar anotações');
    return [];
  }
}

// Create a new note
export async function createNote(contactPhone: string, noteData: NoteCreate): Promise<Note> {
  try {
    const contactId = conversationContactId(contactPhone);
    const resource = contactId ? `by-id/${contactId}` : encodeURIComponent(contactPhone);
    const response = await api.post(`/api/contacts/${resource}/notes`, noteData);
    return response.data;
  } catch (error) {
    console.error("Error creating note:");
    handleApiError(error, 'Erro ao criar anotação');
    throw error;
  }
}

// Update a note
export async function updateNote(noteId: number, noteData: NoteUpdate): Promise<Note> {
  try {
    const response = await api.put(`/api/notes/${noteId}`, noteData);
    return response.data;
  } catch (error) {
    console.error("Error updating note:");
    handleApiError(error, 'Erro ao atualizar anotação');
    throw error;
  }
}

// Delete a note
export async function deleteNote(noteId: number): Promise<void> {
  try {
    await api.delete(`/api/notes/${noteId}`);
  } catch (error) {
    console.error("Error deleting note:");
    handleApiError(error, 'Erro ao excluir anotação');
    throw error;
  }
}

// Get all user notes
export async function getAllUserNotes(params?: {
  limit?: number;
  offset?: number;
}): Promise<Note[]> {
  try {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const response = await api.get(`/api/notes/all?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching user notes:");
    handleApiError(error, 'Erro ao buscar anotações do usuário');
    return [];
  }
}

// Add comment to task
export async function addTaskComment(taskId: number, comment: string): Promise<any> {
  try {
    const response = await api.post(`/api/tasks/${taskId}/comments`, { comment });
    return response.data;
  } catch (error) {
    console.error("Error adding task comment:");
    handleApiError(error, 'Erro ao adicionar comentário');
    throw error;
  }
}

// Get task comments
export async function getTaskComments(taskId: number): Promise<any[]> {
  try {
    const response = await api.get(`/api/tasks/${taskId}/comments`);
    return response.data;
  } catch (error) {
    console.error("Error fetching task comments:");
    handleApiError(error, 'Erro ao buscar comentários');
    return [];
  }
}

// Get task statistics
export async function getTaskStatistics(): Promise<any> {
  try {
    const response = await api.get('/api/tasks/statistics');
    return response.data;
  } catch (error) {
    console.error("Error fetching task statistics:");
    handleApiError(error, 'Erro ao buscar estatísticas de tarefas');
    return null;
  }
}

export interface TaskNotification {
  type: string;
  message: string;
  count: number;
  tasks: Task[];
  clinic_id?: number;
  user_id?: number | string;
  user_type?: string;
}

export async function getPendingNotifications(): Promise<TaskNotification | null> {
  try {
    // Get browser timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const response = await api.get('/api/notifications/pending', {
      headers: {
        'x-timezone': userTimezone
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching pending notifications:");
    handleApiError(error, 'Erro ao buscar notificações pendentes');
    return null;
  }
}

// ============================================
// 24h Conversation Window API
// ============================================
export interface ConversationWindowStatus {
  is_active: boolean;
  window_opened_at: string | null;
  window_expires_at: string | null;
  time_remaining: string | null;
  can_send_session_message: boolean;
  origin_type?: string;
  clinic_timezone?: string;
  message?: string;
}

/**
 * Get the 24h conversation window status for a contact
 * This helps understand if we can send non-template messages
 */
export async function get24hWindowStatus(
  clinicId: number,
  phone: string
): Promise<ConversationWindowStatus | null> {
  try {
    const response = await api.get(`/api/conversation-windows/clinic/${clinicId}/contact/${phone}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching 24h window status:");
    // Return null instead of throwing to handle gracefully
    return null;
  }
}

// =============================================================================
// TEMPLATE DIRECT SEND (Envio de templates fora da janela de 24h)
// =============================================================================

/**
 * Template disponível para seleção no chat
 */
export interface TemplateForChat {
  id: number;
  name: string;
  category: string; // UTILITY | MARKETING
  body_text: string | null;
  credits_cost: number; // Campo legado: novos envios oficiais não consomem créditos Sminy.
}

/**
 * Request para enviar template diretamente
 */
export interface TemplateSendRequest {
  clinic_id: number;
  phone: string;
  contact_id?: number;
  template_name: string;
  language?: string;
  components?: any[];
}

/**
 * Resposta do envio de template direto
 */
export interface TemplateSendResponse {
  success: boolean;
  message_id?: string;
  template_name: string;
  phone: string;
  credits_consumed: number; // Campo legado: zero para novos envios oficiais.
  category: string;
  error?: string;
}

/**
 * Get approved templates available for direct sending in chat
 * Lists all approved templates, not just campaign ones
 */
export async function getTemplatesForChat(clinicId: number): Promise<TemplateForChat[]> {
  const response = await api.get(`/api/template-send/templates-for-chat`, {
    params: { clinic_id: clinicId }
  });
  return response.data;
}

/**
 * Send a template directly from the chat (for contacts outside 24h window)
 * WhatsApp Oficial é cobrado diretamente pela Meta, sem débito de créditos Sminy.
 */
export async function sendTemplateDirect(
  payload: TemplateSendRequest
): Promise<TemplateSendResponse> {
  const response = await api.post(`/api/template-send/direct`, payload);
  return response.data;
}

/**
 * Saldo histórico de créditos de uma clínica; não condiciona novos envios oficiais.
 */
export async function getCreditsBalance(clinicId: number): Promise<{ clinic_id: number; credits_balance: number }> {
  try {
    const response = await api.get(`/api/template-send/credits-balance`, {
      params: { clinic_id: clinicId }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching credits balance:");
    return { clinic_id: clinicId, credits_balance: 0 };
  }
}

export default api;


export async function saveAgentConfig(payload: any): Promise<string> {
  try {
    console.log("Enviando payload de AgentConfig:");
    const clinicId = localStorage.getItem('clinic_id');
    if (!clinicId) {
      console.error("clinic_id não encontrado no localStorage. Não é possível salvar config.");
      throw new Error('Não foi possível determinar a clínica para salvar as configurações.');
    }

    // Inclua clinic_id no payload
    payload.clinic_id = Number(clinicId);

    console.log("Payload final com clinic_id:");

    const resp = await api.post('/agent-config', payload);
    console.log("Resposta AgentConfig:");
    return resp.data.message || 'Configurações salvas com sucesso!';
  } catch (error) {
    console.error("Erro ao salvar AgentConfig:");
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || 'Erro ao salvar configurações do agente.';
      throw new Error(message);
    }
    throw error;
  }
}

export interface ClinicInfo {
  name: string;
  name_clinic: string | null;
  logo_url: string | null;
  id?: number;
  plan?: {
    id: number;
    name: string;
    price: number;
    features: any;
  } | null;
}

export interface BillingPayment {
  id: number;
  asaas_payment_id: string;
  status: string;
  billing_type?: string | null;
  description?: string | null;
  value?: number | null;
  due_date?: string | null;
  payment_date?: string | null;
  invoice_url?: string | null;
  bank_slip_url?: string | null;
  payment_url?: string | null;
}

export interface BillingAlert {
  type: 'due_today' | 'overdue' | string;
  severity: 'warning' | 'danger' | string;
  title: string;
  message: string;
  due_date?: string | null;
  value?: number | null;
  payment_url?: string | null;
  payment?: BillingPayment | null;
}

export interface BillingSummary {
  clinic: {
    id: number;
    name: string;
  };
  plan: {
    id: number;
    name: string;
    price: number;
    features: any;
  } | null;
  billing: {
    status: 'trialing' | 'active' | 'past_due' | 'suspended' | 'canceled' | string;
    grace_until?: string | null;
    suspended_at?: string | null;
    last_payment_at?: string | null;
    last_error?: string | null;
    grace_days: number;
  };
  asaas: {
    customer_id?: string | null;
    subscription_id?: string | null;
    subscription_status?: string | null;
    subscription_billing_type?: string | null;
    subscription_value?: number | null;
  };
  payments: BillingPayment[];
  billing_alert?: BillingAlert | null;
  contract?: {
    id: number;
    status: string;
    d4sign_document_uuid?: string | null;
    d4sign_template_id?: string | null;
    sent_at?: string | null;
    signed_at?: string | null;
    last_error?: string | null;
  } | null;
  sync?: {
    status: string;
    [key: string]: any;
  } | null;
}

export interface BillingContractProfile {
  legal_name?: string | null;
  document?: string | null;
  document_type?: string | null;
  address?: string | null;
  representative_name?: string | null;
  representative_document?: string | null;
  representative_role?: string | null;
  billing_email?: string | null;
  billing_phone?: string | null;
}

export interface BillingSubscriptionClinicOption {
  clinic_id: number;
  name: string;
  legal_name?: string | null;
  document?: string | null;
  plan_id?: number | null;
  plan_name?: string | null;
  plan_price?: number | null;
  billing_status?: string | null;
  trial_ends_at?: string | null;
  billing_phone?: string | null;
  asaas_customer_id?: string | null;
  asaas_subscription_id?: string | null;
  asaas_subscription_status?: string | null;
  contract_profile?: BillingContractProfile | null;
}

export interface BillingSubscriptionPlanOption {
  id: number;
  name: string;
  price: number;
  features?: any;
}

export interface BillingSubscriptionOptions {
  clinics: BillingSubscriptionClinicOption[];
  plans: BillingSubscriptionPlanOption[];
  billing_email?: string | null;
  billing_types: string[];
  cycles: string[];
}

export interface CreateBillingSubscriptionPayload {
  clinic_id: number;
  plan_id: number;
  value: number;
  billing_type: string;
  cycle: string;
  next_due_date: string;
  end_date?: string | null;
  description?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  contract_profile?: {
    legal_name: string;
    document: string;
    address: string;
    representative_name: string;
    representative_document: string;
    representative_role: string;
    billing_email: string;
    billing_phone?: string | null;
  };
}

export interface CreateBillingSubscriptionResponse {
  ok: boolean;
  clinic_id: number;
  plan_id: number;
  asaas_customer_id: string;
  asaas_subscription_id: string;
  asaas_subscription_status?: string | null;
  billing_status: string;
  payments_synced: number;
  contract_id?: number | null;
  contract_status?: string | null;
  d4sign_document_uuid?: string | null;
  contract_error?: string | null;
}

export interface ClinicWhatsAppConfig {
  zapi_instance_id?: string;
  zapi_configured?: boolean;
  waha_enabled?: boolean;
  waha_session_name?: string;
  evolution_instance_id?: string;
  wppconnect_session_name?: string;
}

export interface ExtendedClinicInfo extends ClinicInfo {
  id?: number;
  whatsapp_config?: ClinicWhatsAppConfig;
}

let cachedClinicWhatsAppConfig: ClinicWhatsAppConfig | null = null;

function clearCachedClinicWhatsAppConfig(): void {
  cachedClinicWhatsAppConfig = null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function sanitizeClinicWhatsAppConfig(value: unknown): ClinicWhatsAppConfig {
  const raw = value && typeof value === 'object'
    ? value as Record<string, unknown>
    : {};
  return {
    zapi_instance_id: optionalString(raw.zapi_instance_id),
    zapi_configured: raw.zapi_configured === true,
    waha_enabled: raw.waha_enabled === true,
    waha_session_name: optionalString(raw.waha_session_name),
    evolution_instance_id: optionalString(raw.evolution_instance_id),
    wppconnect_session_name: optionalString(raw.wppconnect_session_name),
  };
}

export async function getClinicInfo(): Promise<ClinicInfo> {
  try {
    const resp = await api.get('/api/clinic');
    console.log("Dados retornados da clínica:");
    const data = resp.data.clinic;
    const finalName = typeof data.name === 'string' ? data.name : '';
    const finalNameClinic = typeof data.name_clinic === 'string' ? data.name_clinic : null;
    const finalLogo = typeof data.logo_url === 'string' ? data.logo_url : null;

    return {
      id: data.id,
      name: finalName,
      name_clinic: finalNameClinic,
      logo_url: finalLogo,
      plan: data.plan, // ✅ Incluindo plano na resposta
    };
  } catch (error) {
    handleApiError(error, 'Erro ao obter informações da clínica');
    // Aqui, para satisfazer o TS que requer um retorno, precisamos lançar ou retornar algo.
    throw new Error('Unreachable');
  }
}

export async function getBillingSummary(sync: boolean = false): Promise<BillingSummary> {
  try {
    const resp = await api.get('/api/billing/summary', { params: { sync } });
    return resp.data;
  } catch (error) {
    handleApiError(error, 'Erro ao obter informações de cobrança');
    throw new Error('Unreachable');
  }
}

export async function syncBillingSummary(): Promise<BillingSummary> {
  try {
    const resp = await api.post('/api/billing/sync');
    return resp.data;
  } catch (error) {
    handleApiError(error, 'Erro ao sincronizar cobrança');
    throw new Error('Unreachable');
  }
}

export async function getBillingSubscriptionOptions(): Promise<BillingSubscriptionOptions> {
  try {
    const resp = await api.get('/api/billing/subscription-options');
    return resp.data;
  } catch (error) {
    handleApiError(error, 'Erro ao carregar opções de assinatura');
    throw new Error('Unreachable');
  }
}

export async function createBillingSubscription(
  payload: CreateBillingSubscriptionPayload
): Promise<CreateBillingSubscriptionResponse> {
  try {
    const resp = await api.post('/api/billing/subscription', payload);
    return resp.data;
  } catch (error) {
    handleApiError(error, 'Erro ao criar assinatura');
    throw new Error('Unreachable');
  }
}

export async function getExtendedClinicInfo(): Promise<ExtendedClinicInfo> {
  try {
    const resp = await api.get('/api/clinic/extended');
    const data = resp.data;
    const whatsappConfig = sanitizeClinicWhatsAppConfig(data.whatsapp_config);
    cachedClinicWhatsAppConfig = whatsappConfig;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('clinic_whatsapp_config');
    }

    const finalName = ''; // Opcional caso não venha do backend
    const finalNameClinic = typeof data.name_clinic === 'string' ? data.name_clinic : null;
    const finalLogo = typeof data.logo_url === 'string' ? data.logo_url : null;

    const result = {
      id: data.id,  // 🔥 INCLUIR O ID DA CLÍNICA
      name: finalName,
      name_clinic: finalNameClinic,
      logo_url: finalLogo,
      whatsapp_config: whatsappConfig,
    };
    return result;
  } catch (error) {
    handleApiError(error, 'Erro ao obter informações completas da clínica');
    throw new Error('Unreachable');
  }
}

export async function updateClinicInfo(name_clinic: string, file?: File): Promise<string> {
  try {
    const formData = new FormData();
    formData.append('name_clinic', name_clinic);
    if (file) {
      formData.append('logo', file);
    }

    console.log("Enviando atualização da clínica:");
    const resp = await api.put('/api/clinic', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    console.log("Resposta atualização clínica:");
    return resp.data.message || 'Configurações da clínica atualizadas com sucesso!';
  } catch (error) {
    console.error("Erro ao atualizar clínica:");
    handleApiError(error, 'Erro ao atualizar configurações da clínica');
    throw new Error('Unreachable');
  }
}

/**
 * Deleta (reseta) as informações da clínica (name_clinic, logo_url).
 * DELETE /api/clinic
 */
export async function deleteClinicInfo(): Promise<string> {
  try {
    const resp = await api.delete('/api/clinic');
    // A resposta deve conter algo como { message: "...sucesso..." }
    return resp.data.message || 'Nome e logo da clínica removidos com sucesso!';
  } catch (error) {
    console.error("Erro ao remover as informações da clínica:");
    handleApiError(error, 'Erro ao remover as informações da clínica');
    throw new Error('Unreachable');
  }
}

// Função para obter configurações do agente
// Função para obter configurações do agente
export async function getAgentConfig(): Promise<any> {
  console.log("%c[AgentConfig] Iniciando getAgentConfig...");

  const clinicId = localStorage.getItem('clinic_id');
  console.log("%c[AgentConfig] clinicId obtido do localStorage:");

  if (!clinicId) {
    console.error("%c[AgentConfig] clinic_id não encontrado no localStorage ao tentar obter config do agente.");
    throw new Error('Não foi possível determinar a clínica para obter as configurações.');
  }

  try {

    const resp = await api.get(`/agent-config/${clinicId}`);

    console.log("%c[AgentConfig] Resposta getAgentConfig:");
    return resp.data;
  } catch (error) {
    console.error("%c[AgentConfig] Erro ao obter AgentConfig:");
    handleApiError(error, 'Erro ao obter configurações do agente');
    throw new Error('Unreachable');
  }
}

export type AgentVoiceGender = 'female' | 'male';

export interface AgentVoiceProfileResolution {
  gender: AgentVoiceGender | null;
  source: 'explicit' | 'name' | 'ambiguous';
  requires_explicit_choice: boolean;
}

export interface AgentVoice {
  voice_id: string;
  name: string;
  gender: AgentVoiceGender;
  locale: 'pt-BR';
  accent: string | null;
  description: string | null;
}

export interface AgentVoiceCatalogResponse {
  voices: AgentVoice[];
  cached: boolean;
}

function getAgentConfigClinicId(): number {
  const clinicId = Number(localStorage.getItem('clinic_id') || 0);
  if (!Number.isInteger(clinicId) || clinicId <= 0) {
    throw new Error('Não foi possível determinar a clínica para configurar a voz do agente.');
  }
  return clinicId;
}

export async function resolveAgentVoiceProfile(
  assistantName: string,
  voiceGender?: AgentVoiceGender,
): Promise<AgentVoiceProfileResolution> {
  const clinicId = getAgentConfigClinicId();
  const response = await api.post<AgentVoiceProfileResolution>(
    `/api/agent-config/${clinicId}/voice-profile/resolve`,
    {
      assistant_name: assistantName.trim(),
      ...(voiceGender ? { voice_gender: voiceGender } : {}),
    },
  );
  return response.data;
}

export async function getAgentVoiceCatalog(
  gender: AgentVoiceGender,
): Promise<AgentVoiceCatalogResponse> {
  const clinicId = getAgentConfigClinicId();
  const response = await api.get<AgentVoiceCatalogResponse>(
    `/api/agent-config/${clinicId}/voices`,
    { params: { gender, locale: 'pt-BR' } },
  );
  return response.data;
}

export async function getAgentVoicePreview(voiceId: string): Promise<Blob> {
  const clinicId = getAgentConfigClinicId();
  const response = await api.get<Blob>(
    `/api/agent-config/${clinicId}/voices/${encodeURIComponent(voiceId)}/preview`,
    { responseType: 'blob' },
  );
  return response.data;
}

export async function getAgentTreatmentTitles(clinicId?: number): Promise<string[]> {
  const resolvedClinicId = clinicId ?? Number(localStorage.getItem('clinic_id') || 0);
  if (!resolvedClinicId) {
    throw new Error('Não foi possível determinar a clínica para obter os tratamentos.');
  }

  const resp = await api.get(`/agent-config/${resolvedClinicId}`);
  const treatments = resp.data?.team_and_specialties?.treatments;
  if (!Array.isArray(treatments)) {
    return [];
  }

  const seen = new Set<string>();
  return treatments.reduce<string[]>((titles, treatment) => {
    const title = String(treatment?.treatmentTitle || '').trim();
    if (!title || seen.has(title)) {
      return titles;
    }
    seen.add(title);
    return [...titles, title];
  }, []);
}

// Função para deletar configurações do agente
export async function deleteAgentConfig(): Promise<string> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) {
    console.error("clinic_id não encontrado no localStorage ao tentar deletar config do agente.");
    throw new Error('Não foi possível determinar a clínica para deletar as configurações.');
  }

  try {
    const resp = await api.delete(`/agent-config/${clinicId}`);
    console.log("Resposta deleteAgentConfig:");
    return resp.data.message || 'Configurações removidas com sucesso!';
  } catch (error) {
    console.error("Erro ao deletar AgentConfig:");
    handleApiError(error, 'Erro ao deletar configurações do agente');
    throw new Error('Unreachable');
  }
}

// Função para atualizar configurações do agente
export async function updateAgentConfig(payload: any): Promise<string> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) {
    console.error("clinic_id não encontrado no localStorage. Não é possível atualizar config.");
    throw new Error('Não foi possível determinar a clínica para atualizar as configurações.');
  }

  payload.clinic_id = Number(clinicId);

  console.log("Enviando payload de updateAgentConfig:");

  try {
    const resp = await api.put(`/agent-config/${clinicId}`, payload);
    console.log("Resposta updateAgentConfig:");
    return resp.data.message || 'Configurações atualizadas com sucesso!';
  } catch (error) {
    console.error("Erro ao atualizar AgentConfig:");
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || 'Erro ao atualizar configurações do agente.';
      throw new Error(message);
    }
    throw error;
  }
}

/** 
 * GOOGLE CALENDAR ENDPOINTS
 */
export async function getGoogleCalendarIntegration(): Promise<{ google_calendar_id: string }> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) throw new Error("clinic_id não encontrado.");

  try {
    const resp = await api.get(`/api/integrations/calendar/google/${clinicId}`);
    return resp.data;  // { google_calendar_id: "..." }
  } catch (error) {
    console.error("Erro ao obter google calendar integration:");
    handleApiError(error, 'Erro ao obter integração Google Calendar');
    throw new Error('Unreachable');
  }
}

export async function updateGoogleCalendarIntegration(google_calendar_id: string): Promise<string> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) throw new Error("clinic_id não encontrado.");

  const payload = { google_calendar_id };  // "Ex: { google_calendar_id: '...' }"
  try {
    const resp = await api.put(`/api/integrations/calendar/google/${clinicId}`, payload);
    return resp.data.message || 'Integração Google Calendar atualizada com sucesso!';
  } catch (error) {
    console.error("Erro ao atualizar google calendar integration:");
    handleApiError(error, 'Erro ao atualizar Google Calendar');
    throw new Error('Unreachable');
  }
}

export async function deleteGoogleCalendarIntegration(): Promise<string> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) throw new Error("clinic_id não encontrado.");

  try {
    const resp = await api.delete(`/api/integrations/calendar/google/${clinicId}`);
    return resp.data.message || 'Integração Google Calendar removida com sucesso!';
  } catch (error) {
    console.error("Erro ao deletar google calendar integration:");
    handleApiError(error, 'Erro ao deletar Google Calendar');
    throw new Error('Unreachable');
  }
}


/**
 * CLINICORP ENDPOINTS
 */
export async function getClinicorpIntegration(): Promise<{
  username: string | null;
  password: string | null;
  code_link: string | null;
  subscriber_id: string | null;
}> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) throw new Error("clinic_id não encontrado.");

  try {
    const resp = await api.get(`/api/integrations/calendar/clinicorp/${clinicId}`);
    return resp.data; // {username, password, code_link, subscriber_id}
  } catch (error) {
    console.error("Erro ao obter clinicorp integration:");
    handleApiError(error, 'Erro ao obter integração Clinicorp');
    throw new Error('Unreachable');
  }
}

export async function updateClinicorpIntegration(payload: {
  username?: string;
  password?: string;
  code_link?: string;
  subscriber_id?: string;
}): Promise<string> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) throw new Error("clinic_id não encontrado.");

  try {
    const resp = await api.put(`/api/integrations/calendar/clinicorp/${clinicId}`, payload);
    return resp.data.message || 'Integração Clinicorp atualizada com sucesso!';
  } catch (error) {
    console.error("Erro ao atualizar clinicorp integration:");
    handleApiError(error, 'Erro ao atualizar integração Clinicorp');
    throw new Error('Unreachable');
  }
}

export async function deleteClinicorpIntegration(): Promise<string> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) throw new Error("clinic_id não encontrado.");

  try {
    const resp = await api.delete(`/api/integrations/calendar/clinicorp/${clinicId}`);
    return resp.data.message || 'Integração Clinicorp removida com sucesso!';
  } catch (error) {
    console.error("Erro ao deletar clinicorp integration:");
    handleApiError(error, 'Erro ao deletar integração Clinicorp');
    throw new Error('Unreachable');
  }
}

/** ========================================================================
 *  INTEGRAÇÕES DE GRUPO DE SUPORTE
 *  Rota no backend: /api/integrations/support-group/{clinic_id}
 * ========================================================================*/

export async function getSupportGroupIntegration(): Promise<any> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) {
    throw new Error('clinic_id não encontrado.');
  }
  try {
    const resp = await api.get(`/api/integrations/support-group/${clinicId}`);
    return resp.data;
  } catch (error) {
    console.error("Erro ao obter support group integration:");
    handleApiError(error, 'Erro ao obter integração do grupo de suporte');
    throw new Error('Unreachable');
  }
}

export async function updateSupportGroupIntegration(payload: any): Promise<string> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) {
    throw new Error('clinic_id não encontrado.');
  }

  payload.clinic_id = Number(clinicId);

  try {
    const resp = await api.put(`/api/integrations/support-group/${clinicId}`, payload);
    return resp.data.message || 'Integração do grupo de suporte atualizada com sucesso!';
  } catch (error) {
    console.error("Erro ao atualizar suporte group integration:");
    handleApiError(error, 'Erro ao atualizar integração do grupo de suporte');
    throw new Error('Unreachable');
  }
}

// Função para deletar integrações de grupo de suporte
export async function deleteSupportGroupIntegration(): Promise<string> {
  console.log("Iniciando deleteSupportGroupIntegration");
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) {
    throw new Error("clinic_id não encontrado.");
  }

  try {

    const resp = await api.delete(`/api/integrations/support-group/${clinicId}`);
    console.log("Resposta deleteSupportGroupIntegration:");
    return resp.data.message || 'Integração do grupo de suporte deletada com sucesso!';
  } catch (error) {
    console.error("Erro ao deletar suporte group integration:");

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || 'Erro ao deletar integração do grupo de suporte.';
      console.error("Detalhes do erro Axios:");
      throw new Error(message);
    }
    throw error;
  }
}

/** ========================================================================
 *  WEBHOOK DE LEADS DE FORMULÁRIO
 *  Rota no backend: /api/integrations/form-leads/webhook
 * ========================================================================*/

export interface FormLeadFieldMapping {
  name: string;
  phone: string;
  source_id?: string;
  source_id_static?: string;
  interest?: string;
  message?: string;
  email?: string;
  external_id?: string;
  thumbnail_url?: string;
  tags?: string;
  extra_fields?: Record<string, string>;
}

export interface FormLeadMessageConfig {
  send_initial_message: boolean;
  campaign_context?: string;
  tag_ids?: number[];
  official_template_id?: number | null;
  official_template_parameters?: Record<string, FormLeadTemplateBinding>;
}

export interface FormLeadTemplateBinding {
  source: 'contact_first_name' | 'contact_full_name' | 'clinic_name' | 'interest' | 'form_message' | 'literal';
  value?: string;
}

export interface FormLeadWebhookConfig {
  id: number;
  clinic_id: number;
  name: string;
  status: string;
  token_preview?: string | null;
  field_mapping: FormLeadFieldMapping;
  message_config: FormLeadMessageConfig;
  created_at?: string;
  updated_at?: string;
  last_used_at?: string | null;
  webhook_url?: string | null;
  last_captured_payload?: Record<string, unknown> | null;
  last_captured_at?: string | null;
  last_capture_event_id?: number | null;
  last_capture_status?: string | null;
  last_capture_error?: string | null;
  last_event_at?: string | null;
  leads_last_30_days?: number;
}

export interface FormLeadWebhookResponse {
  configured: boolean;
  webhook: FormLeadWebhookConfig | null;
}

export interface FormLeadWebhookPayload {
  name: string;
  field_mapping: FormLeadFieldMapping;
  message_config: FormLeadMessageConfig;
}

export interface FormLeadWebhookListResponse {
  webhooks: FormLeadWebhookConfig[];
  total: number;
  summary: {
    total: number;
    active: number;
    setup: number;
    leads_last_30_days: number;
  };
}

export interface FormLeadWebhookListParams {
  status?: 'active' | 'setup' | 'revoked';
  search?: string;
  limit?: number;
  offset?: number;
}

export interface FormLeadWebhookTestResult {
  success: boolean;
  extracted: {
    name: string;
    phone: string;
    source_id?: string | null;
    interest?: string | null;
    message?: string | null;
    email?: string | null;
    external_id?: string | null;
    thumbnail_url?: string | null;
    tags: string[];
    extras: Record<string, unknown>;
  };
}

function resolveFormLeadWebhookClinicId(clinicId?: number): number {
  const resolved = clinicId ?? Number(localStorage.getItem('clinic_id') || 0);
  if (!Number.isFinite(resolved) || resolved <= 0) {
    throw new Error('Não foi possível determinar a clínica para configurar o webhook.');
  }
  return resolved;
}

function formLeadWebhookClinicParams(clinicId?: number) {
  return { params: { clinic_id: resolveFormLeadWebhookClinicId(clinicId) } };
}

export async function getFormLeadWebhook(clinicId?: number): Promise<FormLeadWebhookResponse> {
  try {
    const resp = await api.get('/api/integrations/form-leads/webhook', formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error("Erro ao obter webhook de formulário:");
    handleApiError(error, 'Erro ao obter webhook de formulário');
    throw new Error('Unreachable');
  }
}

export async function listFormLeadWebhooks(
  clinicId?: number,
  filters: FormLeadWebhookListParams = {},
): Promise<FormLeadWebhookListResponse> {
  try {
    const resp = await api.get('/api/integrations/form-leads/webhooks', {
      ...formLeadWebhookClinicParams(clinicId),
      params: { ...formLeadWebhookClinicParams(clinicId).params, ...filters },
    });
    return resp.data;
  } catch (error) {
    console.error('Erro ao listar webhooks de formulários:');
    handleApiError(error, 'Erro ao listar webhooks de formulários');
    throw new Error('Unreachable');
  }
}

export async function createNewFormLeadWebhook(
  name: string,
  messageConfig: FormLeadMessageConfig,
  clinicId?: number,
): Promise<FormLeadWebhookResponse> {
  try {
    const resp = await api.post('/api/integrations/form-leads/webhooks', {
      name,
      message_config: messageConfig,
    }, formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error('Erro ao criar webhook de formulário:');
    handleApiError(error, 'Erro ao criar webhook de formulário');
    throw new Error('Unreachable');
  }
}

export async function getFormLeadWebhookById(
  webhookId: number,
  clinicId?: number,
): Promise<FormLeadWebhookResponse> {
  try {
    const resp = await api.get(`/api/integrations/form-leads/webhooks/${webhookId}`, formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error('Erro ao obter webhook de formulário:');
    handleApiError(error, 'Erro ao obter webhook de formulário');
    throw new Error('Unreachable');
  }
}

export async function updateFormLeadWebhookById(
  webhookId: number,
  payload: FormLeadWebhookPayload,
  clinicId?: number,
): Promise<FormLeadWebhookResponse> {
  try {
    const resp = await api.put(`/api/integrations/form-leads/webhooks/${webhookId}`, payload, formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error('Erro ao salvar webhook de formulário:');
    handleApiError(error, 'Erro ao salvar webhook de formulário');
    throw new Error('Unreachable');
  }
}

export async function rotateFormLeadWebhookById(
  webhookId: number,
  clinicId?: number,
): Promise<FormLeadWebhookResponse> {
  try {
    const resp = await api.post(`/api/integrations/form-leads/webhooks/${webhookId}/rotate`, null, formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error('Erro ao regenerar webhook de formulário:');
    handleApiError(error, 'Erro ao regenerar webhook de formulário');
    throw new Error('Unreachable');
  }
}

export async function revokeFormLeadWebhookById(
  webhookId: number,
  clinicId?: number,
): Promise<{ success: boolean; message: string }> {
  try {
    const resp = await api.delete(`/api/integrations/form-leads/webhooks/${webhookId}`, formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error('Erro ao revogar webhook de formulário:');
    handleApiError(error, 'Erro ao revogar webhook de formulário');
    throw new Error('Unreachable');
  }
}

export async function createFormLeadWebhookSetupLink(name: string = 'Formulários', clinicId?: number): Promise<FormLeadWebhookResponse> {
  try {
    const resp = await api.post('/api/integrations/form-leads/webhook/setup-link', { name }, formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error("Erro ao criar link de webhook de formulário:");
    handleApiError(error, 'Erro ao criar link de webhook');
    throw new Error('Unreachable');
  }
}

export async function saveFormLeadWebhook(payload: FormLeadWebhookPayload, clinicId?: number): Promise<FormLeadWebhookResponse> {
  try {
    const resp = await api.post('/api/integrations/form-leads/webhook', payload, formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error("Erro ao salvar webhook de formulário:");
    handleApiError(error, 'Erro ao salvar webhook de formulário');
    throw new Error('Unreachable');
  }
}

export async function rotateFormLeadWebhook(clinicId?: number): Promise<FormLeadWebhookResponse> {
  try {
    const resp = await api.post('/api/integrations/form-leads/webhook/rotate', null, formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error("Erro ao regenerar webhook de formulário:");
    handleApiError(error, 'Erro ao regenerar webhook de formulário');
    throw new Error('Unreachable');
  }
}

export async function revokeFormLeadWebhook(clinicId?: number): Promise<{ success: boolean; message: string }> {
  try {
    const resp = await api.delete('/api/integrations/form-leads/webhook', formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error("Erro ao revogar webhook de formulário:");
    handleApiError(error, 'Erro ao revogar webhook de formulário');
    throw new Error('Unreachable');
  }
}

export async function testFormLeadWebhookMapping(
  payload: Record<string, unknown>,
  fieldMapping: FormLeadFieldMapping,
  clinicId?: number,
): Promise<FormLeadWebhookTestResult> {
  try {
    const resp = await api.post('/api/integrations/form-leads/webhook/test-mapping', {
      payload,
      field_mapping: fieldMapping,
    }, formLeadWebhookClinicParams(clinicId));
    return resp.data;
  } catch (error) {
    console.error("Erro ao testar mapeamento de webhook de formulário:");
    handleApiError(error, 'Erro ao testar mapeamento');
    throw new Error('Unreachable');
  }
}

// ========================
// MIRROR WEBHOOK ENDPOINTS
// ========================

/**
 * Obtém a URL atual do mirror_webhook.
 * GET /api/clinic/mirror-webhook
 */
export async function getMirrorWebhookUrl(): Promise<MirrorWebhookResponse> {
  try {
    const resp = await api.get('/api/clinic/mirror-webhook');
    // resp.data deve ser { mirror_webhook_url: "..."} ou { mirror_webhook_url: null }
    return resp.data;
  } catch (error) {
    console.error("Erro ao obter Mirror Webhook URL:");
    handleApiError(error, 'Erro ao obter o Mirror Webhook URL');
    throw error;
  }
}

/**
 * Cria (POST) uma nova mirror_webhook_url via FormData.
 * POST /api/clinic/mirror-webhook
 */
export async function createMirrorWebhookUrl(newUrl: string): Promise<string> {
  // Montamos formData com o campo mirror_webhook_url
  const formData = new FormData();
  formData.append('mirror_webhook_url', newUrl);

  try {
    // Enviamos com multipart/form-data
    const resp = await api.post('/api/clinic/mirror-webhook', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return resp.data.message || 'Mirror Webhook criado com sucesso!';
  } catch (error) {
    console.error("Erro ao criar Mirror Webhook URL:");
    handleApiError(error, 'Erro ao criar Mirror Webhook URL');
    throw new Error('Unreachable');
  }
}

/**
 * Atualiza (PUT) a mirror_webhook_url existente via FormData.
 * PUT /api/clinic/mirror-webhook
 */
export async function updateMirrorWebhookUrl(newUrl: string): Promise<string> {
  // Montamos formData com o campo mirror_webhook_url
  const formData = new FormData();
  formData.append('mirror_webhook_url', newUrl);

  try {
    // Enviamos com multipart/form-data
    const resp = await api.put('/api/clinic/mirror-webhook', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return resp.data.message || 'Mirror Webhook atualizado com sucesso!';
  } catch (error) {
    console.error("Erro ao atualizar Mirror Webhook URL:");
    handleApiError(error, 'Erro ao atualizar Mirror Webhook URL');
    throw new Error('Unreachable');
  }
}

/**
 * Deleta (DELETE) a mirror_webhook_url.
 * DELETE /api/clinic/mirror-webhook
 */
export async function deleteMirrorWebhookUrl(): Promise<string> {
  try {
    const resp = await api.delete('/api/clinic/mirror-webhook');
    return resp.data.message || 'Mirror Webhook deletado com sucesso!';
  } catch (error) {
    console.error("Erro ao deletar Mirror Webhook URL:");
    handleApiError(error, 'Erro ao deletar Mirror Webhook URL');
    throw new Error('Unreachable');
  }
}

/**
 * Cria uma nova clínica e a associa ao usuário cujo e-mail é passado.
 * Endpoint: POST /api/clinics-admin
 * Nenhum token é usado.
 */
export async function createNewClinicAdmin(
  clientEmail: string,
  clinicName: string,
  clinicCnpj: string
): Promise<{ clinic_id: number; message: string }> {
  const formData = new FormData();
  formData.append('client_email', clientEmail);
  formData.append('clinic_name', clinicName);
  formData.append('clinic_cnpj', clinicCnpj);

  try {
    // Não setamos "Content-Type" pois o axios detecta como multipart/form-data
    const resp = await api.post('/api/clinics-admin', formData);
    console.log("Resposta createNewClinicAdmin:");
    return resp.data;
  } catch (error) {
    console.error("Erro ao criar nova clínica (admin):");
    // Caso use o handleApiError, pode chamar assim:
    // handleApiError(error, 'Erro ao criar nova clínica (admin)');
    throw error;
  }
}


/**
 * Lista todas as clínicas vinculadas ao usuário logado (GET /api/client-clinics).
 */
export async function getUserClinics(): Promise<any[]> {
  try {
    const resp = await api.get('/api/client-clinics');
    return resp.data; // array de clínicas
  } catch (error) {
    console.error("Erro ao obter lista de clínicas do usuário:");
    throw error;
  }
}

/**
 * Define qual clínica (clinic_id) o usuário está usando no momento.
 * PUT /api/client-clinics/{clinic_id}/select
 */
export async function selectActiveClinic(clinicId: number) {
  const resp = await api.put(`/api/client-clinics/${clinicId}/select`);
  return resp.data; // { message, clinic_logo_url, name_clinic }
}

/**
 * Remove vínculo com uma clínica específica do usuário logado.
 * DELETE /api/client-clinics/{clinic_id}
 */
export async function removeUserClinic(clinicId: number): Promise<string> {
  console.log("[API] removeUserClinic -> Removendo vínculo com clínica:");

  try {
    const resp = await api.delete(`/client-clinics/${clinicId}`);
    console.log("[API] removeUserClinic -> Resposta do servidor:");

    return resp.data.message || 'Vínculo com a clínica removido com sucesso.';
  } catch (error) {
    console.error("[API] removeUserClinic -> Erro ao remover vínculo:");
    handleApiError(error, 'Erro ao remover vínculo com a clínica');
    throw new Error('Unreachable');
  }
}

/**
 * Faz upload de um arquivo (imagem, áudio, vídeo) para o backend,
 * usando `client_id` e `clinic_id` do localStorage.
 */
// api.ts
export async function uploadFile(file: File): Promise<{ id: string; path: string }> {
  try {
    const clientId = Number(localStorage.getItem('client_id'));
    const clinicId = Number(localStorage.getItem('clinic_id'));

    if (!clientId || !clinicId) {
      throw new Error('Faltando client_id ou clinic_id no localStorage.');
    }

    const formData = new FormData();
    formData.append('file', file);

    // Log para debug
    console.log("Enviando arquivo:");

    const resp = await api.post<{ id: string; path: string }>(
      `/api/arquivos/upload/${clientId}/${clinicId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // Aumenta para 30 segundos
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      }
    );

    return resp.data;
  } catch (error: any) {
    console.error("[uploadFile] Erro ao fazer upload:");
    if (error.code === 'ECONNABORTED') {
      throw new Error('O upload está demorando muito. Tente um arquivo menor ou verifique sua conexão.');
    }
    throw error;
  }
}

/**
 * Lista os arquivos do client_id e clinic_id, obtidos do localStorage.
 */
export async function listFiles(): Promise<MediaFile[]> {
  try {
    const clientId = Number(localStorage.getItem('client_id'));
    const clinicId = Number(localStorage.getItem('clinic_id'));

    if (!clientId || !clinicId) {
      throw new Error('Faltando client_id ou clinic_id no localStorage.');
    }

    const resp = await api.get<MediaFile[]>(
      `/api/arquivos/files/${clientId}/${clinicId}`
    );
    return resp.data;
  } catch (error) {
    console.error("[listFiles] Erro ao listar arquivos:");
    handleApiError(error, 'Erro ao listar arquivos');
    throw new Error('Unreachable');
  }
}

/**
 * Deleta um arquivo específico pelo seu ID.
 * O endpoint /api/arquivos/files/{file_id} não depende de client_id/clinic_id,
 * pois só precisa do file_id na rota.
 */
export async function deleteFile(fileId: string): Promise<string> {
  try {
    const resp = await api.delete<{ message: string }>(
      `/api/arquivos/files/${fileId}`
    );
    return resp.data.message || 'Arquivo deletado com sucesso!';
  } catch (error) {
    console.error("[deleteFile] Erro ao deletar arquivo:");
    handleApiError(error, 'Erro ao deletar arquivo');
    throw new Error('Unreachable');
  }
}

/**
 * Retorna a URL final para GET /api/arquivos/files/view/{clinic_id}/{client_id}/{file_name},
 * usando o file_name completo (ex.: "c24465ac-...7007.jpg").
 */
export function getFileUrl(
  clinicId: number,
  clientId: number,
  fileName: string
): string {
  return `/api/arquivos/files/view/${clinicId}/${clientId}/${fileName}`;
}

/**
 * Faz um GET para buscar o arquivo como Blob, usando o fileName completo.
 */
export async function fetchFileBlob(
  clinicId: number,
  clientId: number,
  fileName: string
): Promise<Blob> {
  try {
    const response = await api.get(
      `/api/arquivos/files/view/${clinicId}/${clientId}/${fileName}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    console.error("[fetchFileBlob] Erro ao buscar arquivo:");
    handleApiError(error, 'Erro ao buscar arquivo');
    throw error;
  }
}

/**
 * Envia texto via WhatsApp (Z-API)
 * POST /webhook/send-text
 */
export async function sendWhatsAppText({ phone, message, localMessageId, replyToMessageId, contact_id }: SendTextParams): Promise<any> {
  try {
    const rawOwnClientId = localStorage.getItem("client_id");
    const clinicId = localStorage.getItem("clinic_id");

    // 2) Se for 'user', usar master_client_id. Se for 'master', usar client_id
    // Para usuários staff (user), sempre usar o próprio client_id
    // O backend já trata as permissões corretamente
    let finalClientId = rawOwnClientId;

    // 3) Montar URL com query param
    const url = `/webhook/send-text?client_id=${finalClientId}&clinic_id=${clinicId}`;

    // 4) Fazer POST com body (incluindo localMessageId se fornecido)
    const body: any = { phone, message };
    if (localMessageId) {
      body.localMessageId = localMessageId;
    }
    if (replyToMessageId) {
      body.replyToMessageId = replyToMessageId;
    }
    if (contact_id) {
      body.contact_id = contact_id;
    }

    const resp = await api.post(url, body, {
      headers: { 'Content-Type': 'application/json' },
    });
    return resp.data; // { message: "...", zapi_response: ... }
  } catch (error) {
    console.error("[sendWhatsAppText] Erro ao enviar texto:");
    handleApiError(error, 'Erro ao enviar mensagem de texto via WhatsApp');
    throw new Error('Unreachable');
  }
}

export type WhatsAppPresenceSignal = 'typing' | 'recording' | 'paused';
export type WhatsAppPresenceState =
  | WhatsAppPresenceSignal
  | 'online'
  | 'offline';

export interface WhatsAppPresenceSubscriptionResponse {
  supported: boolean;
  subscribed: boolean;
  presence: WhatsAppPresenceState | null;
  last_seen: string | null;
  observer_id?: string;
  lease_seconds?: number;
  reason?: string;
}

const getWhatsAppPresenceScope = (): {
  client_id: number;
  clinic_id: number;
} => {
  const clientId = Number(localStorage.getItem('client_id'));
  const clinicId = Number(localStorage.getItem('clinic_id'));

  if (!Number.isInteger(clientId) || clientId <= 0) {
    throw new Error('client_id ausente ou invalido para presenca do WhatsApp.');
  }
  if (!Number.isInteger(clinicId) || clinicId <= 0) {
    throw new Error('clinic_id ausente ou invalido para presenca do WhatsApp.');
  }

  return { client_id: clientId, clinic_id: clinicId };
};

export async function subscribeWhatsAppPresence(
  contactId: number,
  observerId: string,
): Promise<WhatsAppPresenceSubscriptionResponse> {
  const scope = getWhatsAppPresenceScope();
  const response = await api.post(
    '/webhook/presence/subscribe',
    { contact_id: contactId, observer_id: observerId },
    { params: scope, timeout: 5_000, _skipPlatformInterruption: true },
  );
  return response.data as WhatsAppPresenceSubscriptionResponse;
}

export async function releaseWhatsAppPresence(
  contactId: number,
  observerId: string,
): Promise<{
  enabled?: boolean;
  released?: boolean;
  idle?: boolean;
  reason?: string;
}> {
  const scope = getWhatsAppPresenceScope();
  const response = await api.post(
    '/webhook/presence/release',
    { contact_id: contactId, observer_id: observerId },
    { params: scope, timeout: 2_000, _skipPlatformInterruption: true },
  );
  return response.data;
}

export async function sendWhatsAppPresence(
  contactId: number,
  presence: WhatsAppPresenceSignal,
): Promise<{
  enabled?: boolean;
  sent?: boolean;
  coalesced?: boolean;
  reason?: string;
}> {
  const scope = getWhatsAppPresenceScope();
  const response = await api.post(
    '/webhook/presence',
    { contact_id: contactId, presence },
    { params: scope, timeout: 2_000, _skipPlatformInterruption: true },
  );
  return response.data;
}

export async function reactToWhatsAppMessage(params: {
  phone: string;
  messageId: string;
  reaction: string;
  contact_id?: number;
}): Promise<any> {
  try {
    const rawOwnClientId = localStorage.getItem("client_id");
    const clinicId = localStorage.getItem("clinic_id");
    const url = `/webhook/message-reaction?client_id=${rawOwnClientId}&clinic_id=${clinicId}`;

    const resp = await api.post(url, params, {
      headers: { 'Content-Type': 'application/json' },
    });
    return resp.data;
  } catch (error) {
    console.error("[reactToWhatsAppMessage] Erro ao reagir mensagem:");
    handleApiError(error, 'Erro ao reagir à mensagem');
    throw new Error('Unreachable');
  }
}

/**
 * Envia imagem via WhatsApp (Z-API)
 * POST /webhook/send-image
 */
export async function sendWhatsAppImage(params: SendImageParams): Promise<any> {
  try {
    const rawOwnClientId = localStorage.getItem("client_id");
    const clinicId = localStorage.getItem("clinic_id");

    // Para usuários staff (user), sempre usar o próprio client_id
    // O backend já trata as permissões corretamente
    let finalClientId = rawOwnClientId;

    // 2) Query string
    const url = `/webhook/send-image?client_id=${finalClientId}&clinic_id=${clinicId}`;

    // 3) POST no endpoint, enviando 'params' no body
    const resp = await api.post(url, params, {
      headers: { 'Content-Type': 'application/json' },
    });
    return resp.data;
  } catch (error) {
    console.error("[sendWhatsAppImage] Erro ao enviar imagem:");
    handleApiError(error, 'Erro ao enviar imagem via WhatsApp');
    throw new Error('Unreachable');
  }
}

/**
 * Envia áudio via WhatsApp (Z-API)
 * POST /webhook/send-audio
 */
export async function sendWhatsAppAudio(params: SendAudioParams): Promise<any> {
  try {
    const rawOwnClientId = localStorage.getItem("client_id");
    const clinicId = localStorage.getItem("clinic_id");

    // Para usuários staff (user), sempre usar o próprio client_id
    // O backend já trata as permissões corretamente
    let finalClientId = rawOwnClientId;

    const url = `/webhook/send-audio?client_id=${finalClientId}&clinic_id=${clinicId}`;

    // Enviar o áudio sem muitas manipulações
    const resp = await api.post(url, params, {
      headers: { 'Content-Type': 'application/json' },
    });
    return resp.data;
  } catch (error) {
    console.error("[sendWhatsAppAudio] Erro ao enviar áudio:");
    throw error;
  }
}


/**
 * Envia vídeo via WhatsApp (Z-API)
 * POST /webhook/send-video
 */
export async function sendWhatsAppVideo(params: SendVideoParams): Promise<any> {
  try {
    const rawOwnClientId = localStorage.getItem("client_id");
    const clinicId = localStorage.getItem("clinic_id");

    // Para usuários staff (user), sempre usar o próprio client_id
    // O backend já trata as permissões corretamente
    let finalClientId = rawOwnClientId;

    const url = `/webhook/send-video?client_id=${finalClientId}&clinic_id=${clinicId}`;

    const resp = await api.post(url, params, {
      headers: { 'Content-Type': 'application/json' },
    });
    return resp.data;
  } catch (error) {
    console.error("[sendWhatsAppVideo] Erro ao enviar vídeo:");
    handleApiError(error, 'Erro ao enviar vídeo via WhatsApp');
    throw new Error('Unreachable');
  }
}

// ==========================================
// API OFICIAL DO WHATSAPP - ENVIO
// ==========================================

export interface SendOfficialTextParams {
  phone: string;
  message: string;
  contact_id?: number;
}

export interface SendOfficialImageParams {
  phone: string;
  image_url: string;
  caption?: string;
  contact_id?: number;
}

export interface SendOfficialAudioParams {
  phone: string;
  audio_url: string;
  contact_id?: number;
}

export interface SendOfficialVideoParams {
  phone: string;
  video_url: string;
  caption?: string;
  contact_id?: number;
}

/**
 * Envia texto via API Oficial do WhatsApp
 * POST /api/official/send-text-official
 */
export async function sendOfficialWhatsAppText(
  params: SendOfficialTextParams
): Promise<any> {
  try {
    const clientId = localStorage.getItem("client_id");
    const clinicId = localStorage.getItem("clinic_id");
    const url = `/api/official/send-text-official?client_id=${clientId}&clinic_id=${clinicId}`;

    console.log("[Official] Enviando texto via API Oficial");
    const resp = await api.post(url, params, {
      headers: { 'Content-Type': 'application/json' },
    });
    return resp.data;
  } catch (error) {
    console.error("[sendOfficialWhatsAppText] Erro:");
    handleApiError(error, 'Erro ao enviar texto via WhatsApp Oficial');
    throw error;
  }
}

/**
 * Envia imagem via API Oficial do WhatsApp
 * POST /api/official/send-image-official
 */
export async function sendOfficialWhatsAppImage(
  params: SendOfficialImageParams
): Promise<any> {
  try {
    const clientId = localStorage.getItem("client_id");
    const clinicId = localStorage.getItem("clinic_id");
    const url = `/api/official/send-image-official?client_id=${clientId}&clinic_id=${clinicId}`;

    console.log("[Official] Enviando imagem via API Oficial");
    const resp = await api.post(url, params, {
      headers: { 'Content-Type': 'application/json' },
    });
    return resp.data;
  } catch (error) {
    console.error("[sendOfficialWhatsAppImage] Erro:");
    handleApiError(error, 'Erro ao enviar imagem via WhatsApp Oficial');
    throw error;
  }
}

/**
 * Envia áudio via API Oficial do WhatsApp
 * POST /api/official/send-audio-official
 */
export async function sendOfficialWhatsAppAudio(
  params: SendOfficialAudioParams
): Promise<any> {
  try {
    const clientId = localStorage.getItem("client_id");
    const clinicId = localStorage.getItem("clinic_id");
    const url = `/api/official/send-audio-official?client_id=${clientId}&clinic_id=${clinicId}`;

    console.log("[Official] Enviando áudio via API Oficial");
    const resp = await api.post(url, params, {
      headers: { 'Content-Type': 'application/json' },
    });
    return resp.data;
  } catch (error) {
    console.error("[sendOfficialWhatsAppAudio] Erro:");
    handleApiError(error, 'Erro ao enviar áudio via WhatsApp Oficial');
    throw error;
  }
}

/**
 * Envia vídeo via API Oficial do WhatsApp
 * POST /api/official/send-video-official
 */
export async function sendOfficialWhatsAppVideo(
  params: SendOfficialVideoParams
): Promise<any> {
  try {
    const clientId = localStorage.getItem("client_id");
    const clinicId = localStorage.getItem("clinic_id");
    const url = `/api/official/send-video-official?client_id=${clientId}&clinic_id=${clinicId}`;

    console.log("[Official] Enviando vídeo via API Oficial");
    const resp = await api.post(url, params, {
      headers: { 'Content-Type': 'application/json' },
    });
    return resp.data;
  } catch (error) {
    console.error("[sendOfficialWhatsAppVideo] Erro:");
    handleApiError(error, 'Erro ao enviar vídeo via WhatsApp Oficial');
    throw error;
  }
}

/**
 * Faz upload de arquivo para o servidor e retorna URL pública
 * Necessário porque API Oficial não aceita base64
 * POST /api/official/upload-media
 */
export async function uploadMediaForOfficial(
  file: Blob,
  filename: string
): Promise<string> {
  try {
    const formData = new FormData();
    formData.append('file', file, filename);

    const clinicId = localStorage.getItem("clinic_id");

    console.log("[Official] Fazendo upload de mídia:");
    const response = await api.post(`/api/official/upload-media?clinic_id=${clinicId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    console.log("[Official] ✅ Upload concluído:");
    return response.data.url; // URL pública do arquivo
  } catch (error) {
    console.error("[uploadMediaForOfficial] Erro:");
    handleApiError(error, 'Erro ao fazer upload de mídia');
    throw error;
  }
}

/**
 * Interface para providers de WhatsApp disponíveis
 */
export interface WhatsAppProviders {
  has_waha: boolean;
  has_official: boolean;
  waha_session?: string;
  official_phone_id?: string;
}

/**
 * Verifica quais providers de WhatsApp a clínica tem configurados
 * GET /api/clinics/{clinic_id}/whatsapp-providers
 */
export async function getClinicWhatsAppProviders(
  clinicId: number
): Promise<WhatsAppProviders> {
  try {
    console.log("[Providers] Verificando providers para clinic_id:");
    const response = await api.get(`/api/clinics/${clinicId}/whatsapp-providers`);
    console.log("[Providers] Providers disponíveis:");
    return response.data;
  } catch (error) {
    console.error("[getClinicWhatsAppProviders] Erro:");
    // Retornar configuração padrão (apenas WAHA) em caso de erro
    return {
      has_waha: true,
      has_official: false
    };
  }
}

// [GERENCIAR FOLLOW-UP]

/**
 * Cria uma nova sequência de follow-up para uma clínica específica.
 * Endpoint: POST /api/followups/{clinic_id}
 */
export async function createFollowUpSequence(
  clinicId: number,
  payload: FollowUpSequenceCreate
): Promise<FollowUpSequenceResponse> {
  try {
    const resp = await api.post<FollowUpSequenceResponse>(
      `/api/followups/${clinicId}`,
      payload
    );
    return resp.data; // { message: '...', sequence_id: 123 }
  } catch (error) {
    console.error("Erro ao criar FollowUpSequence:");
    throw new Error('Não foi possível criar a sequência de follow-ups.');
  }
}

/**
 * Obtém a sequência de follow-up (caso exista) para uma clínica específica.
 * Endpoint: GET /api/followups/{clinic_id}
 */
export async function getFollowUpSequence(
  clinicId: number
): Promise<GetFollowUpSequenceDetail | null> {
  try {
    const resp = await api.get<GetFollowUpSequenceDetail>(
      `/api/followups/${clinicId}`,
      {
        // Aqui configuramos o validateStatus para aceitar 404 como “status OK”
        validateStatus: (status) => {
          return (status >= 200 && status < 300) || status === 404;
        },
      }
    );

    // Se o status for 404, retornamos null (indicando "não encontrado")
    if (resp.status === 404) {
      return null;
    }

    // Se não for 404, então resp.data é a sequência (200, por ex.)
    return resp.data;
  } catch (error) {
    // Aqui só cai se for algo fora do 2xx e 404 — por ex. 500, 403, erro de rede etc.
    console.error("Erro ao obter FollowUpSequence:");
    throw new Error('Não foi possível obter a sequência de follow-ups.');
  }
}

/**
 * Atualiza completamente a sequência de follow-up para a clínica específica,
 * sobrescrevendo steps e messages conforme o payload.
 * Endpoint: PUT /api/followups/{clinic_id}
 */
export async function updateFollowUpSequence(
  clinicId: number,
  payload: FollowUpSequenceUpdate
): Promise<FollowUpSequenceResponse> {
  try {
    const resp = await api.put<FollowUpSequenceResponse>(
      `/api/followups/${clinicId}`,
      payload
    );
    return resp.data;
  } catch (error) {
    console.error("Erro ao atualizar FollowUpSequence:");
    throw new Error('Não foi possível atualizar a sequência de follow-ups.');
  }
}

/**
 * Deleta a sequência de follow-up (se existir) para a clínica específica.
 * Endpoint: DELETE /api/followups/{clinic_id}
 */
export async function deleteFollowUpSequence(
  clinicId: number
): Promise<{ message: string }> {
  try {
    const resp = await api.delete<{ message: string }>(
      `/api/followups/${clinicId}`
    );
    return resp.data; // { message: 'Follow-up sequence ... deleted successfully' }
  } catch (error) {
    console.error("Erro ao deletar FollowUpSequence:");
    throw new Error('Não foi possível deletar a sequência de follow-ups.');
  }
}

/**
 * Cria a configuração de follow-up schedule para a clínica {clinicId}.
 * Endpoint: POST /followup-schedule/{clinic_id}
 */
export async function createFollowUpScheduleConfig(
  clinicId: number,
  payload: FollowUpScheduleCreate
): Promise<{ message: string; id: number; clinic_id: number }> {
  try {
    // Exemplo de chamada:
    // POST /followup-schedule/123
    // body: { follow_up_sequence_id, schedule_data: { days_of_week, times } }

    const resp = await api.post<{ message: string; id: number; clinic_id: number }>(
      `/api/followup-schedule/${clinicId}`,
      payload
    );
    return resp.data;
  } catch (error) {
    console.error("[createFollowUpScheduleConfig] Erro:");
    // Se você tiver a handleApiError:
    // handleApiError(error, 'Erro ao criar configuração de horários');
    throw error;
  }
}

/**
 * Obtém a configuração de follow-up schedule da clínica {clinicId}.
 * Endpoint: GET /followup-schedule/{clinic_id}
 */
export async function getFollowUpScheduleConfig(
  clinicId: number
): Promise<FollowUpScheduleConfig> {
  try {
    const resp = await api.get<FollowUpScheduleConfig>(
      `/api/followup-schedule/${clinicId}`
    );
    return resp.data;
  } catch (error) {
    console.error("[getFollowUpScheduleConfig] Erro:");
    // handleApiError(error, 'Erro ao obter configuração de horários');
    throw error;
  }
}

/**
 * Atualiza a configuração de follow-up schedule para a clínica {clinicId}.
 * Endpoint: PUT /followup-schedule/{clinic_id}
 */
export async function updateFollowUpScheduleConfig(
  clinicId: number,
  payload: FollowUpScheduleUpdate
): Promise<{ message: string; id: number; clinic_id: number }> {
  try {
    // PUT /followup-schedule/123
    // body: { schedule_data: { days_of_week, times } }

    const resp = await api.put<{ message: string; id: number; clinic_id: number }>(
      `/api/followup-schedule/${clinicId}`,
      payload
    );
    return resp.data;
  } catch (error) {
    console.error("[updateFollowUpScheduleConfig] Erro:");
    // handleApiError(error, 'Erro ao atualizar configuração de horários');
    throw error;
  }
}

/**
 * Deleta a configuração de follow-up schedule para a clínica {clinicId}.
 * Endpoint: DELETE /followup-schedule/{clinic_id}
 */
export async function deleteFollowUpScheduleConfig(
  clinicId: number
): Promise<{ message: string }> {
  try {
    const resp = await api.delete<{ message: string }>(
      `/api/followup-schedule/${clinicId}`
    );
    return resp.data;
  } catch (error) {
    console.error("[deleteFollowUpScheduleConfig] Erro:");
    // handleApiError(error, 'Erro ao deletar configuração de horários');
    throw error;
  }
}

/**
 * Cria uma nova sequência de confirmação para uma clínica específica.
 * Endpoint: POST /api/confirmations/{clinic_id}
 */
export async function createConfirmationSequence(
  clinicId: number,
  payload: ConfirmationSequenceCreate
): Promise<ConfirmationSequenceResponse> {
  try {
    const resp = await api.post<ConfirmationSequenceResponse>(
      `/api/confirmations/${clinicId}`,
      payload
    );
    return resp.data; // { message: '...', sequence_id: 123 }
  } catch (error) {
    console.error("Erro ao criar ConfirmationSequence:");
    throw new Error('Não foi possível criar a sequência de confirmação.');
  }
}

/**
 * Obtém a sequência de confirmação (caso exista) para uma clínica específica.
 * Endpoint: GET /api/confirmations/{clinic_id}
 */
export async function getConfirmationSequence(
  clinicId: number
): Promise<ConfirmationSequenceDetail | null> {
  try {
    const resp = await api.get<ConfirmationSequenceDetail>(
      `/api/confirmations/${clinicId}`,
      {
        validateStatus: (status) => {
          // Aceita 200 (OK) e 404 (não encontrado)
          return (status >= 200 && status < 300) || status === 404;
        },
      }
    );

    if (resp.status === 404) {
      return null;
    }

    return resp.data;
  } catch (error) {
    console.error("Erro ao obter ConfirmationSequence:");
    throw new Error('Não foi possível obter a sequência de confirmação.');
  }
}

/**
 * Atualiza completamente a sequência de confirmação para a clínica específica,
 * sobrescrevendo steps e messages conforme o payload.
 * Endpoint: PUT /api/confirmations/{clinic_id}
 */
export async function updateConfirmationSequence(
  clinicId: number,
  payload: ConfirmationSequenceUpdate
): Promise<ConfirmationSequenceResponse> {
  try {
    const resp = await api.put<ConfirmationSequenceResponse>(
      `/api/confirmations/${clinicId}`,
      payload
    );
    return resp.data;
  } catch (error: any) {
    console.error("Erro ao atualizar ConfirmationSequence:");
    throw new Error(
      error?.response?.data?.detail || 'Não foi possível atualizar a sequência de confirmação.'
    );
  }
}

/**
 * Deleta a sequência de confirmação (se existir) para a clínica específica.
 * Endpoint: DELETE /api/confirmations/{clinic_id}
 */
export async function deleteConfirmationSequence(
  clinicId: number
): Promise<{ message: string }> {
  try {
    const resp = await api.delete<{ message: string }>(
      `/api/confirmations/${clinicId}`
    );
    return resp.data; // { message: 'Confirmation sequence ... deleted successfully' }
  } catch (error) {
    console.error("Erro ao deletar ConfirmationSequence:");
    throw new Error('Não foi possível deletar a sequência de confirmação.');
  }
}

/**
 * Cria a configuração de CONFIRMAÇÃO schedule para a clínica {clinicId}.
 * Endpoint: POST /api/confirmation-schedule/{clinic_id}
 */
export async function createConfirmationScheduleConfig(
  clinicId: number,
  payload: ConfirmationScheduleCreate
): Promise<{ message: string; id: number; clinic_id: number }> {
  try {
    const resp = await api.post<{ message: string; id: number; clinic_id: number }>(
      `/api/confirmation-schedule/${clinicId}`,
      payload
    );
    return resp.data;
  } catch (error) {
    console.error("[createConfirmationScheduleConfig] Erro:");
    throw error;
  }
}

/**
 * Obtém a configuração de CONFIRMAÇÃO schedule da clínica {clinicId}.
 * Endpoint: GET /api/confirmation-schedule/{clinic_id}
 */
export async function getConfirmationScheduleConfig(
  clinicId: number
): Promise<ConfirmationScheduleConfig | null> {
  console.log("[getConfirmationScheduleConfig] Iniciando chamada com clinicId:");

  try {
    const resp = await api.get<ConfirmationScheduleConfig>(
      `/api/confirmation-schedule/${clinicId}`,
      {
        validateStatus: (status) => {
          // Aceita 200 e 404
          return (status >= 200 && status < 300) || status === 404;
        },
      }
    );

    console.log("[getConfirmationScheduleConfig] Status da resposta:");
    console.log("[getConfirmationScheduleConfig] Dados recebidos:");

    if (resp.status === 404) {
      console.warn("[getConfirmationScheduleConfig] Recebemos 404, retornando null");
      return null;
    }

    console.log("[getConfirmationScheduleConfig] Retornando schedule config:");
    return resp.data;

  } catch (error) {
    console.error("[getConfirmationScheduleConfig] Erro ao obter config:");
    throw error;
  }
}


/**
 * Atualiza a configuração de CONFIRMAÇÃO schedule para a clínica {clinicId}.
 * Endpoint: PUT /api/confirmation-schedule/{clinic_id}
 */
export async function updateConfirmationScheduleConfig(
  clinicId: number,
  payload: ConfirmationScheduleUpdate
): Promise<{ message: string; id: number; clinic_id: number }> {
  try {
    const resp = await api.put<{ message: string; id: number; clinic_id: number }>(
      `/api/confirmation-schedule/${clinicId}`,
      payload
    );
    return resp.data;
  } catch (error) {
    console.error("[updateConfirmationScheduleConfig] Erro:");
    throw error;
  }
}

/**
 * Deleta a configuração de CONFIRMAÇÃO schedule para a clínica {clinicId}.
 * Endpoint: DELETE /api/confirmation-schedule/{clinic_id}
 */
export async function deleteConfirmationScheduleConfig(
  clinicId: number
): Promise<{ message: string }> {
  try {
    const resp = await api.delete<{ message: string }>(
      `/api/confirmation-schedule/${clinicId}`
    );
    return resp.data;
  } catch (error) {
    console.error("[deleteConfirmationScheduleConfig] Erro:");
    throw error;
  }
}

/**
 * 1) Obtém a lista de agendamentos completos
 */
export async function listarAgendamentos(): Promise<AgendamentoResponse[]> {
  const clientId = localStorage.getItem('client_id');
  const clinicId = localStorage.getItem('clinic_id');

  console.log("Escopo do funil:");

  if (!clientId || !clinicId) {
    throw new Error('Informações de autenticação não encontradas');
  }

  try {
    return await getCrmList<AgendamentoResponse[]>(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/agendamentos/`
    );
  } catch (error) {
    console.error("Erro ao listar agendamentos:");
    throw error;
  }
}

/**
 * 2) Atualiza um agendamento específico (por ID)
 */
export async function atualizarAgendamento(
  clientId: number,
  clinicId: number,
  agendamentoId: number,
  data: AgendamentoUpdate
): Promise<AgendamentoResponse> {
  const response: AxiosResponse<AgendamentoResponse> = await api.put(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/agendamentos/${agendamentoId}`,
    data
  );
  return response.data;
}

/**
 *  Deleta um agendamento específico (DELETE)
 *  DELETE /api/agenda/clients/{clientId}/clinics/{clinicId}/agendamentos/{agendamentoId}
 */
export async function deletarAgendamento(
  clientId: number,
  clinicId: number,
  agendamentoId: number
): Promise<void> {
  await api.delete(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/agendamentos/${agendamentoId}`
  );
}

export async function marcarCrmPerdido(clientId: number, clinicId: number, leadId: number,
  data: { expected_stage: CrmLossStage; reason: string; reason_id?: number; observation?: string }): Promise<CrmLossMutation> {
  return (await api.post(`/api/agenda/clients/${clientId}/clinics/${clinicId}/crm/leads/${leadId}/loss`, data)).data;
}

export async function obterCrmHistoricoPerdas(clientId: number, clinicId: number, leadId: number): Promise<CrmLossHistory> {
  const url = `/api/agenda/clients/${clientId}/clinics/${clinicId}/crm/leads/${leadId}/loss-history`;
  const result: CrmLossHistory = (await api.get(url, { params: { limit: 100, offset: 0 } })).data;
  while (result.items.length < result.total) {
    const page: CrmLossHistory = (await api.get(url, { params: { limit: 100, offset: result.items.length } })).data;
    if (!page.items.length) throw new Error('O histórico mudou durante a consulta. Atualize e tente novamente.');
    result.items.push(...page.items);
  }
  return result;
}

export async function reabrirCrmPerdido(clientId: number, clinicId: number, leadId: number,
  data: { loss_id: number; expected_stage: CrmStage; justification: string }): Promise<CrmLossMutation> {
  return (await api.post(`/api/agenda/clients/${clientId}/clinics/${clinicId}/crm/leads/${leadId}/reopen`, data)).data;
}

export async function listarTodasPerdasCrm(clientId: number, clinicId: number, params: CrmListParams = {}): Promise<CrmLoss[]> {
  const items: CrmLoss[] = [];
  const limit = 200;
  let total = Infinity;
  while (items.length < total) {
    const page = await listarCrmPagina(clientId, clinicId, 'lost', items.length, limit, params);
    total = page.total;
    if (!page.items.length) {
      if (items.length < total) throw new Error('A lista de perdas mudou durante a exportação. Tente novamente.');
      break;
    }
    items.push(...page.items);
  }
  return items;
}

export async function listarCrmInicial(
  clientId: number,
  clinicId: number,
  limit = 60,
  params: CrmListParams = {}
): Promise<CrmInitialResponse> {
  const queryParams = buildCrmQueryParams(limit, params);

  return await getCrmList<CrmInitialResponse>(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/crm/initial?${queryParams}`
  );
}

export async function listarCrmPagina<TKey extends CrmEntityKey>(
  clientId: number,
  clinicId: number,
  entityKey: TKey,
  offset: number,
  limit = 60,
  params: CrmListParams = {}
): Promise<CrmPageResponse<CrmEntityItemMap[TKey]>> {
  const queryParams = buildCrmQueryParams(limit, params, offset);

  return await getCrmList<CrmPageResponse<CrmEntityItemMap[TKey]>>(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/crm/${entityKey}?${queryParams}`
  );
}

/**
 * 1) Lista todos os leads de um determinado client/clinic.
 */
export async function listarLeads(
  clientId: number,
  clinicId: number
): Promise<Lead[]> {
  // Ex: GET /api/agenda/clients/{clientId}/clinics/{clinicId}/leads
  return await getCrmList<Lead[]>(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/leads/`
  );
}

/**
 * 2) Obtém um lead específico pelo ID.
 */
export async function obterLead(
  clientId: number,
  clinicId: number,
  leadId: number
): Promise<Lead> {
  // Ex: GET /api/agenda/clients/{clientId}/clinics/{clinicId}/leads/{leadId}
  const response: AxiosResponse<Lead> = await api.get(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/leads/${leadId}`
  );
  return response.data;
}

/**
 * Lista todos os comparecimentos (GET).
 */
export async function listarComparecimentos(
  clientId: number,
  clinicId: number
): Promise<Comparecimento[]> {
  // GET /api/agenda/clients/{clientId}/clinics/{clinicId}/comparecimentos
  return await getCrmList<Comparecimento[]>(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/comparecimentos/`
  );
}

/**
 * Obtém um comparecimento específico (GET).
 */
export async function obterComparecimento(
  clientId: number,
  clinicId: number,
  comparecimentoId: number
): Promise<Comparecimento> {
  // GET /api/agenda/clients/{clientId}/clinics/{clinicId}/comparecimentos/{comparecimentoId}
  const response: AxiosResponse<Comparecimento> = await api.get(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/comparecimentos/${comparecimentoId}`
  );
  return response.data;
}

/**
 * Cria um novo comparecimento (POST).
 */
export async function criarComparecimento(
  clientId: number,
  clinicId: number,
  data: ComparecimentoCreate
): Promise<Comparecimento> {
  // POST /api/agenda/clients/{clientId}/clinics/{clinicId}/comparecimentos
  const response: AxiosResponse<Comparecimento> = await api.post(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/comparecimentos`,
    data
  );
  return response.data;
}

/**
 * Atualiza um comparecimento específico (PUT).
 */
export async function atualizarComparecimento(
  clientId: number,
  clinicId: number,
  comparecimentoId: number,
  data: ComparecimentoUpdate
): Promise<Comparecimento> {
  // PUT /api/agenda/clients/{clientId}/clinics/{clinicId}/comparecimentos/{comparecimentoId}
  const response: AxiosResponse<Comparecimento> = await api.put(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/comparecimentos/${comparecimentoId}`,
    data
  );
  return response.data;
}

/**
 * Deleta um comparecimento específico (DELETE).
 */
export async function deletarComparecimento(
  clientId: number,
  clinicId: number,
  comparecimentoId: number
): Promise<void> {
  // DELETE /api/agenda/clients/{clientId}/clinics/{clinicId}/comparecimentos/{comparecimentoId}
  await api.delete(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/comparecimentos/${comparecimentoId}`
  );
}

/**
 * 1) Lista todas as vendas (GET)
 * GET /api/agenda/clients/{clientId}/clinics/{clinicId}/vendas
 */
export async function listarVendas(
  clientId: number,
  clinicId: number
): Promise<Venda[]> {
  return await getCrmList<Venda[]>(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/vendas/`
  );
}

/**
 * 2) Obtém uma venda específica (GET)
 * GET /api/agenda/clients/{clientId}/clinics/{clinicId}/vendas/{vendaId}
 */
export async function obterVenda(
  clientId: number,
  clinicId: number,
  vendaId: number
): Promise<Venda> {
  const response: AxiosResponse<Venda> = await api.get(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/vendas/${vendaId}`
  );
  return response.data;
}

/**
 * 3) Cria uma nova venda (POST)
 * POST /api/agenda/clients/{clientId}/clinics/{clinicId}/vendas
 */
export async function criarVenda(
  clientId: number,
  clinicId: number,
  data: VendaCreate
): Promise<Venda> {
  const response: AxiosResponse<Venda> = await api.post(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/vendas`,
    data
  );
  return response.data;
}

/**
 * 4) Atualiza uma venda específica (PUT)
 * PUT /api/agenda/clients/{clientId}/clinics/{clinicId}/vendas/{vendaId}
 */
export async function atualizarVenda(
  clientId: number,
  clinicId: number,
  vendaId: number,
  data: VendaUpdate
): Promise<Venda> {
  const response: AxiosResponse<Venda> = await api.put(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/vendas/${vendaId}`,
    data
  );
  return response.data;
}

/**
 * 5) Deleta uma venda específica (DELETE)
 * DELETE /api/agenda/clients/{clientId}/clinics/{clinicId}/vendas/{vendaId}
 */
export async function deletarVenda(
  clientId: number,
  clinicId: number,
  vendaId: number
): Promise<void> {
  await api.delete(
    `/api/agenda/clients/${clientId}/clinics/${clinicId}/vendas/${vendaId}`
  );
}

// ==============================
// [NO-SHOW FOLLOW-UP] SEQUÊNCIAS
// ==============================

/**
 * Cria uma nova sequência de no-show para uma clínica específica.
 * Endpoint: POST /api/noshow-followups/{clinic_id}
 */
export async function createNoShowFollowUpSequence(
  clinicId: number,
  payload: NoShowFollowUpSequenceCreate
): Promise<NoShowFollowUpSequenceResponse> {
  try {
    const resp = await api.post<NoShowFollowUpSequenceResponse>(
      `/api/noshow-followups/${clinicId}`,
      payload
    );
    return resp.data; // { message, sequence_id }
  } catch (error) {
    console.error("[createNoShowFollowUpSequence] Erro:");
    throw new Error('Não foi possível criar a sequência de no-show.');
  }
}

/**
 * Obtém a sequência de no-show (caso exista) para uma clínica específica.
 * Endpoint: GET /api/noshow-followups/{clinic_id}
 */
export async function getNoShowFollowUpSequence(
  clinicId: number
): Promise<NoShowFollowUpSequenceDetail | null> {
  try {
    const resp = await api.get<NoShowFollowUpSequenceDetail>(
      `/api/noshow-followups/${clinicId}`,
      {
        validateStatus: (status) => {
          // Aceita 200 e 404
          return (status >= 200 && status < 300) || status === 404;
        },
      }
    );

    if (resp.status === 404) {
      return null;
    }

    return resp.data;
  } catch (error) {
    console.error("[getNoShowFollowUpSequence] Erro:");
    throw new Error('Não foi possível obter a sequência de no-show.');
  }
}

/**
 * Atualiza completamente a sequência de no-show para a clínica específica.
 * Endpoint: PUT /api/noshow-followups/{clinic_id}
 */
export async function updateNoShowFollowUpSequence(
  clinicId: number,
  payload: NoShowFollowUpSequenceUpdate
): Promise<NoShowFollowUpSequenceResponse> {
  try {
    const resp = await api.put<NoShowFollowUpSequenceResponse>(
      `/api/noshow-followups/${clinicId}`,
      payload
    );
    return resp.data; // { message, sequence_id }
  } catch (error) {
    console.error("[updateNoShowFollowUpSequence] Erro:");
    throw new Error('Não foi possível atualizar a sequência de no-show.');
  }
}

/**
 * Deleta a sequência de no-show (se existir) para a clínica específica.
 * Endpoint: DELETE /api/noshow-followups/{clinic_id}
 */
export async function deleteNoShowFollowUpSequence(
  clinicId: number
): Promise<{ message: string }> {
  try {
    const resp = await api.delete<{ message: string }>(
      `/api/noshow-followups/${clinicId}`
    );
    return resp.data; // { message: 'No-show follow-up sequence excluída...' }
  } catch (error) {
    console.error("[deleteNoShowFollowUpSequence] Erro:");
    throw new Error('Não foi possível deletar a sequência de no-show.');
  }
}

// ==============================
// [NO-SHOW FOLLOW-UP] SCHEDULE
// ==============================

/**
 * Cria uma nova configuração de no-show schedule para a clínica {clinicId}.
 * Endpoint: POST /api/noshow-schedule/{clinic_id}
 */
export async function createNoShowScheduleConfig(
  clinicId: number,
  payload: NoShowScheduleCreate
): Promise<{ message: string; id: number; clinic_id: number }> {
  try {
    const resp = await api.post<{ message: string; id: number; clinic_id: number }>(
      `/api/noshow-schedule/${clinicId}`,
      payload
    );
    return resp.data;
  } catch (error) {
    console.error("[createNoShowScheduleConfig] Erro:");
    throw new Error('Não foi possível criar a configuração de no-show schedule.');
  }
}

/**
 * Obtém a configuração de no-show schedule da clínica {clinicId}.
 * Endpoint: GET /api/noshow-schedule/{clinic_id}
 */
export async function getNoShowScheduleConfig(
  clinicId: number
): Promise<NoShowScheduleConfig | null> {
  try {
    const resp = await api.get<NoShowScheduleConfig>(
      `/api/noshow-schedule/${clinicId}`,
      {
        validateStatus: (status) => {
          return (status >= 200 && status < 300) || status === 404;
        },
      }
    );
    if (resp.status === 404) {
      return null; // caso não exista config
    }
    return resp.data;
  } catch (error) {
    console.error("[getNoShowScheduleConfig] Erro:");
    throw new Error('Não foi possível obter a configuração de no-show schedule.');
  }
}

/**
 * Atualiza a configuração de no-show schedule para a clínica {clinicId}.
 * Endpoint: PUT /api/noshow-schedule/{clinic_id}
 */
export async function updateNoShowScheduleConfig(
  clinicId: number,
  payload: NoShowScheduleUpdate
): Promise<{ message: string; id: number; clinic_id: number }> {
  try {
    const resp = await api.put<{ message: string; id: number; clinic_id: number }>(
      `/api/noshow-schedule/${clinicId}`,
      payload
    );
    return resp.data;
  } catch (error) {
    console.error("[updateNoShowScheduleConfig] Erro:");
    throw new Error('Não foi possível atualizar a configuração de no-show schedule.');
  }
}

/**
 * Deleta a configuração de no-show schedule para a clínica {clinicId}.
 * Endpoint: DELETE /api/noshow-schedule/{clinic_id}
 */
export async function deleteNoShowScheduleConfig(
  clinicId: number
): Promise<{ message: string }> {
  try {
    const resp = await api.delete<{ message: string }>(
      `/api/noshow-schedule/${clinicId}`
    );
    return resp.data;
  } catch (error) {
    console.error("[deleteNoShowScheduleConfig] Erro:");
    throw new Error('Não foi possível deletar a configuração de no-show schedule.');
  }
}

/**
 * Marcar um agendamento como NO_SHOW enviando o payload { observacao: "..." }.
 * PUT /api/agenda/clients/{clientId}/clinics/{clinicId}/agendamentos/{agendamentoId}/noshow
 */
export async function marcarNoShowAgendamento(
  clientId: number,
  clinicId: number,
  agendamentoId: number,
  payload: NoShowCreatePayload
): Promise<string> {
  try {
    // body JSON contendo { observacao: "..." }
    const resp = await api.put(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/agendamentos/${agendamentoId}/noshow`,
      payload
    );
    // Se o backend retorna { message: "...", task_id, noshow_id }, pegamos message
    return resp.data.message || 'Agendamento marcado como no-show com sucesso!';
  } catch (error) {
    console.error("[marcarNoShowAgendamento] Erro ao marcar no-show:");
    throw error;
  }
}

/**
 * Lista todos os no-shows de um determinado clientId e clinicId.
 * GET /api/agenda/clients/{clientId}/clinics/{clinicId}/noshow-events
 */
export async function listarNoShows(
  clientId: number,
  clinicId: number
): Promise<NoShowEvent[]> {
  try {
    return await getCrmList<NoShowEvent[]>(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/noshow-events/`
    );
  } catch (error) {
    console.error("[listarNoShows] Erro:");
    throw error;
  }
}

/**
 * Obtém um no-show específico pelo ID.
 * GET /api/agenda/clients/{clientId}/clinics/{clinicId}/noshow-events/{noShowId}
 */
export async function obterNoShowEvent(
  clientId: number,
  clinicId: number,
  noShowId: number
): Promise<NoShowEvent> {
  try {
    const resp = await api.get<NoShowEvent>(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/noshow-events/${noShowId}`
    );
    return resp.data;
  } catch (error) {
    console.error("[obterNoShowEvent] Erro:");
    throw error;
  }
}

/**
 * Se quisermos atualizar apenas a observação do no-show:
 * PUT /api/agenda/clients/{clientId}/clinics/{clinicId}/noshow-events/{noShowId}
 */
export interface NoShowUpdatePayload {
  observacao?: string;
  midia?: string;
  // Se quiser permitir editar nome, phone, data_agendada,
  // adicione aqui: nome?: string, phone?: string, data_agendada?: string ...
}

export async function atualizarNoShowEvent(
  clientId: number,
  clinicId: number,
  noShowId: number,
  data: NoShowUpdatePayload
): Promise<NoShowEvent> {
  try {
    const resp = await api.put<NoShowEvent>(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/noshow-events/${noShowId}`,
      data
    );
    return resp.data; // objeto NoShowEvent atualizado
  } catch (error) {
    console.error("[atualizarNoShowEvent] Erro:");
    throw error;
  }
}

/**
 * Deleta um registro de no-show específico.
 * DELETE /api/agenda/clients/{clientId}/clinics/{clinicId}/noshow-events/{noShowId}
 */
export async function deletarNoShowEvent(
  clientId: number,
  clinicId: number,
  noShowId: number
): Promise<void> {
  try {
    await api.delete(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/noshow-events/${noShowId}`
    );
  } catch (error) {
    console.error("[deletarNoShowEvent] Erro:");
    throw error;
  }
}

export async function getMessageMedia(content: string, fromMe: boolean): Promise<string> {
  try {
    // URLs internas e URLs app/dev persistidas do WAHA sempre passam pelo
    // proxy same-origin, com autorização de clínica validada no backend.
    const normalizedWahaUrl = convertWahaUrlToProxy(content);
    if (normalizedWahaUrl.startsWith('/api/waha/media/')) {
      try {
        const wahaResp = await api.get(normalizedWahaUrl, {
          responseType: 'blob'
        });

        const blob = new Blob([wahaResp.data], {
          type: wahaResp.headers['content-type'] || getMimeType(normalizedWahaUrl)
        });
        return URL.createObjectURL(blob);
      } catch (wahaError) {

        return normalizedWahaUrl;
      }
    }

    // Para conteúdo já em base64, converter para Blob e criar URL
    if (content.startsWith('data:')) {
      // Identificar se é um áudio ou vídeo em base64
      const isAudio = content.includes('audio/');
      const isVideo = content.includes('video/');

      if (isAudio) {
        // Extrair o MIME type completo, mantendo parâmetros como "codecs=opus"
        const mimePartMatch = content.match(/data:(audio\/[^;]+)(;[^,]*)?/);
        const mimeType = mimePartMatch ? mimePartMatch[1] : 'audio/ogg';
        // Extrair a parte base64
        const base64Content = content.split(',')[1];

        // Converter base64 para Blob
        const binary = atob(base64Content);
        const array = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
          array[i] = binary.charCodeAt(i);
        }

        // Criar Blob com o MIME type correto
        const blob = new Blob([array], { type: mimeType });

        // Criar uma URL para este blob
        const blobUrl = URL.createObjectURL(blob);

        // Criar um elemento de Audio para pré-carregar os metadados
        const audio = new Audio();
        audio.src = blobUrl;

        // Retornar uma Promise que resolve com a URL quando os metadados estiverem carregados
        return new Promise((resolve) => {
          audio.onloadedmetadata = () => {

            resolve(blobUrl);
          };

          // Se falhar ao carregar metadados, ainda assim retornar a URL do blob
          audio.onerror = () => {
            console.warn("Erro ao carregar metadados do áudio, usando URL sem duração");
            resolve(blobUrl);
          };

          // Se demorar muito, continuar mesmo sem metadados
          setTimeout(() => {
            if (audio.duration === 0) {
              console.warn("Timeout ao carregar metadados do áudio, usando URL sem duração");
              resolve(blobUrl);
            }
          }, 2000);
        });
      } else if (isVideo) {
        // Extrair o MIME type completo para vídeos
        const mimePartMatch = content.match(/data:(video\/[^;]+)(;[^,]*)?/);
        const mimeType = mimePartMatch ? mimePartMatch[1] : 'video/mp4';

        // Extrair a parte base64
        const base64Content = content.split(',')[1];

        // Converter base64 para Blob
        const binary = atob(base64Content);
        const array = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
          array[i] = binary.charCodeAt(i);
        }

        // Criar Blob com o MIME type correto
        const blob = new Blob([array], { type: mimeType });

        // Criar uma URL para este blob
        const blobUrl = URL.createObjectURL(blob);

        // Criar um elemento de Video para pré-carregar os metadados
        const video = document.createElement('video');
        video.src = blobUrl;

        // Retornar uma Promise que resolve com a URL quando os metadados estiverem carregados
        return new Promise((resolve) => {
          video.onloadedmetadata = () => {

            resolve(blobUrl);
          };

          // Se falhar ao carregar metadados, ainda assim retornar a URL do blob
          video.onerror = () => {
            console.warn("Erro ao carregar metadados do vídeo, usando URL sem duração");
            resolve(blobUrl);
          };

          // Se demorar muito, continuar mesmo sem metadados
          setTimeout(() => {
            if (video.duration === 0 || video.videoWidth === 0) {
              console.warn("Timeout ao carregar metadados do vídeo, usando URL sem duração");
              resolve(blobUrl);
            }
          }, 2000);
        });
      }

      // Para outros tipos de mídia em base64, retornar como está
      return content;
    }

    if (content.startsWith('/api/arquivos/media/')) {


      const localResp = await api.get(content, {
        responseType: 'blob'
      });

      const blob = new Blob([localResp.data], {
        type: localResp.headers['content-type'] || getMimeType(content)
      });
      return URL.createObjectURL(blob);
    }

    // Se o content for uma URL HTTP completa (incluindo WAHA externo)
    if (content.startsWith('http')) {


      try {
        // Tentar buscar via proxy do backend para melhor tratamento de CORS/autenticação
        const proxyResp = await api.get('/media/whatsapp/proxy', {
          params: { url: content },
          responseType: 'blob'
        });

        const blob = new Blob([proxyResp.data], {
          type: proxyResp.headers['content-type'] || getMimeType(content)
        });
        console.log("[Media Fix] ✅ Sucesso via proxy HTTP");
        return URL.createObjectURL(blob);
      } catch (proxyError) {


        // Fallback: tentar busca direta
        try {
          const directResp = await fetch(content);
          if (!directResp.ok) {
            throw new Error(`HTTP ${directResp.status}: ${directResp.statusText}`);
          }

          const blob = await directResp.blob();
          console.log("[Media Fix] ✅ Sucesso via busca direta");
          return URL.createObjectURL(blob);
        } catch (directError) {

          throw new Error(`Falha ao carregar mídia HTTP: ${directError}`);
        }
      }
    }

    // Resto do código existente...
    // Obtém client_id e clinic_id do localStorage
    const clientId = localStorage.getItem('client_id');
    const clinicId = localStorage.getItem('clinic_id');
    if (!clientId || !clinicId) {
      throw new Error('client_id ou clinic_id não encontrados');
    }

    // Para imagens no formato "client_X/clinic_Y/..."
    const clientClinicPattern = /^client_(\d+)\/clinic_(\d+)\/(.+)$/;
    const match = content.match(clientClinicPattern);

    if (match) {
      // Extrair os componentes do caminho corretamente
      const [, pathClientId, pathClinicId, actualPath] = match;

      // PRIMEIRA TENTATIVA: endpoint público para arquivos locais salvos pelo backend
      try {
        const localPath = `/api/arquivos/media/client_${pathClientId}/clinic_${pathClinicId}/${actualPath}`;

        const localResp = await api.get(localPath, {
          responseType: 'blob'
        });

        const blob = new Blob([localResp.data], {
          type: localResp.headers['content-type'] || getMimeType(actualPath)
        });
        console.log("[Media Fix] ✅ Sucesso com arquivo local");
        return URL.createObjectURL(blob);
      } catch (localError) {

      }

      // SEGUNDA TENTATIVA: Endpoint tradicional autenticado para mensagens
      try {

        const resp = await api.get(`/media/messages/${pathClientId}/${pathClinicId}/${actualPath}`, {
          responseType: 'blob'
        });

        const blob = new Blob([resp.data], {
          type: resp.headers['content-type'] || getMimeType(actualPath)
        });
        console.log("[Media Fix] ✅ Sucesso com endpoint tradicional");
        return URL.createObjectURL(blob);
      } catch (innerError) {

      }

      // TERCEIRA TENTATIVA: Endpoint WAHA para URLs legadas
      try {

        const wahaResp = await api.get(convertWahaUrlToProxy(`/api/waha/media/client_${pathClientId}/clinic_${pathClinicId}/${actualPath}`), {
          responseType: 'blob'
        });

        const blob = new Blob([wahaResp.data], {
          type: wahaResp.headers['content-type'] || getMimeType(actualPath)
        });
        console.log("[Media Fix] ✅ Sucesso com endpoint WAHA");
        return URL.createObjectURL(blob);
      } catch (wahaError) {

        return `${API_URL}/api/arquivos/media/${content}`;
      }
    }

    // Para outros formatos de caminho (sem prefixo client/clinic)
    try {
      const resp = await api.get(`/media/messages/${clientId}/${clinicId}/${content}`, {
        responseType: 'blob'
      });

      const blob = new Blob([resp.data], {
        type: resp.headers['content-type'] || 'application/octet-stream'
      });
      return URL.createObjectURL(blob);
    } catch (error) {

      // Tentar acessar a URL direta como último recurso
      return `${API_URL}/${content}`;
    }
  } catch (error) {

    // Adicionar logs para depuração


    // Retornar placeholder como último recurso
    return '/assets/image-placeholder.png';
  }
}

/**
 * Consulta as métricas de funil (Leads, Agendamentos, Comparecimentos, Vendas, etc.)
 * Endpoint: GET /api/metrics/funnels?clinic_id=...&start_date=...&end_date=...
 */
export async function getFunnelMetrics(params: FunnelMetricsParams): Promise<FunnelMetricsResponse> {
  try {
    // Montamos a query string
    const queryString = new URLSearchParams();

    if (params.clinicId) {
      queryString.append('clinic_id', params.clinicId.toString());
    }
    if (params.startDate) {
      queryString.append('start_date', params.startDate);
    }
    if (params.endDate) {
      queryString.append('end_date', params.endDate);
    }
    if (params.fonte) {
      queryString.append('fonte', params.fonte);
    }
    if (params.tagIds && params.tagIds.length > 0) {
      queryString.append('tag_ids', params.tagIds.join(','));
    }
    appendCreatorMetricsParams(queryString, params.createdByType, params.createdByUserId);

    // Exemplo de URL final: /api/metrics/funnels?clinic_id=1&start_date=2025-01-01&end_date=2025-01-31
    const url = `/api/metrics/funnels?${queryString.toString()}`;
    console.log("[getFunnelMetrics] Chamando GET em:");

    // Fazemos a chamada usando nosso axios 'api'
    const resp = await api.get<FunnelMetricsResponse>(url);

    console.log("[getFunnelMetrics] Resposta:");
    return resp.data;
  } catch (error) {
    console.error("[getFunnelMetrics] Erro ao obter métricas do funil:");
    throw error;
  }
}

export interface ContactModeAuditReceipt {
  id?: number | null;
  action_type?: 'human_takeover' | 'release_to_bot' | string;
  actor_id?: number | null;
  actor_kind?: 'user' | 'client' | 'system' | string;
  actor_name?: string | null;
  actor_email?: string | null;
  created_at?: string | null;
}

export interface ContactModeResponse {
  success: boolean;
  changed: boolean;
  contact_id: number;
  phone: string;
  human_mode: boolean;
  previous_human_mode?: boolean;
  audit_id?: number | null;
  changed_at?: string | null;
  audit?: ContactModeAuditReceipt | null;
  message?: string;
}

interface ContactModeScopeParams {
  clinic_id: string;
  client_id: string;
}

const getContactModeScopeParams = (): ContactModeScopeParams => {
  const clinicId = localStorage.getItem('clinic_id');
  const clientId = localStorage.getItem('client_id');

  if (!clinicId) {
    throw new Error('Clínica não identificada');
  }
  if (!clientId) {
    throw new Error('Cliente não identificado');
  }

  return {
    clinic_id: clinicId,
    client_id: clientId,
  };
};

const requirePositiveContactId = (contactId: number): number => {
  if (!Number.isInteger(contactId) || contactId <= 0) {
    throw new Error('Contato não identificado com segurança');
  }
  return contactId;
};

export async function getContactMode(contactId: number): Promise<ContactModeResponse> {
  const safeContactId = requirePositiveContactId(contactId);
  const response = await api.get<ContactModeResponse>(
    `/api/atendimento/contacts/${safeContactId}/mode`,
    { params: getContactModeScopeParams() },
  );
  return response.data;
}

export async function setContactMode(
  contactId: number,
  humanMode: boolean,
  expectedHumanMode: boolean,
): Promise<ContactModeResponse> {
  const safeContactId = requirePositiveContactId(contactId);
  const response = await api.put<ContactModeResponse>(
    `/api/atendimento/contacts/${safeContactId}/mode`,
    {
      human_mode: humanMode,
      expected_human_mode: expectedHumanMode,
    },
    { params: getContactModeScopeParams() },
  );
  return response.data;
}

export function isContactModeTransitionAmbiguous(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  return (
    isContactModeTransitionRetryable(error) ||
    status === 409
  );
}

export function isContactModeTransitionRetryable(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  return (
    !error.response ||
    error.code === 'ECONNABORTED' ||
    error.code === 'ERR_NETWORK'
  );
}

/**
 * Compatibilidade para consumidores antigos do serviço.
 * Os novos fluxos devem preferir setContactMode, que exige o estado esperado.
 */
export async function takeOverContact(
  contactId: number,
  expectedHumanMode = false,
): Promise<ContactModeResponse> {
  return setContactMode(contactId, true, expectedHumanMode);
}

export async function releaseContactToBot(
  contactId: number,
  expectedHumanMode = true,
): Promise<ContactModeResponse> {
  return setContactMode(contactId, false, expectedHumanMode);
}


// --------------------
//    Funções de API
// --------------------

export async function listUsers(clinicId?: number): Promise<User[]> {
  try {
    const params = clinicId ? { clinic_id: clinicId } : {};
    const response = await api.get('/api/users/', { params });
    return response.data;
  } catch (error) {
    console.error("Erro ao listar usuários:");
    handleApiError(error, 'Erro ao obter lista de usuários');
    throw new Error('Unreachable');
  }
}

export async function createUser(userData: UserCreate): Promise<User> {
  try {
    const response = await api.post('/api/users/', userData);
    return response.data;
  } catch (error) {
    console.error("Erro ao criar usuário:");
    handleApiError(error, 'Erro ao criar novo usuário');
    throw new Error('Unreachable');
  }
}

export async function updateUser(userId: number, userData: UserUpdate): Promise<User> {
  try {
    const response = await api.patch(`/api/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    console.error("Erro ao atualizar usuário:");
    handleApiError(error, 'Erro ao atualizar usuário');
    throw new Error('Unreachable');
  }
}

export async function deleteUser(userId: number): Promise<void> {
  try {
    await api.delete(`/api/users/${userId}`);
  } catch (error) {
    console.error("Erro ao deletar usuário:");
    handleApiError(error, 'Erro ao remover usuário');
    throw new Error('Unreachable');
  }
}

export async function changeUserPassword(
  userId: number,
  newPassword: string,
  confirmPassword: string
): Promise<User> {
  try {
    const response = await api.post(`/api/users/${userId}/change-password`, {
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao alterar senha:");
    handleApiError(error, 'Erro ao alterar senha do usuário');
    throw new Error('Unreachable');
  }
}

// --------------------
//  Funções de Equipes
// --------------------

export async function getTeams(): Promise<Team[]> {
  try {
    const response = await api.get('/api/teams/');
    return response.data;
  } catch (error) {
    console.error("Erro ao listar equipes:");
    handleApiError(error, 'Erro ao obter lista de equipes');
    throw new Error('Unreachable');
  }
}

export async function getTeam(teamId: number): Promise<Team> {
  try {
    const response = await api.get(`/api/teams/${teamId}`);
    return response.data;
  } catch (error) {
    console.error("Erro ao obter equipe:");
    handleApiError(error, 'Erro ao obter dados da equipe');
    throw new Error('Unreachable');
  }
}

export async function createTeam(teamData: TeamCreateRequest): Promise<Team> {
  try {
    const response = await api.post('/api/teams/', teamData);
    return response.data;
  } catch (error) {
    console.error("Erro ao criar equipe:");
    handleApiError(error, 'Erro ao criar equipe');
    throw new Error('Unreachable');
  }
}

export async function updateTeam(teamId: number, teamData: TeamUpdateRequest): Promise<Team> {
  try {
    const response = await api.patch(`/api/teams/${teamId}`, teamData);
    return response.data;
  } catch (error) {
    console.error("Erro ao atualizar equipe:");
    handleApiError(error, 'Erro ao atualizar equipe');
    throw new Error('Unreachable');
  }
}

export async function updateTeamPermissions(teamId: number, permissions: TeamPermissionsRequest): Promise<Team> {
  try {
    const response = await api.put(`/api/teams/${teamId}/permissions`, permissions);
    return response.data;
  } catch (error) {
    console.error("Erro ao atualizar permissões da equipe:");
    handleApiError(error, 'Erro ao atualizar permissões da equipe');
    throw new Error('Unreachable');
  }
}

export async function deleteTeam(teamId: number): Promise<void> {
  try {
    await api.delete(`/api/teams/${teamId}`);
  } catch (error) {
    console.error("Erro ao remover equipe:");
    handleApiError(error, 'Erro ao remover equipe');
    throw new Error('Unreachable');
  }
}

export async function getMyTeamPermissions(): Promise<MyTeamPermissionsResponse> {
  try {
    const response = await api.get('/api/teams/me/permissions');
    return response.data;
  } catch (error) {
    console.error("Erro ao obter permissões da equipe:");
    handleApiError(error, 'Erro ao obter permissões da equipe');
    throw new Error('Unreachable');
  }
}

export async function getTeamUsers(teamId: number): Promise<User[]> {
  try {
    const response = await api.get(`/api/teams/${teamId}/users`);
    return response.data;
  } catch (error) {
    console.error("Erro ao listar usuários da equipe:");
    handleApiError(error, 'Erro ao obter usuários da equipe');
    throw new Error('Unreachable');
  }
}

export async function assignUserToTeam(teamId: number, userId: number): Promise<void> {
  try {
    await api.post(`/api/teams/${teamId}/users`, { user_id: userId });
  } catch (error) {
    console.error("Erro ao atribuir usuário à equipe:");
    handleApiError(error, 'Erro ao atribuir usuário à equipe');
    throw new Error('Unreachable');
  }
}

export async function removeUserFromTeam(teamId: number, userId: number): Promise<void> {
  try {
    await api.delete(`/api/teams/${teamId}/users/${userId}`);
  } catch (error) {
    console.error("Erro ao remover usuário da equipe:");
    handleApiError(error, 'Erro ao remover usuário da equipe');
    throw new Error('Unreachable');
  }
}

export async function initializeTeams(): Promise<void> {
  try {
    await api.post('/api/teams/initialize');
  } catch (error) {
    console.error("Erro ao inicializar equipes:");
    handleApiError(error, 'Erro ao inicializar equipes padrão');
    throw new Error('Unreachable');
  }
}

// ----------------------------------------------------------------
// Criação de Lead (POST)
// ----------------------------------------------------------------
export async function criarLead(
  clientId: number,
  clinicId: number,
  data: LeadCreate
): Promise<Lead> {
  try {
    // POST /api/agenda/clients/{clientId}/clinics/{clinicId}/leads
    const response = await api.post<Lead>(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/leads`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("[criarLead] Erro ao criar lead:");
    throw error;
  }
}

// ----------------------------------------------------------------
// Atualização de Lead (PUT)
// ----------------------------------------------------------------
export async function atualizarLead(
  clientId: number,
  clinicId: number,
  leadId: number,
  data: LeadUpdate
): Promise<Lead> {
  try {
    // PUT /api/agenda/clients/{clientId}/clinics/{clinicId}/leads/{leadId}
    const response = await api.put<Lead>(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/leads/${leadId}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("[atualizarLead] Erro ao atualizar lead:");
    throw error;
  }
}

// ----------------------------------------------------------------
// Exclusão de Lead (DELETE)
// ----------------------------------------------------------------
export async function deletarLead(
  clientId: number,
  clinicId: number,
  leadId: number
): Promise<void> {
  try {
    // DELETE /api/agenda/clients/{clientId}/clinics/{clinicId}/leads/{leadId}
    await api.delete(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/leads/${leadId}`
    );
  } catch (error) {
    console.error("[deletarLead] Erro ao deletar lead:");
    throw error;
  }
}

export interface CrmExportPage {
  leads: Pick<Lead, 'id' | 'name' | 'phone' | 'data_entrada' | 'source_id'>[];
  appointments: Pick<AgendamentoResponse, 'id' | 'lead_id' | 'consulta_data'>[];
  attendances: Pick<Comparecimento, 'id' | 'lead_id' | 'compareceu_em' | 'valor_orcamento'>[];
  sales: Pick<Venda, 'id' | 'lead_id' | 'venda_data' | 'valor_faturado' | 'valor_pago'>[];
  noShows: Pick<NoShowEvent, 'id' | 'lead_id' | 'agendamento_id'>[];
  losses: Pick<CrmLoss, 'id' | 'lead_id' | 'reopened_at' | 'from_stage' | 'actor_name' | 'lost_at' | 'reason' | 'observation'>[];
  lead_tags: Record<number, { tags: Pick<TagResponse, 'id' | 'name'>[] }>;
  users: Record<number, { usuario_atribuido: { nome: string } | null }>;
  total: number;
}

const CRM_EXPORT_PAGE_SIZE = 200;

async function loadCrmExportData(
  clientId: number,
  clinicId: number,
  startDate: string | undefined,
  endDate: string | undefined,
  lossesEnabled: boolean
): Promise<CrmExportPage> {
  const dataset: CrmExportPage = {
    leads: [], appointments: [], attendances: [], sales: [], noShows: [], losses: [],
    lead_tags: {}, users: {}, total: 0,
  };
  const leadIds = new Set<number>();
  let expectedTotal: number | undefined;

  do {
    const { data: page } = await api.get<CrmExportPage>(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/crm/export`,
      { params: {
        start_date: startDate,
        end_date: endDate,
        include_losses: lossesEnabled,
        limit: CRM_EXPORT_PAGE_SIZE,
        offset: dataset.leads.length,
      } }
    );
    const incomplete = () => new Error('Não foi possível carregar o relatório completo. Nenhum arquivo foi gerado.');
    const collections = ['leads', 'appointments', 'attendances', 'sales', 'noShows', 'losses'] as const;
    if (!page || !Number.isSafeInteger(page.total) || page.total < 0 ||
        collections.some(key => !Array.isArray(page[key])) ||
        !page.lead_tags || !page.users ||
        (expectedTotal !== undefined && page.total !== expectedTotal) ||
        page.leads.length > CRM_EXPORT_PAGE_SIZE ||
        dataset.leads.length + page.leads.length > page.total ||
        (dataset.leads.length < page.total && page.leads.length === 0)) {
      throw incomplete();
    }
    expectedTotal = page.total;
    const pageLeadIds = new Set<number>();
    for (const lead of page.leads) {
      if (!lead || !Number.isSafeInteger(lead.id) || lead.id <= 0 ||
          leadIds.has(lead.id) || pageLeadIds.has(lead.id)) throw incomplete();
      const tags = page.lead_tags[lead.id]?.tags;
      const user = page.users[lead.id];
      if (!Array.isArray(tags) || tags.some(tag => (
        !tag || !Number.isSafeInteger(tag.id) || tag.id <= 0 || typeof tag.name !== 'string'
      )) || !user || !Object.prototype.hasOwnProperty.call(user, 'usuario_atribuido') ||
          (user.usuario_atribuido !== null && typeof user.usuario_atribuido?.nome !== 'string')) {
        throw incomplete();
      }
      pageLeadIds.add(lead.id);
    }
    if (Object.keys(page.lead_tags).some(id => !pageLeadIds.has(Number(id))) ||
        Object.keys(page.users).some(id => !pageLeadIds.has(Number(id)))) throw incomplete();
    for (const key of ['appointments', 'attendances', 'sales', 'noShows', 'losses'] as const) {
      if (page[key].some(item => !item || !Number.isSafeInteger(item.id) || item.id <= 0 ||
          !pageLeadIds.has(item.lead_id))) throw incomplete();
    }
    pageLeadIds.forEach(id => leadIds.add(id));
    dataset.leads.push(...page.leads);
    dataset.appointments = dataset.appointments.concat(page.appointments);
    dataset.attendances = dataset.attendances.concat(page.attendances);
    dataset.sales = dataset.sales.concat(page.sales);
    dataset.noShows = dataset.noShows.concat(page.noShows);
    dataset.losses = dataset.losses.concat(page.losses);
    Object.assign(dataset.lead_tags, page.lead_tags);
    Object.assign(dataset.users, page.users);
    dataset.total = page.total;
  } while (dataset.leads.length < dataset.total);

  return dataset;
}

// O período seleciona os contatos; os fatos e responsáveis vêm do contrato de exportação.
export async function exportLeadsToExcel(
  clientId: number,
  clinicId: number,
  startDate: string | undefined,
  endDate: string | undefined,
  lossesEnabled = false
) {
  try {
    if (!Number.isSafeInteger(clientId) || clientId <= 0 ||
        !Number.isSafeInteger(clinicId) || clinicId <= 0) {
      throw new Error('Informações de autenticação não encontradas');
    }
    const {
      leads: allLeadsToExport,
      appointments: relevantAgendamentos,
      attendances: relevantComparecimentos,
      sales: relevantVendas,
      noShows: relevantNoShows,
      losses: perdas,
      lead_tags: leadTagsMap,
      users: assignedUsers,
    } = await loadCrmExportData(clientId, clinicId, startDate, endDate, lossesEnabled);

    // Importar xlsx
    const XLSX = await import('xlsx');

    // Preparar dados para exportação
    const exportData = allLeadsToExport.map(lead => {
      const leadTags = leadTagsMap[lead.id].tags || [];
      const uniqueLeadTags = leadTags.filter((tag, index, self) =>
        index === self.findIndex(currentTag => currentTag.id === tag.id)
      );
      const formattedLeadTags = uniqueLeadTags.map(tag => tag.name).join(', ');

      // Buscar TODOS os dados relacionados ao lead
      const leadAgendamentos = relevantAgendamentos.filter(a => a.lead_id === lead.id);
      const leadComparecimentos = relevantComparecimentos.filter(c => c.lead_id === lead.id);
      const leadVendas = relevantVendas.filter(v => v.lead_id === lead.id);

      // Pegar o agendamento mais recente
      const agendamento = leadAgendamentos.sort((a, b) => {
        const dateA = new Date(a.consulta_data || 0).getTime();
        const dateB = new Date(b.consulta_data || 0).getTime();
        return dateB - dateA;
      })[0];

      // Verificar se teve no-show
      const hasNoShow = agendamento && relevantNoShows.some(n => n.agendamento_id === agendamento.id);

      // Pegar o comparecimento mais recente
      const comparecimento = leadComparecimentos.sort((a, b) => {
        const dateA = new Date(a.compareceu_em || 0).getTime();
        const dateB = new Date(b.compareceu_em || 0).getTime();
        return dateB - dateA;
      })[0];

      // Pegar a venda mais recente
      const venda = leadVendas.sort((a, b) => {
        const dateA = new Date(a.venda_data || 0).getTime();
        const dateB = new Date(b.venda_data || 0).getTime();
        return dateB - dateA;
      })[0];

      const perda = perdas.find(item => item.lead_id === lead.id && !item.reopened_at);
      // Determinar status do funil
      let statusFunil = 'Lead';
      if (perda) {
        statusFunil = 'Perdido';
      } else if (venda) {
        statusFunil = 'Vendido';
      } else if (comparecimento) {
        statusFunil = 'Compareceu';
      } else if (hasNoShow) {
        statusFunil = 'Faltou';
      } else if (agendamento) {
        statusFunil = 'Agendado';
      }

      // Formatar datas
      const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return '';
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '';
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${day}/${month}/${year} ${hours}:${minutes}`;
        } catch {
          return '';
        }
      };

      // Formatar valores monetários
      const formatCurrency = (value: number | undefined) => {
        if (value === undefined || value === null) return '';
        return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      };

      // Mapear source_id para nome da mídia
      const getMediaName = (sourceId: string | undefined) => {
        if (!sourceId) return '';
        // Mapear os valores comuns
        const mediaMap: { [key: string]: string } = {
          'facebook': 'Facebook',
          'instagram': 'Instagram',
          'google': 'Google Ads',
          'whatsapp': 'WhatsApp',
          'site': 'Site',
          'indicacao': 'Indicação',
          'meta': 'Meta Ads',
          'google_ads': 'Google Ads',
          'organico': 'Orgânico',
          'outros': 'Outros'
        };
        return mediaMap[sourceId.toLowerCase()] || sourceId;
      };

      return {
        'ID Lead': lead.id,
        'Usuário atribuído': assignedUsers[lead.id]?.usuario_atribuido?.nome || '',
        'Nome': lead.name || '',
        'Telefone': lead.phone || '',
        'Tags': formattedLeadTags,
        'Mídia': getMediaName(lead.source_id),
        'Data Entrada': formatDate(lead.data_entrada),
        'Status Funil': statusFunil,
        ...(lossesEnabled ? {
          'Etapa antes da perda': perda ? ({ leads: 'Lead', appointments: 'Agendamento', noShows: 'No-show', attendances: 'Comparecimento' }[perda.from_stage]) : '',
          'Marcado perdido por': perda?.actor_name || '',
          'Data da perda': perda ? (localStorage.getItem(`sminy.clinicTimezone.${clinicId}`)
            ? new Date(perda.lost_at).toLocaleString('pt-BR', {
                timeZone: localStorage.getItem(`sminy.clinicTimezone.${clinicId}`)!,
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
              })
            : perda.lost_at) : '',
          'Motivo da perda': perda?.reason || '',
          'Observação da perda': perda?.observation || '',
        } : {}),
        'Agendado': agendamento ? 'Sim' : 'Não',
        'Data Agendamento': agendamento ? formatDate(agendamento.consulta_data) : '',
        'Faltou': hasNoShow ? 'Sim' : 'Não',
        'Compareceu': comparecimento ? 'Sim' : 'Não',
        'Data Comparecimento': comparecimento ? formatDate(comparecimento.compareceu_em) : '',
        'Valor Orçado': comparecimento ? formatCurrency(comparecimento.valor_orcamento) : '',
        'Vendido': venda ? 'Sim' : 'Não',
        'Data Venda': venda ? formatDate(venda.venda_data) : '',
        'Valor Faturado': venda ? formatCurrency(venda.valor_faturado) : '',
        'Valor Pago': venda ? formatCurrency(venda.valor_pago) : ''
      };
    });

    // Criar workbook
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Relatório CRM');

    // Ajustar largura das colunas
    const wscols = [
      { wch: 10 }, // ID Lead
      { wch: 28 }, // Usuário atribuído
      { wch: 30 }, // Nome
      { wch: 15 }, // Telefone
      { wch: 40 }, // Tags
      { wch: 15 }, // Mídia
      { wch: 18 }, // Data Entrada
      { wch: 15 }, // Status Funil
      ...(lossesEnabled ? [{ wch: 23 }, { wch: 28 }, { wch: 22 }, { wch: 28 }, { wch: 45 }] : []),
      { wch: 10 }, // Agendado
      { wch: 18 }, // Data Agendamento
      { wch: 8 },  // Faltou
      { wch: 12 }, // Compareceu
      { wch: 20 }, // Data Comparecimento
      { wch: 15 }, // Valor Orçado
      { wch: 10 }, // Vendido
      { wch: 18 }, // Data Venda
      { wch: 15 }, // Valor Faturado
      { wch: 15 }  // Valor Pago
    ];
    ws['!cols'] = wscols;

    // Gerar nome do arquivo com data e período
    const today = new Date();
    const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
    const periodStr = startDate && endDate ? `_${startDate.replace(/-/g, '')}_${endDate.replace(/-/g, '')}` : '';
    const fileName = `relatorio_crm_${dateStr}${periodStr}.xlsx`;

    // Exportar arquivo
    XLSX.writeFile(wb, fileName);

    return true;
  } catch (error) {
    console.error("Erro ao exportar para Excel:");
    throw error;
  }
}

// Manter função CSV original para compatibilidade
export function exportLeadsAsCsv(leads: Lead[]) {
  // 1. Montar cabeçalho
  const csvHeader = 'ID,Nome,Telefone,Mídia,Data de Entrada\n';

  // 2. Montar as linhas
  const csvLines = leads.map((lead) => {
    // Evite quebrar o CSV se existir vírgula, aspas, etc. 
    // Caso precise, use algo como: `"${lead.name?.replace(/"/g, '""')}"` entre aspas duplas.
    const id = lead.id ?? '';
    const nome = lead.name ?? '';
    const telefone = lead.phone ?? '';
    const midia = lead.source_id ?? '';
    const dataEntrada = lead.data_entrada ?? '';
    // Gera a linha CSV
    return `${id},${nome},${telefone},${midia},${dataEntrada}`;
  });

  // 3. Unir tudo
  const csvContent = csvHeader + csvLines.join('\n');

  // 4. Criar Blob
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // 5. Criar link temporário
  const url = URL.createObjectURL(blob);

  // 6. Criar um <a> "invisível" pra forçar o download
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'leads_export.csv');
  // Você pode customizar o nome do arquivo, ex: "leads-2025-02-06.csv"

  // 7. Disparar o clique no link
  document.body.appendChild(link);
  link.click();

  // 8. Limpar
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Cria um novo agendamento para o clientId/clinicId informado.
 * POST /api/agenda/clients/{clientId}/clinics/{clinicId}/agendamentos
 */
export async function criarAgendamento(
  clientId: number,
  clinicId: number,
  data: AgendamentoCreate
): Promise<AgendamentoResponse> {
  try {
    const response = await api.post<AgendamentoResponse>(
      `/api/agenda/clients/${clientId}/clinics/${clinicId}/agendamentos`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("[criarAgendamento] Erro ao criar agendamento:");
    throw error;
  }
}

// Interface para dados diários do funil
export interface DailyFunnelItem {
  date: string;
  leads: number;
  agendamentos: number;
  comparecimentos: number;
  vendas: number;
}

/**
 * Chama GET /api/metrics/daily_funnel?clinic_id=...&start_date=...&end_date=...&fonte=...
 */
export async function getDailyFunnel(
  clinicId?: number,
  startDate?: string,
  endDate?: string,
  fonte?: string,
  tagIds?: number[],
  createdByType?: string,
  createdByUserId?: number
): Promise<DailyFunnelItem[]> {
  const qs = new URLSearchParams();
  if (clinicId) qs.append("clinic_id", clinicId.toString());
  if (startDate) qs.append("start_date", startDate);
  if (endDate) qs.append("end_date", endDate);
  if (fonte) qs.append("fonte", fonte);
  if (tagIds && tagIds.length > 0) qs.append("tag_ids", tagIds.join(','));
  appendCreatorMetricsParams(qs, createdByType, createdByUserId);

  const url = `/api/metrics/daily_funnel?${qs.toString()}`;

  try {
    const resp = await api.get<DailyFunnelItem[]>(url);
    return resp.data;
  } catch (error) {
    console.error("[getDailyFunnel] Erro ao obter dados:");
    throw error;
  }
}

/**
 * Chama GET /api/metrics/funnel_by_source?clinic_id=...&start_date=...&end_date=...
 */
export async function getFunnelBySource(
  clinicId?: number,
  startDate?: string,
  endDate?: string,
  fonte?: string,
  tagIds?: number[],
  createdByType?: string,
  createdByUserId?: number
): Promise<FunnelBySourceItem[]> {
  // Monta a query string
  const qs = new URLSearchParams();
  if (clinicId) qs.append("clinic_id", clinicId.toString());
  if (startDate) qs.append("start_date", startDate);
  if (endDate) qs.append("end_date", endDate);
  if (fonte) qs.append("fonte", fonte);
  if (tagIds && tagIds.length > 0) qs.append("tag_ids", tagIds.join(','));
  appendCreatorMetricsParams(qs, createdByType, createdByUserId);

  // Ex: /api/metrics/funnel_by_source?clinic_id=10&start_date=2025-01-01&end_date=2025-01-31
  const url = `/api/metrics/funnel_by_source?${qs.toString()}`;

  try {
    const resp = await api.get<FunnelBySourceItem[]>(url);
    return resp.data;
  } catch (error) {
    console.error("[getFunnelBySource] Erro ao obter dados:");
    throw error;
  }
}

/**
 * Chama GET /api/metrics/timeline?clinic_id=...&start_date=...&end_date=...&limit=...
 */
export async function getTimeline(
  clinicId?: number,
  startDate?: string,
  endDate?: string,
  limit = 20,
  tagIds?: number[],
  createdByType?: string,
  createdByUserId?: number
): Promise<TimelineEvent[]> {
  // Monta query string
  const qs = new URLSearchParams();
  if (clinicId) qs.append("clinic_id", clinicId.toString());
  if (startDate) qs.append("start_date", startDate);
  if (endDate) qs.append("end_date", endDate);
  if (limit) qs.append("limit", limit.toString());
  if (tagIds && tagIds.length > 0) qs.append("tag_ids", tagIds.join(','));
  appendCreatorMetricsParams(qs, createdByType, createdByUserId);

  const url = `/api/metrics/timeline?${qs.toString()}`;

  try {
    const resp = await api.get<TimelineEvent[]>(url);
    return resp.data;
  } catch (error) {
    console.error("[getTimeline] Erro ao obter timeline:");
    throw error;
  }
}

/**
 * Chama GET /api/metrics/projections?clinic_id=...
 * (no nosso backend, definimos que é opcional, mas você pode mudar)
 */
export async function getProjections(
  clinicId?: number,
  tagIds?: number[],
  createdByType?: string,
  createdByUserId?: number
): Promise<ProjectionsResponse> {
  // Monta query string se quiser
  const qs = new URLSearchParams();
  if (clinicId) qs.append("clinic_id", clinicId.toString());
  if (tagIds && tagIds.length > 0) qs.append("tag_ids", tagIds.join(','));
  appendCreatorMetricsParams(qs, createdByType, createdByUserId);

  const url = `/api/metrics/projections?${qs.toString()}`;

  try {
    const resp = await api.get<ProjectionsResponse>(url);
    return resp.data;
  } catch (error) {
    console.error("[getProjections] Erro ao obter projeções:");
    throw error;
  }
}

/**
 * Chama GET /api/metrics/time_between_stages?clinic_id=...
 */
export async function getTimeBetweenStages(
  clinicId?: number,
  startDate?: string,
  endDate?: string,
  tagIds?: number[],
  createdByType?: string,
  createdByUserId?: number
): Promise<TimeBetweenStagesResponse> {
  const qs = new URLSearchParams();

  if (clinicId) {
    qs.append("clinic_id", clinicId.toString());
  }
  if (startDate) {
    qs.append("start_date", startDate);
  }
  if (endDate) {
    qs.append("end_date", endDate);
  }
  if (tagIds && tagIds.length > 0) {
    qs.append("tag_ids", tagIds.join(','));
  }
  appendCreatorMetricsParams(qs, createdByType, createdByUserId);

  const url = `/api/metrics/time_between_stages?${qs.toString()}`;

  try {
    const resp = await api.get<TimeBetweenStagesResponse>(url);
    return resp.data;
  } catch (error) {
    console.error("[getTimeBetweenStages] Erro ao obter tempos médios:");
    throw error;
  }
}

/**
 * Cria uma nova configuração de IA para horários de resposta.
 * POST /api/ai-windows
 * Precisamos enviar {clinic_id, timezone, time_windows} no body.
 * Também podemos passar clinic_id/client_id via query param se o backend quiser.
 */
export async function createAIWindow(
  payload: AIResponseWindowsCreate
): Promise<{ id: number; message: string }> {
  // 1) Lê do localStorage
  const rawOwnClientId = localStorage.getItem('client_id');
  const clinicId = localStorage.getItem('clinic_id');

  if (!clinicId) {
    throw new Error("Clinic ID não encontrado no localStorage.");
  }

  // Se seu backend precisa de client_id, definimos:
  // Para usuários staff (user), sempre usar o próprio client_id
  // O backend já trata as permissões corretamente
  let finalClientId = rawOwnClientId;

  // 2) Forçamos no body a usar o clinic_id do localStorage
  payload.clinic_id = Number(clinicId);

  try {
    // 3) Chamamos a rota /api/ai-windows (POST).
    //    Caso o backend exija client_id e clinic_id via query param,
    //    podemos adicionar `params: { client_id: finalClientId, clinic_id }`.
    const resp = await api.post('/api/ai-windows', payload, {
      params: {
        client_id: finalClientId,
        clinic_id: clinicId,
      },
    });
    return resp.data as { id: number; message: string };
  } catch (error) {
    console.error("[createAIWindow] Erro ao criar config IA:");
    handleApiError(error, 'Erro ao criar configuração de IA');
    throw new Error('Unreachable');
  }
}

/**
 * Busca a configuração de janelas de resposta pelo clinic_id (via localStorage).
 * GET /api/ai-windows/{clinic_id}
 */
export async function getAIWindow(clinicId: number): Promise<AIResponseWindowsData | null> {
  // 1) Lê localStorage
  const rawOwnClientId = localStorage.getItem('client_id');
  //const clinicId = localStorage.getItem('clinic_id');

  if (!clinicId) {
    throw new Error("Clinic ID não encontrado no localStorage.");
  }

  // Para usuários staff (user), sempre usar o próprio client_id
  // O backend já trata as permissões corretamente
  let finalClientId = rawOwnClientId;

  try {
    // 2) Fazemos GET /api/ai-windows/{clinicId}, 
    //    e passamos { client_id, clinic_id } como query param se necessário.
    const resp = await api.get(`/api/ai-windows/${clinicId}`, {
      params: {
        client_id: finalClientId,
        clinic_id: clinicId,
      },
      validateStatus: (status) => {
        // se 404 => retorna null
        return (status >= 200 && status < 300) || status === 404;
      },
    });

    if (resp.status === 404) {
      return null; // sem config
    }

    return resp.data as AIResponseWindowsData;
  } catch (error) {
    console.error("[getAIWindow] Erro ao obter config IA:");
    handleApiError(error, 'Erro ao obter configuração de IA');
    throw new Error('Unreachable');
  }
}

/**
 * Atualiza a configuração de janelas de resposta, baseado em ID (que você pegou do GET).
 * PUT /api/ai-windows/{id}
 * O payload deve conter { timezone, time_windows? } e possivelmente clinic_id no body.
 */
export async function updateAIWindow(
  id: number,
  payload: AIResponseWindowsUpdate
): Promise<{ message: string }> {
  const rawOwnClientId = localStorage.getItem('client_id');
  const clinicId = localStorage.getItem('clinic_id');

  if (!clinicId) {
    throw new Error("Clinic ID não encontrado no localStorage.");
  }

  // Para usuários staff (user), sempre usar o próprio client_id
  // O backend já trata as permissões corretamente
  let finalClientId = rawOwnClientId;

  // Se o backend quiser clinic_id no body:
  // (depende do seu design)
  // (ex.: payload["clinic_id"] = Number(clinicId);)

  try {
    const resp = await api.put(`/api/ai-windows/${id}`, payload, {
      params: {
        client_id: finalClientId,
        clinic_id: clinicId,
      },
    });
    return resp.data as { message: string };
  } catch (error) {
    console.error("[updateAIWindow] Erro ao atualizar config IA:");
    handleApiError(error, 'Erro ao atualizar configuração de IA');
    throw new Error('Unreachable');
  }
}

// api.ts (trecho simplificado)

// IMPORTS e variáveis de contexto omitidos...

/**
 * Deleta a configuração de horários de IA, identificada pelo ID.
 * DELETE /api/ai-windows/{id}
 */
export async function deleteAIWindow(id: number): Promise<{ message: string }> {
  // 1) Obter dados do localStorage (para contexto de client_id, clinic_id, etc. - se for preciso)
  const rawOwnClientId = localStorage.getItem('client_id');
  const clinicId = localStorage.getItem('clinic_id');

  if (!clinicId) {
    throw new Error('Clinic ID não encontrado no localStorage.');
  }

  // Caso precise decidir qual client_id usar:
  // Para usuários staff (user), sempre usar o próprio client_id
  // O backend já trata as permissões corretamente
  let finalClientId = rawOwnClientId;

  // 2) Chamar endpoint DELETE /api/ai-windows/{id}, passando query params se necessários
  try {
    const resp = await api.delete(`/api/ai-windows/${id}`, {
      params: {
        client_id: finalClientId,
        clinic_id: clinicId
      }
    });
    // O backend retorna { message: "Configuração deletada com sucesso." }
    return resp.data as { message: string };
  } catch (error) {
    console.error("[deleteAIWindow] Erro ao deletar config IA:");
    handleApiError(error, 'Erro ao deletar configuração de IA');
    throw new Error('Unreachable');
  }
}

function getDefaultTimeWindows() {
  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const defaultPeriod = {
    enabled: false,
    start: "08:00",
    end: "12:00",
  };
  const defaultDay = {
    morning: { ...defaultPeriod },
    afternoon: { ...defaultPeriod, start: "13:00", end: "18:00" },
    night: { ...defaultPeriod, start: "18:00", end: "22:00" },
    dawn: { ...defaultPeriod, start: "22:00", end: "06:00" },
  };

  const result: Record<string, typeof defaultDay> = {};
  for (const day of days) {
    // Clona para evitar referências compartilhadas
    result[day] = JSON.parse(JSON.stringify(defaultDay));
  }

  return result;
}

function unifyTimeWindows(
  fromBackend?: Record<string, any>
): Record<string, any> {
  const def = getDefaultTimeWindows();
  if (!fromBackend) return def;

  // Percorre cada dia do default
  for (const day of Object.keys(def)) {
    // Se não existe day no backend, adiciona
    if (!fromBackend[day]) {
      fromBackend[day] = def[day];
    } else {
      // Verifica cada período
      for (const period of ["morning", "afternoon", "night", "dawn"]) {
        if (!fromBackend[day][period]) {
          fromBackend[day][period] = (def as any)[day][period];
        }
      }
    }
  }
  return fromBackend;
}

/**
 * Obtém mensagens paginadas de um contato específico.
 * Suporta paginação por ID ou timestamp.
 */
export async function getPagedMessages(
  contact_phone: string,
  limit: number = 30,
  before_id?: string,
  before_timestamp?: number
): Promise<PagedMessagesResponse> {
  try {
    // Construir a query string
    const contactId = conversationContactId(contact_phone);
    const identityParam = contactId ? `contact_id=${contactId}` : `contact_phone=${encodeURIComponent(contact_phone)}`;
    let url = `/api/chat-optimized/messages/paged?${identityParam}&limit=${limit}`;

    if (before_id) {
      url += `&before_id=${encodeURIComponent(before_id)}`;
    } else if (before_timestamp) {
      url += `&before_timestamp=${before_timestamp}`;
    }

    const resp = await api.get(url);

    // Verificar se a resposta tem a estrutura esperada
    if (!resp.data || !resp.data.messages) {
      console.error("Resposta inválida da API:");
      // Retornar uma resposta padrão vazia para evitar erros
      return {
        messages: [],
        pagination: {
          totalCount: 0,
          hasMore: false
        }
      };
    }

    // Processar as mensagens para o formato esperado pelo frontend
    const processedMessages = resp.data.messages.map((m: any) => {
      const messageDate = new Date(m.timestamp);
      return {
        id: m.id.toString(),
        type: m.type,
        content: m.content,
        sender: {
          phone: m.sender.phone,
          name: m.sender.name,
          photo: m.sender.photo || '',
        },
        timestamp: messageDate.toLocaleTimeString(),
        timestampNumber: m.timestampNumber || messageDate.getTime(),
        fromMe: m.fromMe,
        sequenceNumber: m.sequenceNumber,
        source_channel: m.source_channel,
        status: m.status,
        ack: m.ack,
        templateSnapshot: m.templateSnapshot,
        deliveryError: m.deliveryError,
        deliveryStatusAt: m.deliveryStatusAt,
        providerMessageId: m.providerMessageId || m.messageId || null,
        replyTo: m.replyTo || null,
        reactions: m.reactions || [],
        author_type: m.author_type || null,
        author_id: m.author_id ?? null,
        author_name: m.author_name ?? null,
        author_evidence: m.author_evidence ?? null,
        agent_run_id: m.agent_run_id ?? null,
        learning_action: m.learning_action,
        databaseMessageId: Number.isInteger(Number(m.id)) && Number(m.id) > 0 ? Number(m.id) : null
      } as OptimizedMessage;
    });

    return {
      messages: processedMessages,
      pagination: resp.data.pagination || { totalCount: processedMessages.length, hasMore: false }
    };
  } catch (error) {
    console.error("Erro ao obter mensagens paginadas:");
    // Retornamos um objeto vazio mas válido em caso de erro
    return {
      messages: [],
      pagination: {
        totalCount: 0,
        hasMore: false
      }
    };
  }
}

/**
 * Classe WebSocketManager que gerencia uma única conexão WebSocket
 * com subscrição a múltiplos tópicos (contatos).
 */
export class UnifiedWebSocketManager {
  private ws: WebSocket | null = null;
  private isConnecting: boolean = false;
  private topics: Set<string> = new Set();
  private topicReferenceCounts: Map<string, number> = new Map();
  private messageHandlers: Map<string, ((message: any) => void)[]> = new Map();
  private connectionStatusHandlers: ((connected: boolean) => void)[] = [];
  private reconnectTimer: any = null;
  private reconnectAttempts: number = 0;
  private intentionalDisconnect: boolean = false;
  private shouldResumeAfterSessionRotation: boolean = false;

  /**
   * Obtém a URL do WebSocket unificado.
   */
  private getWebSocketUrl(topics: Iterable<string> = this.topics): string {
    const rawClinicId = localStorage.getItem('clinic_id');
    if (!rawClinicId) {
      throw new Error('clinic_id ausente no localStorage. Não é possível prosseguir.');
    }

    const clinicId = parseInt(rawClinicId, 10);
    if (!clinicId) {
      throw new Error(`clinic_id inválido (${rawClinicId}).`);
    }

    // Junta os tópicos em uma string separada por vírgula
    const topicsStr = Array.from(topics).join(',');

    // Decide se será wss:// ou ws:// com base em API_URL
    const protocol = API_URL.startsWith('https') ? 'wss' : 'ws';
    const domain = API_URL.replace(/^https?:\/\//, ''); // remove "http://" ou "https://"

    // Monta a URL final com query params
    return (
      `${protocol}://${domain}/api/chat-optimized/ws/unified` +
      `?clinic_id=${encodeURIComponent(String(clinicId))}` +
      `&topics=${encodeURIComponent(topicsStr)}`
    );
  }

  // Adicione este método na classe UnifiedWebSocketManager
  public broadcastMessage(message: any): void {
    if (message && message.phone) {
      // Cria um evento personalizado com os dados da mensagem
      const updateEvent = new CustomEvent('unified-message-received', {
        detail: {
          phone: message.phone,
          type: message.type,
          content: message.content,
          timestamp: Date.now(),
          senderName: message.senderName,
          fromMe: message.fromMe,
          author_type: message.author_type,
          author_id: message.author_id,
          author_name: message.author_name,
          author_evidence: message.author_evidence,
          agent_run_id: message.agent_run_id,
          learning_action: message.learning_action
        }
      });

      // Dispara o evento para qualquer componente que esteja ouvindo
      window.dispatchEvent(updateEvent);
    }
  }

  /**
   * Conecta ao WebSocket, se ainda não estiver conectado.
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.intentionalDisconnect = false;
    this.isConnecting = true;

    try {
      const topicsAtConnect = new Set(this.topics);
      const wsUrl = this.getWebSocketUrl(topicsAtConnect);
      console.log("[UnifiedWS] Iniciando conexão autenticada");

      const socket = new WebSocket(wsUrl);
      this.ws = socket;

      socket.onopen = () => {
        if (this.ws !== socket) {
          return;
        }
        console.log("[UnifiedWS] Conexão estabelecida");
        this.isConnecting = false;
        this.reconnectAttempts = 0;

        const topicsAddedWhileConnecting = Array.from(this.topics).filter(
          topic => !topicsAtConnect.has(topic),
        );
        const topicsRemovedWhileConnecting = Array.from(topicsAtConnect).filter(
          topic => !this.topics.has(topic),
        );

        if (topicsRemovedWhileConnecting.length > 0) {
          socket.send(JSON.stringify({
            type: 'unsubscribe',
            topics: topicsRemovedWhileConnecting,
          }));
        }
        if (topicsAddedWhileConnecting.length > 0) {
          socket.send(JSON.stringify({
            type: 'subscribe',
            topics: topicsAddedWhileConnecting,
          }));
        }

        this.notifyConnectionStatus(true);
      };

      socket.onmessage = (event) => {
        if (this.ws !== socket) {
          return;
        }
        try {
          const data = JSON.parse(event.data);
          console.log("[UnifiedWS] Mensagem recebida:");

          // Se for mensagem de conexão estabelecida, ignoramos
          if (data.type === 'connection_established') {
            return;
          }

          // Processar a mensagem recebida
          this.processMessage(data);
        } catch (error) {
          console.error("[UnifiedWS] Erro ao processar mensagem:");
        }
      };

      socket.onclose = (event) => {
        if (this.intentionalDisconnect && this.ws !== socket) {
          return;
        }
        if (this.ws && this.ws !== socket) {
          return;
        }
        console.warn("[UnifiedWS] Conexão fechada:");
        this.isConnecting = false;
        this.ws = null;
        this.notifyConnectionStatus(false);

        if (!this.intentionalDisconnect) {
          this.scheduleReconnect();
        }
      };

      socket.onerror = (error) => {
        if (this.ws !== socket) {
          return;
        }
        console.error("[UnifiedWS] Erro na conexão:");
        this.isConnecting = false;
      };
    } catch (error) {
      console.error("[UnifiedWS] Erro ao iniciar conexão:");
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Agenda uma tentativa de reconexão com backoff exponencial.
   */
  private scheduleReconnect(): void {
    if (this.intentionalDisconnect) {
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    // Backoff exponencial: 1s, 2s, 4s, 8s, 16s, max 30s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);


    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.intentionalDisconnect) {
        return;
      }
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  /**
   * Inscreve-se em um novo tópico (contato).
   */
  subscribe(topic: string): void {
    const currentReferenceCount = this.topicReferenceCounts.get(topic) ?? 0;
    this.topicReferenceCounts.set(topic, currentReferenceCount + 1);

    if (currentReferenceCount > 0) {
      return;
    }

    this.topics.add(topic);

    // Se já estiver conectado, envia comando de subscribe
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'subscribe',
        topics: [topic]
      }));
    } else {
      // Se não está conectado, tenta conectar com o novo tópico
      this.connect();
    }
  }

  /**
   * Cancela a inscrição em um tópico (contato).
   */
  unsubscribe(topic: string): void {
    const currentReferenceCount = this.topicReferenceCounts.get(topic) ?? 0;

    if (currentReferenceCount === 0) {
      return;
    }

    if (currentReferenceCount > 1) {
      this.topicReferenceCounts.set(topic, currentReferenceCount - 1);
      return;
    }

    this.topicReferenceCounts.delete(topic);
    this.topics.delete(topic);

    // Se estiver conectado, envia comando de unsubscribe
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'unsubscribe',
        topics: [topic]
      }));
    }
  }

  /**
   * Fecha a conexão de forma limpa.
   */
  disconnect(): void {
    this.intentionalDisconnect = true;
    this.shouldResumeAfterSessionRotation = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const socket = this.ws;
    this.ws = null;

    if (socket) {
      try {
        socket.close(1000, 'Desconexão solicitada pelo cliente');
      } catch (error) {
        console.error("[UnifiedWS] Erro ao fechar conexão:");
      }
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.notifyConnectionStatus(false);
  }

  suspendForSessionRotation(): void {
    const shouldResume = Boolean(this.ws || this.isConnecting || this.reconnectTimer);
    this.disconnect();
    this.shouldResumeAfterSessionRotation = shouldResume;
  }

  resumeAfterSessionRotation(): void {
    const shouldResume = this.shouldResumeAfterSessionRotation;
    this.shouldResumeAfterSessionRotation = false;
    if (shouldResume) {
      this.connect();
    }
  }

  reconnectAfterSessionRotation(): void {
    this.suspendForSessionRotation();
    this.resumeAfterSessionRotation();
  }

  // Adicione este método após o método broadcastMessage na classe UnifiedWebSocketManager
  public notifyGlobalListeners(data: any): void {
    // Notifica os handlers do tópico '*' (handlers globais)
    const globalHandlers = this.messageHandlers.get('*');
    if (globalHandlers) {
      globalHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error("[UnifiedWS] Erro no handler global:");
        }
      });
    }

    // Dispara um evento customizado para qualquer componente escutando
    if (data && data.phone) {
      const updateEvent = new CustomEvent('unified-message-received', {
        detail: data
      });
      window.dispatchEvent(updateEvent);
    }
  }

  // 2. MODIFICAR O MÉTODO processMessage NA CLASSE UnifiedWebSocketManager

  private processMessage(data: any): void {
    // Pegamos o tópico da mensagem (phone do contato)
    const topic = conversationEventKey(data) || '__global__';

    // Notificamos os handlers específicos do tópico
    const topicHandlers = this.messageHandlers.get(topic);
    if (topicHandlers) {
      topicHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {

        }
      });
    }

    // Sempre notificar os handlers globais e disparar evento window, 
    // independente do tópico específico
    this.notifyGlobalListeners(data);
  }

  /**
   * Registra um handler para mensagens de um tópico específico.
   * Use '*' para receber todas as mensagens.
   */
  onMessage(topic: string, handler: (message: any) => void): () => void {
    if (!this.messageHandlers.has(topic)) {
      this.messageHandlers.set(topic, []);
    }

    this.messageHandlers.get(topic)!.push(handler);

    // Retorna uma função para remover este handler
    return () => {
      const handlers = this.messageHandlers.get(topic);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index !== -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  /**
   * Registra um handler para notificações de status de conexão.
   */
  onConnectionStatus(handler: (connected: boolean) => void): () => void {
    this.connectionStatusHandlers.push(handler);

    // Notifica imediatamente o status atual
    if (this.ws?.readyState === WebSocket.OPEN) {
      handler(true);
    } else {
      handler(false);
    }

    // Retorna uma função para remover este handler
    return () => {
      const index = this.connectionStatusHandlers.indexOf(handler);
      if (index !== -1) {
        this.connectionStatusHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Notifica os handlers de status de conexão.
   */
  private notifyConnectionStatus(connected: boolean): void {
    this.connectionStatusHandlers.forEach(handler => {
      try {
        handler(connected);
      } catch (error) {
        console.error("[UnifiedWS] Erro no handler de status de conexão:");
      }
    });
  }

  /**
   * Verifica se está conectado.
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Criar uma instância singleton do gerenciador
export const unifiedWebSocketManager = new UnifiedWebSocketManager();

/**
 * Hook para gerenciar o cache de mensagens para cada contato.
 * Implementa uma estratégia de LRU (Least Recently Used).
 */
export class MessageCacheManager {
  private static readonly MAX_CACHE_AGE_MS = 30 * 60 * 1000;
  private static readonly MAX_CACHE_ENTRIES = 20;
  private static readonly MAX_MESSAGES_PER_CONTACT = 200;
  private static readonly MAX_CACHE_CONTENT_CHARS = 250000;
  private cache = new Map<string, { messages: OptimizedMessage[]; timestamp: number }>();
  private lruKeys: string[] = [];
  private activeRuntimeIdentity: string | null = null;

  private static sanitizeMessageForCache(message: OptimizedMessage): OptimizedMessage {
    const content = message.content as any;

    if (typeof content === 'string') {
      if (content.length <= MessageCacheManager.MAX_CACHE_CONTENT_CHARS) {
        return message;
      }

      if (message.type === 'audio' || message.type === 'video' || message.type === 'image') {
        return {
          ...message,
          content: {
            url: '',
            fallbackText: message.type === 'audio' ? 'Audio' : message.type === 'video' ? 'Video' : 'Imagem',
            error: 'Conteudo de midia omitido do cache em memoria',
            needsLoading: false,
          } as any,
        };
      }

      return {
        ...message,
        content: content.slice(0, MessageCacheManager.MAX_CACHE_CONTENT_CHARS),
      };
    }

    if (content && typeof content === 'object') {
      const nextContent = { ...content };
      ['url', 'mediaPath', 'audioUrl', 'videoUrl', 'imageUrl', 'data', 'base64'].forEach((field) => {
        if (
          typeof nextContent[field] === 'string' &&
          nextContent[field].length > MessageCacheManager.MAX_CACHE_CONTENT_CHARS
        ) {
          nextContent[field] = '';
          nextContent.error = 'Conteudo de midia omitido do cache em memoria';
          nextContent.needsLoading = false;
        }
      });

      return {
        ...message,
        content: nextContent,
      };
    }

    return message;
  }

  private static cloneMessageForCache(message: OptimizedMessage): OptimizedMessage {
    const sanitized = MessageCacheManager.sanitizeMessageForCache(message);
    const content = sanitized.content;

    return {
      ...sanitized,
      sender: { ...sanitized.sender },
      content: content && typeof content === 'object' ? { ...content } : content,
      replyTo: sanitized.replyTo ? { ...sanitized.replyTo } : sanitized.replyTo,
      reactions: sanitized.reactions?.map((reaction) => ({ ...reaction })),
      templateSnapshot: sanitized.templateSnapshot ? JSON.parse(JSON.stringify(sanitized.templateSnapshot)) : sanitized.templateSnapshot,
      deliveryError: sanitized.deliveryError ? { ...sanitized.deliveryError } : sanitized.deliveryError,
    };
  }

  private resolveScope(): { cachePrefix: string; runtimeIdentity: string } | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const clientId = localStorage.getItem('client_id');
    const clinicId = localStorage.getItem('clinic_id');
    if (!clientId || !clinicId) {
      return null;
    }

    const runtimeIdentity = [
      localStorage.getItem('user_type') || '',
      localStorage.getItem('user_id') || '',
      clientId,
      clinicId,
    ].join(':');

    if (this.activeRuntimeIdentity && this.activeRuntimeIdentity !== runtimeIdentity) {
      this.cache.clear();
      this.lruKeys = [];
    }
    this.activeRuntimeIdentity = runtimeIdentity;

    return {
      cachePrefix: `${clientId}:${clinicId}`,
      runtimeIdentity,
    };
  }

  private getCacheKey(contactPhone: string): string | null {
    const scope = this.resolveScope();
    const normalizedPhone = String(contactPhone || '').trim();
    if (!scope || !normalizedPhone) {
      return null;
    }

    return `${scope.cachePrefix}:${normalizedPhone}`;
  }

  private touch(cacheKey: string): void {
    this.lruKeys = [
      cacheKey,
      ...this.lruKeys.filter((existingKey) => existingKey !== cacheKey),
    ];
  }

  private cleanupCache(): void {
    if (this.lruKeys.length <= MessageCacheManager.MAX_CACHE_ENTRIES) {
      return;
    }

    const keysToRemove = this.lruKeys.slice(MessageCacheManager.MAX_CACHE_ENTRIES);
    keysToRemove.forEach((cacheKey) => this.cache.delete(cacheKey));
    this.lruKeys = this.lruKeys.slice(0, MessageCacheManager.MAX_CACHE_ENTRIES);
  }

  saveMessages(contactPhone: string, messages: OptimizedMessage[]): void {
    const cacheKey = this.getCacheKey(contactPhone);
    if (!cacheKey) {
      return;
    }

    const messagesToSave = messages
      .slice(-MessageCacheManager.MAX_MESSAGES_PER_CONTACT)
      .map(MessageCacheManager.cloneMessageForCache);

    this.cache.set(cacheKey, {
      messages: messagesToSave,
      timestamp: Date.now(),
    });
    this.touch(cacheKey);
    this.cleanupCache();
  }

  getMessages(contactPhone: string): OptimizedMessage[] | null {
    const cacheKey = this.getCacheKey(contactPhone);
    if (!cacheKey) {
      return null;
    }

    const entry = this.cache.get(cacheKey);
    if (!entry) {
      return null;
    }

    if (Date.now() - entry.timestamp > MessageCacheManager.MAX_CACHE_AGE_MS) {
      this.cache.delete(cacheKey);
      this.lruKeys = this.lruKeys.filter((existingKey) => existingKey !== cacheKey);
      return null;
    }

    const validatedMessages = entry.messages.filter((message) => (
      message.fromMe ||
      !message.sender?.phone ||
      message.sender.phone === contactPhone
    ));

    if (validatedMessages.length !== entry.messages.length) {
      this.cache.set(cacheKey, {
        messages: validatedMessages.map(MessageCacheManager.cloneMessageForCache),
        timestamp: entry.timestamp,
      });
    }

    this.touch(cacheKey);
    return validatedMessages.map(MessageCacheManager.cloneMessageForCache);
  }

  clearAllCorruptedCaches(): void {
    this.clearAllCache();
    purgeLegacyMessageCacheStorage();
  }

  updateMessage(contactPhone: string, message: OptimizedMessage): void {
    const messages = this.getMessages(contactPhone) || [];
    const index = messages.findIndex((cachedMessage) => cachedMessage.id === message.id);

    if (index >= 0) {
      messages[index] = message;
    } else {
      messages.push(message);
      messages.sort((first, second) => {
        if (first.sequenceNumber !== undefined && second.sequenceNumber !== undefined) {
          return first.sequenceNumber - second.sequenceNumber;
        }
        return (first.timestampNumber || 0) - (second.timestampNumber || 0);
      });
    }

    this.saveMessages(contactPhone, messages);
  }

  removeFromCache(contactPhone: string): void {
    const cacheKey = this.getCacheKey(contactPhone);
    if (!cacheKey) {
      return;
    }

    this.cache.delete(cacheKey);
    this.lruKeys = this.lruKeys.filter((existingKey) => existingKey !== cacheKey);
  }

  clearAllCache(): void {
    this.cache.clear();
    this.lruKeys = [];
    this.activeRuntimeIdentity = null;
  }
}

// Criar uma instância singleton do gerenciador de cache
export const messageCacheManager = new MessageCacheManager();

export async function prepareAudioForZAPI(audioBlob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    try {


      const reader = new FileReader();

      reader.onloadend = () => {
        if (!reader.result) {
          reject(new Error('Falha ao converter áudio para base64'));
          return;
        }

        // Obter o resultado como string
        const base64Data = reader.result.toString();

        // Se o resultado já tiver um prefixo data:, verificar e corrigir se necessário
        if (base64Data.startsWith('data:')) {
          // Extrair partes do data URL
          const [prefix, data] = base64Data.split(',');

          // Determinar se precisamos mudar o tipo MIME para audio/ogg
          if (!prefix.includes('audio/ogg') && !prefix.includes('audio/mpeg')) {
            // Z-API prefere audio/ogg para áudios
            console.log("Corrigindo prefixo MIME para audio/ogg");
            resolve(`data:audio/ogg;base64,${data}`);
          } else {
            // O prefixo já é audio/ogg ou audio/mpeg, manter o original
            console.log("Prefixo MIME já está correto:");
            resolve(base64Data);
          }
        } else {
          // Não tem prefixo, adicionar prefix audio/ogg
          console.log("Adicionando prefixo audio/ogg ao base64");
          resolve(`data:audio/ogg;base64,${base64Data}`);
        }
      };

      reader.onerror = (error) => {
        console.error("Erro ao ler arquivo de áudio:");
        reject(new Error('Falha ao ler o arquivo de áudio'));
      };

      // Iniciar a leitura como data URL (base64)
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error("Erro ao preparar áudio para Z-API:");
      reject(error);
    }
  });
}

// ===============================
// INTERFACES E FUNÇÕES PÓS-CONSULTA
// ===============================

// Interfaces para mensagens pós-consulta
export interface PosConsultaMessageUpdate {
  id?: number;
  type: 'text' | 'image' | 'audio' | 'video' | 'nps';
  content: string;
}

export interface PosConsultaStepUpdate {
  id?: number;
  step_number: number;
  send_after: number;
  send_after_unit: 'days' | 'hours' | 'minutes';
  messages: PosConsultaMessageUpdate[];
}

export interface PosConsultaMessageCreate {
  type: 'text' | 'image' | 'audio' | 'video' | 'nps';
  content: string;
}

export interface PosConsultaStepCreate {
  step_number: number;
  send_after: number;
  send_after_unit: 'days' | 'hours' | 'minutes';
  messages: PosConsultaMessageCreate[];
}

export interface PosConsultaFollowUpSequenceCreate {
  clinic_id: number;
  name: string;
  description?: string;
  steps: PosConsultaStepCreate[];
}

export interface PosConsultaFollowUpSequenceUpdate {
  clinic_id: number;
  name: string;
  description?: string;
  steps: PosConsultaStepUpdate[];
}

export interface PosConsultaFollowUpSequenceResponse {
  id: number;
  name: string;
  description?: string;
  active: boolean;
}

export interface PosConsultaFollowUpSequenceDetail {
  id: number;
  clinic_id: number;
  name: string;
  description?: string;
  active: boolean;
  steps: Array<{
    id: number;
    step_number: number;
    send_after: number;
    send_after_unit: 'days' | 'hours' | 'minutes';
    messages: Array<{
      id: number;
      type: 'text' | 'image' | 'audio' | 'video' | 'nps';
      content: string;
    }>;
  }>;
}

// Interfaces para configuração de horários pós-consulta
export interface DailyRangePosConsulta {
  enabled: boolean;
  start: string; // formato "HH:mm"
  end: string;   // formato "HH:mm"
}

export interface PosConsultaScheduleData {
  [key: string]: DailyRangePosConsulta;
}

export interface PosConsultaScheduleCreate {
  schedule_data: PosConsultaScheduleData;
}

export interface PosConsultaScheduleUpdate {
  schedule_data: PosConsultaScheduleData;
}

export interface PosConsultaScheduleConfig {
  id: number;
  clinic_id: number;
  pos_consulta_sequence_id: number;
  schedule_data: PosConsultaScheduleData;
}

// ===============================
// FUNÇÕES NPS
// ===============================

export interface SendNPSParams {
  phone: string;
  contact_id?: number;
  clinic_id?: number;
  question?: string;
  campaign_name?: string;
}

export const sendNPS = async (params: SendNPSParams): Promise<any> => {
  const { phone, contact_id, clinic_id, question, campaign_name } = params;

  // Usar query parameters conforme esperado pelo backend
  const searchParams = new URLSearchParams({
    phone,
    question: question || 'Em uma escala de 1 a 5, como você avalia nosso atendimento?',
    campaign_name: campaign_name || 'manual_chat'
  });
  if (contact_id) {
    searchParams.set('contact_id', String(contact_id));
  }
  const scopedClinicId =
    clinic_id || Number(localStorage.getItem('clinic_id') || 0);
  if (scopedClinicId > 0) {
    searchParams.set('clinic_id', String(scopedClinicId));
  }

  const response = await api.post(`/api/nps/send?${searchParams.toString()}`);
  return response.data;
};

// Interface para parâmetros de ligação
export interface SendCallParams {
  phone: string;
  callDuration?: number;
  clinic_id?: number;
  contact_id?: number;
}

// Função para fazer ligação via WhatsApp
export const sendCall = async (params: SendCallParams): Promise<any> => {
  const { phone, callDuration = 10, clinic_id, contact_id } = params;

  // Usar query parameters conforme esperado pelo backend
  const searchParams = new URLSearchParams({
    phone,
    callDuration: callDuration.toString()
  });
  const scopedClinicId =
    clinic_id || Number(localStorage.getItem('clinic_id') || 0);
  if (scopedClinicId > 0) {
    searchParams.set('clinic_id', String(scopedClinicId));
  }
  if (contact_id && contact_id > 0) {
    searchParams.set('contact_id', String(contact_id));
  }

  const response = await api.post(`/api/call/send?${searchParams.toString()}`);
  return response.data;
};

// Função para buscar métricas NPS para o Dashboard
export const getNPSDashboardMetrics = async (
  startDate?: string,
  endDate?: string,
  campaignName?: string
): Promise<NPSDashboardMetrics> => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (campaignName) params.append('campaign_name', campaignName);

    const response = await api.get(`/api/nps/dashboard/metrics?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar métricas NPS:");
    throw error;
  }
};

// ===============================
// FUNÇÕES CRUD PÓS-CONSULTA
// ===============================

/**
 * Cria uma nova sequência de follow-up pós-consulta
 */
export async function createPosConsultaFollowUpSequence(
  clinicId: number,
  payload: PosConsultaFollowUpSequenceCreate
): Promise<PosConsultaFollowUpSequenceResponse> {
  try {
    const resp = await api.post<PosConsultaFollowUpSequenceResponse>(
      `/api/pos-consulta-followups/${clinicId}`,
      payload
    );
    console.log("[createPosConsultaFollowUpSequence] Sucesso:");
    return resp.data;
  } catch (error) {
    console.error("[createPosConsultaFollowUpSequence] Erro:");
    throw error;
  }
}

/**
 * Busca a sequência de follow-up pós-consulta
 */
export async function getPosConsultaFollowUpSequence(
  clinicId: number
): Promise<PosConsultaFollowUpSequenceDetail | null> {
  try {
    const resp = await api.get<PosConsultaFollowUpSequenceDetail>(
      `/api/pos-consulta-followups/${clinicId}`,
      {
        validateStatus: (status) => {
          // Se 404, não tem sequência criada ainda
          return (status >= 200 && status < 300) || status === 404;
        },
      }
    );

    if (resp.status === 404) {
      console.log("[getPosConsultaFollowUpSequence] Nenhuma sequência encontrada.");
      return null;
    }

    console.log("[getPosConsultaFollowUpSequence] Sequência obtida:");
    return resp.data;
  } catch (error) {
    console.error("[getPosConsultaFollowUpSequence] Erro:");
    throw error;
  }
}

/**
 * Atualiza uma sequência de follow-up pós-consulta
 */
export async function updatePosConsultaFollowUpSequence(
  clinicId: number,
  payload: PosConsultaFollowUpSequenceUpdate
): Promise<PosConsultaFollowUpSequenceResponse> {
  try {
    const resp = await api.put<PosConsultaFollowUpSequenceResponse>(
      `/api/pos-consulta-followups/${clinicId}`,
      payload
    );
    console.log("[updatePosConsultaFollowUpSequence] Sucesso:");
    return resp.data;
  } catch (error) {
    console.error("[updatePosConsultaFollowUpSequence] Erro:");
    throw error;
  }
}

/**
 * Deleta uma sequência de follow-up pós-consulta
 */
export async function deletePosConsultaFollowUpSequence(
  clinicId: number
): Promise<void> {
  try {
    await api.delete(`/api/pos-consulta-followups/${clinicId}`);
    console.log("[deletePosConsultaFollowUpSequence] Sequência deletada com sucesso.");
  } catch (error) {
    console.error("[deletePosConsultaFollowUpSequence] Erro:");
    throw error;
  }
}

// ===============================
// FUNÇÕES PARA CONFIGURAÇÃO DE HORÁRIOS PÓS-CONSULTA
// ===============================

/**
 * Cria uma nova configuração de horários para pós-consulta
 */
export async function createPosConsultaScheduleConfig(
  clinicId: number,
  payload: PosConsultaScheduleCreate
): Promise<{ message: string }> {
  try {
    const resp = await api.post(`/api/pos-consulta-followups/${clinicId}/schedule`, payload);
    console.log("[createPosConsultaScheduleConfig] Sucesso:");
    return resp.data;
  } catch (error) {
    console.error("[createPosConsultaScheduleConfig] Erro:");
    throw error;
  }
}

/**
 * Busca a configuração de horários para pós-consulta
 */
export async function getPosConsultaScheduleConfig(
  clinicId: number
): Promise<PosConsultaScheduleConfig | null> {
  try {
    const resp = await api.get<PosConsultaScheduleConfig>(
      `/api/pos-consulta-followups/${clinicId}/schedule`,
      {
        validateStatus: (status) => {
          return (status >= 200 && status < 300) || status === 404;
        },
      }
    );

    if (resp.status === 404) {
      return null;
    }

    return resp.data;
  } catch (error) {
    console.error("[getPosConsultaScheduleConfig] Erro:");
    throw error;
  }
}

/**
 * Atualiza a configuração de horários para pós-consulta
 */
export async function updatePosConsultaScheduleConfig(
  clinicId: number,
  payload: PosConsultaScheduleUpdate
): Promise<{ message: string }> {
  try {
    const resp = await api.put(`/api/pos-consulta-followups/${clinicId}/schedule`, payload);
    console.log("[updatePosConsultaScheduleConfig] Sucesso:");
    return resp.data;
  } catch (error) {
    console.error("[updatePosConsultaScheduleConfig] Erro:");
    throw error;
  }
}

/**
 * Deleta a configuração de horários para pós-consulta
 */
export async function deletePosConsultaScheduleConfig(
  clinicId: number
): Promise<{ message: string }> {
  try {
    const resp = await api.delete(`/api/pos-consulta-followups/${clinicId}/schedule`);
    console.log("[deletePosConsultaScheduleConfig] Sucesso:");
    return resp.data;
  } catch (error) {
    console.error("[deletePosConsultaScheduleConfig] Erro:");
    throw error;
  }
}

// ===============================
// INTERFACES PÓS-VENDA
// ===============================

export interface PosVendaMessageCreate {
  type: 'text' | 'image' | 'audio' | 'video' | 'nps';
  content: string;
}

export interface PosVendaStepCreate {
  step_number: number;
  send_after: number;
  send_after_unit: 'days' | 'hours' | 'minutes';
  messages: PosVendaMessageCreate[];
}

export interface PosVendaMessageUpdate {
  id?: number;
  type: 'text' | 'image' | 'audio' | 'video' | 'nps';
  content: string;
}

export interface PosVendaStepUpdate {
  id?: number;
  step_number: number;
  send_after: number;
  send_after_unit: 'days' | 'hours' | 'minutes';
  messages: PosVendaMessageUpdate[];
}

export interface PosVendaFollowUpSequenceCreate {
  clinic_id: number;
  name: string;
  description: string;
  steps: PosVendaStepCreate[];
}

export interface PosVendaFollowUpSequenceUpdate {
  clinic_id: number;
  name: string;
  description: string;
  steps: PosVendaStepUpdate[];
}

export interface PosVendaFollowUpSequenceResponse {
  message: string;
  sequence_id: number;
}

export interface PosVendaFollowUpSequenceDetail {
  id: number;
  clinic_id: number;
  name: string;
  description: string;
  active: boolean;
  steps: Array<{
    id: number;
    step_number: number;
    send_after: number;
    send_after_unit: 'days' | 'hours' | 'minutes';
    messages: Array<{
      id: number;
      type: 'text' | 'image' | 'audio' | 'video' | 'nps';
      content: string;
    }>;
  }>;
}

// Interfaces para configuração de horários pós-venda
export interface DailyRangePosVenda {
  enabled: boolean;
  start: string; // formato "HH:mm"
  end: string;   // formato "HH:mm"
}

export interface PosVendaScheduleData {
  [key: string]: DailyRangePosVenda;
}

export interface PosVendaScheduleCreate {
  schedule_data: PosVendaScheduleData;
}

export interface PosVendaScheduleUpdate {
  schedule_data: PosVendaScheduleData;
}

export interface PosVendaScheduleConfig {
  id: number;
  clinic_id: number;
  pos_venda_sequence_id: number;
  schedule_data: PosVendaScheduleData;
}

// ===============================
// FUNÇÕES CRUD PÓS-VENDA
// ===============================

/**
 * Cria uma nova sequência de follow-up pós-venda
 */
export async function createPosVendaFollowUpSequence(
  clinicId: number,
  payload: PosVendaFollowUpSequenceCreate
): Promise<PosVendaFollowUpSequenceResponse> {
  try {
    const resp = await api.post<PosVendaFollowUpSequenceResponse>(
      `/api/pos-venda-followups/${clinicId}`,
      payload
    );
    console.log("[createPosVendaFollowUpSequence] Sucesso:");
    return resp.data;
  } catch (error) {
    console.error("[createPosVendaFollowUpSequence] Erro:");
    throw error;
  }
}

/**
 * Busca a sequência de follow-up pós-venda
 */
export async function getPosVendaFollowUpSequence(
  clinicId: number
): Promise<PosVendaFollowUpSequenceDetail | null> {
  try {
    const resp = await api.get<PosVendaFollowUpSequenceDetail>(
      `/api/pos-venda-followups/${clinicId}`,
      {
        validateStatus: (status) => {
          // Se 404, não tem sequência criada ainda
          return (status >= 200 && status < 300) || status === 404;
        },
      }
    );

    if (resp.status === 404) {
      console.log("[getPosVendaFollowUpSequence] Nenhuma sequência encontrada.");
      return null;
    }

    console.log("[getPosVendaFollowUpSequence] Sequência obtida:");
    return resp.data;
  } catch (error) {
    console.error("[getPosVendaFollowUpSequence] Erro:");
    throw error;
  }
}

/**
 * Atualiza uma sequência de follow-up pós-venda
 */
export async function updatePosVendaFollowUpSequence(
  clinicId: number,
  payload: PosVendaFollowUpSequenceUpdate
): Promise<PosVendaFollowUpSequenceResponse> {
  try {
    const resp = await api.put<PosVendaFollowUpSequenceResponse>(
      `/api/pos-venda-followups/${clinicId}`,
      payload
    );
    console.log("[updatePosVendaFollowUpSequence] Sucesso:");
    return resp.data;
  } catch (error) {
    console.error("[updatePosVendaFollowUpSequence] Erro:");
    throw error;
  }
}

/**
 * Deleta uma sequência de follow-up pós-venda
 */
export async function deletePosVendaFollowUpSequence(
  clinicId: number
): Promise<{ message: string }> {
  try {
    const resp = await api.delete<{ message: string }>(
      `/api/pos-venda-followups/${clinicId}`
    );
    console.log("[deletePosVendaFollowUpSequence] Sequência deletada com sucesso.");
    return resp.data;
  } catch (error) {
    console.error("[deletePosVendaFollowUpSequence] Erro:");
    throw error;
  }
}

// ===============================
// FUNÇÕES PARA CONFIGURAÇÃO DE HORÁRIOS PÓS-VENDA
// ===============================

/**
 * Cria uma nova configuração de horários para pós-venda
 */
export async function createPosVendaScheduleConfig(
  clinicId: number,
  payload: PosVendaScheduleCreate
): Promise<{ message: string; config_id: number }> {
  try {
    const resp = await api.post(`/api/pos-venda-followups/${clinicId}/schedule`, payload);
    console.log("[createPosVendaScheduleConfig] Sucesso:");
    return resp.data;
  } catch (error) {
    console.error("[createPosVendaScheduleConfig] Erro:");
    throw error;
  }
}

/**
 * Busca a configuração de horários para pós-venda
 */
export async function getPosVendaScheduleConfig(
  clinicId: number
): Promise<PosVendaScheduleConfig | null> {
  try {
    const resp = await api.get<PosVendaScheduleConfig>(
      `/api/pos-venda-followups/${clinicId}/schedule`,
      {
        validateStatus: (status) => {
          return (status >= 200 && status < 300) || status === 404;
        },
      }
    );

    if (resp.status === 404) {
      return null;
    }

    return resp.data;
  } catch (error) {
    console.error("[getPosVendaScheduleConfig] Erro:");
    throw error;
  }
}

/**
 * Atualiza a configuração de horários para pós-venda
 */
export async function updatePosVendaScheduleConfig(
  clinicId: number,
  payload: PosVendaScheduleUpdate
): Promise<{ message: string; config_id: number }> {
  try {
    const resp = await api.put(`/api/pos-venda-followups/${clinicId}/schedule`, payload);
    console.log("[updatePosVendaScheduleConfig] Sucesso:");
    return resp.data;
  } catch (error) {
    console.error("[updatePosVendaScheduleConfig] Erro:");
    throw error;
  }
}

/**
 * Deleta a configuração de horários para pós-venda
 */
export async function deletePosVendaScheduleConfig(
  clinicId: number
): Promise<{ message: string }> {
  try {
    const resp = await api.delete(`/api/pos-venda-followups/${clinicId}/schedule`);
    console.log("[deletePosVendaScheduleConfig] Sucesso:");
    return resp.data;
  } catch (error) {
    console.error("[deletePosVendaScheduleConfig] Erro:");
    throw error;
  }
}

// ================================================================
// NUTRITION CAMPAIGN API FUNCTIONS
// ================================================================

export interface NutritionCampaignSequence {
  id?: number;
  name: string;
  description?: string;
  active: boolean;
  target_contact_status?: string[];
  target_contact_categories?: string[];
  target_contact_tags?: string[];
  message_delay_min: number;
  message_delay_max: number;
  created_at?: string;
  updated_at?: string;
  steps?: NutritionCampaignStep[];
}

export interface NutritionCampaignStep {
  id?: number;
  step_number: number;
  send_after: number;
  send_after_unit: 'minutes' | 'hours' | 'days';
  random_delay_min: number;
  random_delay_max: number;
  created_at?: string;
  updated_at?: string;
  messages?: NutritionCampaignMessage[];
}

export interface NutritionCampaignMessage {
  id?: number;
  type: 'text' | 'image' | 'audio' | 'video' | 'nps';
  content: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Lista todas as sequências de campanha de nutrição
 */
export async function getNutritionCampaignSequences(): Promise<{
  success: boolean;
  sequences: NutritionCampaignSequence[];
}> {
  try {
    const resp = await api.get('/api/nutrition-campaigns/sequences');
    return resp.data;
  } catch (error) {
    console.error("[getNutritionCampaignSequences] Erro:");
    throw error;
  }
}

/**
 * Cria uma nova sequência de campanha de nutrição
 */
export async function createNutritionCampaignSequence(
  sequence: Omit<NutritionCampaignSequence, 'id' | 'created_at' | 'updated_at'>
): Promise<{ success: boolean; message: string; sequence_id: number }> {
  try {
    const resp = await api.post('/api/nutrition-campaigns/sequences', sequence);
    return resp.data;
  } catch (error) {
    console.error("[createNutritionCampaignSequence] Erro:");
    throw error;
  }
}

/**
 * Lança uma campanha de nutrição
 */
export async function launchNutritionCampaign(
  sequenceId?: number
): Promise<{ success: boolean; message: string }> {
  try {
    const resp = await api.post('/api/nutrition-campaigns/launch', {
      sequence_id: sequenceId
    });
    return resp.data;
  } catch (error) {
    console.error("[launchNutritionCampaign] Erro:");
    throw error;
  }
}

/**
 * Obtém estatísticas das campanhas de nutrição
 */
export async function getNutritionCampaignStats(): Promise<{
  success: boolean;
  stats: Array<{
    sequence_name: string;
    total_executions: number;
    successful_executions: number;
    failed_executions: number;
    scheduled_executions: number;
    processing_executions: number;
  }>;
}> {
  try {
    const resp = await api.get('/api/nutrition-campaigns/stats');
    return resp.data;
  } catch (error) {
    console.error("[getNutritionCampaignStats] Erro:");
    throw error;
  }
}

/**
 * Obtém preview dos contatos que serão atingidos pelos critérios de targeting
 */
export async function getNutritionTargetingPreview(criteria: {
  target_contact_status?: string[];
  target_contact_categories?: string[];
  target_contact_tags?: string[];
}): Promise<{
  success: boolean;
  total_contacts: number;
  contacts: Array<{
    id: number;
    name: string;
    phone: string;
    status: string;
    categoria: string;
    tags: string[];
  }>;
}> {
  try {
    const params = new URLSearchParams();

    if (criteria.target_contact_status) {
      criteria.target_contact_status.forEach(status =>
        params.append('target_contact_status', status)
      );
    }

    if (criteria.target_contact_categories) {
      criteria.target_contact_categories.forEach(category =>
        params.append('target_contact_categories', category)
      );
    }

    if (criteria.target_contact_tags) {
      criteria.target_contact_tags.forEach(tag =>
        params.append('target_contact_tags', tag)
      );
    }

    const resp = await api.get(`/api/nutrition-campaigns/targeting/preview?${params}`);
    return resp.data;
  } catch (error) {
    console.error("[getNutritionTargetingPreview] Erro:");
    throw error;
  }
}



/**
 * Salva configuração de horários para uma sequência de campanha de nutrição
 */
export async function saveNutritionCampaignScheduleConfig(
  sequenceId: number,
  scheduleData: any
): Promise<{ success: boolean; message: string }> {
  try {
    const resp = await api.post(`/api/nutrition-campaigns/sequences/${sequenceId}/schedule`, {
      schedule_data: scheduleData
    });
    return resp.data;
  } catch (error) {
    console.error("[saveNutritionCampaignScheduleConfig] Erro:");
    throw error;
  }
}

// ==========================================
// EVOLUTION API - Gestão de Instâncias
// ==========================================

/**
 * Conecta uma clínica ao Evolution API
 * Cria uma nova instância e salva no banco
 */
export async function connectEvolution(instanceName: string, phoneNumber?: string): Promise<{ message: string; instance_name: string }> {
  const payload: { instance_name: string; phone_number?: string } = {
    instance_name: instanceName,
  };

  if (phoneNumber) {
    payload.phone_number = phoneNumber;
  }

  const response = await api.post('/webhook/evolution/connect', payload);
  return response.data;
}

/**
 * Obtém a configuração Evolution da clínica logada
 */
export async function getEvolutionConfig(): Promise<{
  instance_id: string;
}> {
  const response = await api.get('/webhook/evolution/config');
  return {
    instance_id: response.data.instance_id,
  };
}

/**
 * Interface para dados de conexão Evolution (QR Code ou Pairing Code)
 */
export interface EvolutionConnectionData {
  mode: 'qr' | 'pairing';
  qrcode?: string;          // Se mode=qr
  pairingCode?: string;     // Se mode=pairing
  cached?: boolean;
  fresh?: boolean;
  age_seconds?: number;
  fallback?: boolean;       // Se true, Evolution retornou modo diferente do solicitado
}

/**
 * Obtém conexão Evolution (QR Code ou Pairing Code)
 * @param forceRefresh - Se true, força busca de novo código ignorando cache
 * @param mode - 'qr' para QR Code, 'pairing' para Pairing Code
 * @param phoneNumber - Número de telefone com código do país (ex: 5521999887766) - usado para pairing code
 */
export async function getEvolutionConnection(
  forceRefresh: boolean = false,
  mode: 'qr' | 'pairing' = 'qr',
  phoneNumber?: string
): Promise<EvolutionConnectionData> {
  const params: any = { force_refresh: forceRefresh, mode };

  if (mode === 'pairing' && phoneNumber) {
    params.phone_number = phoneNumber;
  }

  const response = await api.post('/webhook/evolution/qrcode', undefined, { params });
  return response.data;
}

/**
 * Obtém o QR Code para conectar o WhatsApp
 * @param forceRefresh - Se true, força busca de novo QR code ignorando cache
 * @deprecated Use getEvolutionConnection() com mode='qr' para mais flexibilidade
 */
export async function getEvolutionQRCode(forceRefresh: boolean = false): Promise<{ qrcode: string }> {
  const data = await getEvolutionConnection(forceRefresh, 'qr');
  return { qrcode: data.qrcode || '' };
}

/**
 * Obtém o status da conexão Evolution
 */
export async function getEvolutionStatus(): Promise<{
  connected: boolean;
  state: string;
}> {
  const response = await api.get('/webhook/evolution/status');
  return response.data;
}

/**
 * Obtém dados do device conectado (WhatsApp)
 */
export async function getEvolutionDevice(): Promise<{
  id: string;
  name: string;
  phone: string;
  imgUrl: string;
  isBusiness: boolean;
  device: {
    sessionName: string;
    device_model: string;
  };
}> {
  const response = await api.get('/webhook/evolution/device');
  return response.data;
}

/**
 * Desconecta a instância Evolution (logout do WhatsApp)
 */
export async function disconnectEvolution(): Promise<{ message: string }> {
  const response = await api.post('/webhook/evolution/disconnect');
  return response.data;
}

/**
 * Reseta completamente a configuração Evolution da clínica
 */
export async function resetEvolution(): Promise<{ message: string }> {
  const response = await api.post('/webhook/evolution/reset');
  return response.data;
}

// ==========================================
// WPPCONNECT - Gestão de Sessões
// ==========================================

/**
 * Conecta uma clínica ao WPPConnect
 * Cria uma nova sessão e salva no banco
 */
export async function connectWPPConnect(sessionName: string): Promise<{
  message: string;
  session_name: string;
  status: string;
}> {
  const payload = {
    session_name: sessionName,
  };

  const response = await api.post('/webhook/wppconnect/connect', payload);
  return response.data;
}

/**
 * Obtém a configuração WPPConnect da clínica logada
 */
export async function getWPPConnectConfig(): Promise<{
  session_name: string;
}> {
  const response = await api.get('/webhook/wppconnect/config');
  return {
    session_name: response.data.session_name,
  };
}

/**
 * Obtém o QR Code para conectar o WhatsApp via WPPConnect
 */
export async function getWPPConnectQRCode(): Promise<{
  qrcode: string;
  session_name: string;
}> {
  const response = await api.post('/webhook/wppconnect/qrcode');
  return response.data;
}

/**
 * Obtém o status da conexão WPPConnect
 */
export async function getWPPConnectStatus(): Promise<{
  connected: boolean;
  status: string;
  session: string;
}> {
  const response = await api.get('/webhook/wppconnect/status');
  return response.data;
}

/**
 * Obtém dados do device conectado (WhatsApp) via WPPConnect
 */
export async function getWPPConnectDevice(): Promise<{
  id: string;
  name: string;
  phone?: string;
  profilePicUrl?: string;
  status: string;
  connected: boolean;
  isBusiness?: boolean;
  device: {
    sessionName: string;
    device_model: string;
    pushname?: string;
    phone?: string;
    wid?: string;
    platform?: string;
  };
}> {
  const response = await api.get('/webhook/wppconnect/device');
  return response.data;
}

/**
 * Desconecta a sessão WPPConnect (logout do WhatsApp)
 */
export async function disconnectWPPConnect(): Promise<{ message: string }> {
  const response = await api.post('/webhook/wppconnect/disconnect');
  return response.data;
}

/**
 * Reseta completamente a configuração WPPConnect da clínica
 */
export async function resetWPPConnect(): Promise<{ message: string }> {
  const response = await api.post('/webhook/wppconnect/reset');
  return response.data;
}

// ==========================================
// WAHA FUNCTIONS (WhatsApp HTTP API)
// ==========================================

/**
 * Verifica o status detalhado da sessão WAHA
 */
export async function getWahaSessionStatus(): Promise<{
  name: string;
  status: string;
  connected: boolean;
  needsQR: boolean;
  needsStart: boolean;
  me?: {
    id: string;
    pushName: string;
  };
}> {
  const response = await api.get('/webhook/whatsapp/waha/session-status');
  return response.data;
}

/**
 * Inicia uma sessão WAHA (se estiver STOPPED)
 */
export async function startWahaSession(): Promise<{
  message: string;
  sessionStatus: string;
  nextStep: string;
}> {
  const response = await api.post('/webhook/whatsapp/waha/start-session');
  return response.data;
}

export interface WahaPairingCodeResponse {
  mode: 'pairing' | 'connected';
  pairingCode?: string;
  code?: string;
  connected?: boolean;
  sessionName: string;
  status: string;
  message?: string;
}

/**
 * Solicita pairing code WAHA para conectar a sessão por número de telefone
 */
export async function requestWahaPairingCode(phoneNumber: string): Promise<WahaPairingCodeResponse> {
  const response = await api.post('/webhook/whatsapp/waha/request-code', {
    phone_number: phoneNumber
  });
  return response.data;
}

/**
 * Para/desconecta uma sessão WAHA (útil para resetar estado FAILED)
 */
export async function stopWahaSession(): Promise<{
  message: string;
}> {
  const response = await api.post('/webhook/whatsapp/disconnect');
  return response.data;
}

export async function connectWaha(sessionName = ''): Promise<{
  message: string;
  sessionName: string;
  status: string;
}> {
  const response = await api.post('/webhook/whatsapp/connect-waha', {
    session_name: sessionName,
  });
  return {
    ...response.data,
    sessionName: response.data.sessionName || response.data.session_name || sessionName,
    status: response.data.status || response.data.session_status || 'unknown',
  };
}

// ============= FUNÇÕES MULTI-PROVIDER =============

/**
 * Detecta qual provider WhatsApp está configurado para a clínica
 */
export function detectWhatsAppProvider(config?: any): WhatsAppProviderInfo | null {
  const rawConfig = config
    ? (config.whatsapp_config || config)
    : cachedClinicWhatsAppConfig;
  if (!rawConfig) {
    return null;
  }
  const whatsappConfig = sanitizeClinicWhatsAppConfig(rawConfig);

  // Prioridade: WAHA > Evolution > WPPConnect > Z-API
  if (whatsappConfig.waha_enabled && whatsappConfig.waha_session_name) {
    return {
      provider: 'waha',
      config: {
        session_name: whatsappConfig.waha_session_name,
      },
    };
  }

  if (whatsappConfig.evolution_instance_id) {
    return {
      provider: 'evolution',
      config: {
        instance_id: whatsappConfig.evolution_instance_id
      }
    };
  }

  if (whatsappConfig.wppconnect_session_name) {
    return {
      provider: 'wppconnect',
      config: {
        session_name: whatsappConfig.wppconnect_session_name,
      },
    };
  }

  if (whatsappConfig.zapi_instance_id && whatsappConfig.zapi_configured) {
    return {
      provider: 'zapi',
      config: {
        instance_id: whatsappConfig.zapi_instance_id,
      },
    };
  }

  return null;
}

async function resolveWhatsAppProvider(): Promise<WhatsAppProviderInfo | null> {
  const cachedProvider = detectWhatsAppProvider();
  if (cachedProvider) {
    return cachedProvider;
  }
  return detectWhatsAppProvider(await getExtendedClinicInfo());
}

/**
 * Envia mensagem de texto usando o provider detectado automaticamente
 */
export async function sendWhatsAppTextMultiProvider({ phone, message, localMessageId, replyToMessageId, contact_id }: SendTextParams): Promise<any> {
  const providerInfo = await resolveWhatsAppProvider();

  if (!providerInfo) {
    throw new Error('Nenhum provider WhatsApp configurado');
  }



  if (providerInfo.provider === 'zapi') {
    // Usa função existente para Z-API
    return await sendWhatsAppText({ phone, message, localMessageId, replyToMessageId, contact_id });
  }

  // Para outros providers, usa endpoint unificado do backend
  const rawOwnClientId = localStorage.getItem("client_id");
  const clinicId = localStorage.getItem("clinic_id");

  let finalClientId = rawOwnClientId;
  const url = `/webhook/send-text?client_id=${finalClientId}&clinic_id=${clinicId}`;

  const body: any = { phone, message, provider: providerInfo.provider };
  if (localMessageId) {
    body.localMessageId = localMessageId;
  }
  if (replyToMessageId) {
    body.replyToMessageId = replyToMessageId;
  }
  if (contact_id) {
    body.contact_id = contact_id;
  }

  const resp = await api.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  });

  return resp.data;
}

/**
 * Envia imagem usando o provider detectado automaticamente
 */
export async function sendWhatsAppImageMultiProvider(params: SendImageParams): Promise<any> {
  const providerInfo = await resolveWhatsAppProvider();

  if (!providerInfo) {
    throw new Error('Nenhum provider WhatsApp configurado');
  }



  if (providerInfo.provider === 'zapi') {
    // Usa função existente para Z-API
    return await sendWhatsAppImage(params);
  }

  // Para outros providers, usa endpoint unificado do backend
  const rawOwnClientId = localStorage.getItem("client_id");
  const clinicId = localStorage.getItem("clinic_id");

  let finalClientId = rawOwnClientId;
  const url = `/webhook/send-image?client_id=${finalClientId}&clinic_id=${clinicId}`;

  const body = { ...params, provider: providerInfo.provider };

  const resp = await api.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  });

  return resp.data;
}

/**
 * Envia áudio usando o provider detectado automaticamente
 */
export async function sendWhatsAppAudioMultiProvider(params: SendAudioParams): Promise<any> {
  const providerInfo = await resolveWhatsAppProvider();

  if (!providerInfo) {
    throw new Error('Nenhum provider WhatsApp configurado');
  }



  if (providerInfo.provider === 'zapi') {
    // Usa função existente para Z-API
    return await sendWhatsAppAudio(params);
  }

  // Para outros providers, usa endpoint unificado do backend
  const rawOwnClientId = localStorage.getItem("client_id");
  const clinicId = localStorage.getItem("clinic_id");

  let finalClientId = rawOwnClientId;
  const url = `/webhook/send-audio?client_id=${finalClientId}&clinic_id=${clinicId}`;

  const body = { ...params, provider: providerInfo.provider };

  const resp = await api.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  });

  return resp.data;
}

/**
 * Envia vídeo usando o provider detectado automaticamente
 */
export async function sendWhatsAppVideoMultiProvider(params: SendVideoParams): Promise<any> {
  const providerInfo = await resolveWhatsAppProvider();

  if (!providerInfo) {
    throw new Error('Nenhum provider WhatsApp configurado');
  }



  if (providerInfo.provider === 'zapi') {
    // Usa função existente para Z-API
    return await sendWhatsAppVideo(params);
  }

  // Para outros providers, usa endpoint unificado do backend
  const rawOwnClientId = localStorage.getItem("client_id");
  const clinicId = localStorage.getItem("clinic_id");

  let finalClientId = rawOwnClientId;
  const url = `/webhook/send-video?client_id=${finalClientId}&clinic_id=${clinicId}`;

  const body = { ...params, provider: providerInfo.provider };

  const resp = await api.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  });

  return resp.data;
}

// Função Multi-Provider para enviar NPS
export async function sendNPSMultiProvider(params: {
  phone: string;
  question: string;
  campaign_name?: string;
  provider?: WhatsAppProvider;
}) {
  // Obter configuração da clínica
  const clinicInfo = await getClinicInfo();
  const extendedClinicInfo = await getExtendedClinicInfo();

  console.log("[NPSMulti] extendedClinicInfo completo:");
  console.log("[NPSMulti] extendedClinicInfo.id:");

  if (!extendedClinicInfo || !extendedClinicInfo.whatsapp_config) {
    throw new Error('Configuração do WhatsApp não encontrada para esta clínica');
  }

  const whatsappConfig = extendedClinicInfo.whatsapp_config;
  const providerInfo = detectWhatsAppProvider(extendedClinicInfo);

  // Usar provider passado como parâmetro ou detectar automaticamente
  const selectedProvider = params.provider || providerInfo?.provider;

  // Obter ownClientId baseado no provider
  let rawOwnClientId = '';
  switch (selectedProvider) {
    case 'zapi':
      rawOwnClientId = whatsappConfig.zapi_instance_id || '';
      break;
    case 'waha':
      rawOwnClientId = whatsappConfig.waha_session_name || '';
      break;
    case 'evolution':
      rawOwnClientId = whatsappConfig.evolution_instance_id || '';
      break;
    case 'wppconnect':
      rawOwnClientId = whatsappConfig.wppconnect_session_name || '';
      break;
    default:
      throw new Error(`Provider WhatsApp não suportado: ${selectedProvider}`);
  }

  if (!rawOwnClientId) {
    throw new Error(`ID do WhatsApp não configurado para provider ${selectedProvider}`);
  }

  // Usar endpoint unificado multi-provider para NPS
  // O endpoint espera phone como query parameter, não no body
  const url = `/api/nps/send?clinic_id=${extendedClinicInfo.id}&phone=${params.phone}`;




  const body = {
    question: params.question,
    campaign_name: params.campaign_name || 'manual_chat',
    context: { provider: selectedProvider }  // Enviar provider no context
  };

  console.log("[NPSMulti] Body enviado:");
  console.log("[NPSMulti] URL completa:");

  const resp = await api.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  });

  return resp.data;
}

// Função Multi-Provider para enviar chamada
export async function sendCallMultiProvider(params: {
  phone: string;
  callDuration: number;
  provider?: WhatsAppProvider;
}) {
  // Obter configuração da clínica
  const clinicInfo = await getClinicInfo();
  const extendedClinicInfo = await getExtendedClinicInfo();

  if (!extendedClinicInfo || !extendedClinicInfo.whatsapp_config) {
    throw new Error('Configuração do WhatsApp não encontrada para esta clínica');
  }

  const whatsappConfig = extendedClinicInfo.whatsapp_config;
  const providerInfo = detectWhatsAppProvider(extendedClinicInfo);

  // Usar provider passado como parâmetro ou detectar automaticamente
  const selectedProvider = params.provider || providerInfo?.provider;

  // Obter ownClientId baseado no provider
  let rawOwnClientId = '';
  switch (selectedProvider) {
    case 'zapi':
      rawOwnClientId = whatsappConfig.zapi_instance_id || '';
      break;
    case 'waha':
      rawOwnClientId = whatsappConfig.waha_session_name || '';
      break;
    case 'evolution':
      rawOwnClientId = whatsappConfig.evolution_instance_id || '';
      break;
    case 'wppconnect':
      rawOwnClientId = whatsappConfig.wppconnect_session_name || '';
      break;
    default:
      throw new Error(`Provider WhatsApp não suportado: ${selectedProvider}`);
  }

  if (!rawOwnClientId) {
    throw new Error(`ID do WhatsApp não configurado para provider ${selectedProvider}`);
  }

  // Sanitizar ID para uso na URL
  let finalClientId = rawOwnClientId;

  // Rotas específicas por provider
  let url = '';
  switch (selectedProvider) {
    case 'zapi':
      url = `/api/z-api/send-call?client_id=${finalClientId}&clinic_id=${clinicInfo.id}`;
      break;
    case 'waha':
      url = `/api/waha/send-call?session=${finalClientId}&clinic_id=${clinicInfo.id}`;
      break;
    case 'evolution':
      url = `/api/evolution/send-call?instance=${finalClientId}&clinic_id=${clinicInfo.id}`;
      break;
    case 'wppconnect':
      url = `/api/wppconnect/send-call?session=${finalClientId}&clinic_id=${clinicInfo.id}`;
      break;
  }

  const body = {
    phone: params.phone,
    callDuration: params.callDuration,
    provider: selectedProvider
  };

  const resp = await api.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  });

  return resp.data;
}

// ================================================================
// 🎯 NOVA FUNCIONALIDADE: Áudio Direto WAHA (Otimizado)
// ================================================================

/**
 * Interface para envio direto de áudio via WAHA
 * Formato otimizado compatível com agents_sdk
 */
export interface WahaDirectAudioParams {
  phone: string;
  audioBlob: Blob;        // Blob direto, sem base64!
  session?: string;       // Backend descobre da clínica logada se não fornecido
  clinicId?: number;
  convert?: boolean;
  localMessageId?: string;
  contact_id?: number;
}

/**
 * Envia áudio diretamente via WAHA com formato otimizado
 *
 * 🔥 NOVO: Performance ~60% melhor que fluxo atual
 * - Envia Blob como multipart/form-data (sem base64)
 * - Formato compatível com agents_sdk
 * - Menor processamento no frontend
 *
 * @param params Parâmetros para envio do áudio
 * @returns Promise com resposta do WAHA
 *
 * @example
 * const blob = new Blob([audioData], { type: 'audio/webm' });
 * await sendWhatsAppAudioDirect({
 *   phone: "5521998332217",
 *   audioBlob: blob,
 *   session: "default",
 *   clinicId: 68
 * });
 */
export async function sendWhatsAppAudioDirect(params: WahaDirectAudioParams): Promise<any> {
  console.log("[WAHA Direct Audio] 🚀 Enviando áudio via WAHA (formato otimizado)");
  console.log("[WAHA Direct Audio] 📊 Parâmetros:");

  try {
    // Validar parâmetros
    if (!params.phone || !params.audioBlob) {
      throw new Error("Parâmetros obrigatórios ausentes: phone, audioBlob");
    }

    // Criar FormData para envio multipart (sem base64!)
    const formData = new FormData();
    formData.append('phone', params.phone);

    // Adicionar session apenas se fornecida (backend descobre automaticamente)
    if (params.session) {
      formData.append('session', params.session);
    }

    formData.append('audio', params.audioBlob, `voice.${params.audioBlob.type.split('/')[1] || 'webm'}`);
    formData.append('convert', String(params.convert ?? true));

    if (params.localMessageId) {
      formData.append('localMessageId', params.localMessageId);
    }
    if (params.contact_id) {
      formData.append('contact_id', String(params.contact_id));
    }

    // O clinic_id selecionado mantém o envio no mesmo escopo do Contact.
    const scopedClinicId =
      params.clinicId || Number(localStorage.getItem('clinic_id') || 0);
    if (scopedClinicId > 0) {
      formData.append('clinic_id', String(scopedClinicId));
    }

    console.log("[WAHA Direct Audio] 📤 Enviando requisição multipart...");
    console.log("[WAHA Direct Audio] 📦 FormData keys:");

    // Enviar para novo endpoint WAHA
    const response = await api.post('/api/waha/send-voice', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 segundos para upload de áudio
    });

    console.log("[WAHA Direct Audio] ✅ Resposta WAHA:");
    return response.data;

  } catch (error: any) {
    console.error("[WAHA Direct Audio] ❌ Erro ao enviar áudio:");

    handleApiError(error, 'Erro ao enviar áudio via WhatsApp');
    throw new Error('Unreachable');
  }
}

/**
 * Verifica se o provider WAHA está disponível e configurado
 * @returns Boolean indicando se WAHA pode ser usado
 */
export function isWahaAvailable(): boolean {
  const providerInfo = detectWhatsAppProvider();
  return providerInfo?.provider === 'waha' && !!providerInfo.config?.session_name;
}

/**
 * Obtém informações da sessão WAHA atual
 * @returns Informações da sessão ou null se não disponível
 */
export function getWahaSessionInfo(): { session: string; clinicId: number | undefined } | null {
  const providerInfo = detectWhatsAppProvider();

  // providerInfo.provider é 'waha' e config contém session_name
  if (providerInfo?.provider === 'waha' && providerInfo.config?.session_name) {
    const clinicId = parseInt(localStorage.getItem('clinic_id') || '0');
    return {
      session: providerInfo.config.session_name,
      clinicId: clinicId > 0 ? clinicId : undefined
    };
  }

  return null;
}

// --- Admin Resources Endpoints ---

export async function getTelnyxNumbers(): Promise<TelnyxNumber[]> {
  const response = await api.get('/api/admin/resources/telnyx/numbers');
  return response.data;
}

export async function assignTelnyxNumber(phoneNumber: string, clinicId: number): Promise<void> {
  await api.post('/api/admin/resources/telnyx/assign', { phone_number: phoneNumber, clinic_id: clinicId });
}

export async function getWhatsAppNumbers(): Promise<WhatsAppNumber[]> {
  const response = await api.get('/api/admin/resources/whatsapp/numbers');
  return response.data;
}

export async function assignWhatsAppNumber(phoneId: string, wabaId: string, clinicId: number, token?: string): Promise<void> {
  await api.post('/api/admin/resources/whatsapp/assign', { phone_id: phoneId, waba_id: wabaId, clinic_id: clinicId, token });
}

// --- Marketing Endpoints ---

export async function sendOfficialBlast(contactIds: number[], message: string): Promise<void> {
  await api.post('/marketing/send-official-blast', { contact_ids: contactIds, message });
}

export async function getAdminMe() {
  const response = await api.get('/api/admin/resources/me');
  return response.data;
}

export interface ActivateWhatsAppResponse {
  success: boolean;
  message: string;
  clinic_id: number;
  phone_id: string;
}

export async function activateWhatsAppNumber(
  phoneId: string,
  clinicId: number
): Promise<ActivateWhatsAppResponse> {
  const response = await api.post('/api/admin/resources/whatsapp/activate', {
    phone_id: phoneId,
    clinic_id: clinicId
  });
  return response.data;
}

export async function unassignWhatsAppNumber(clinicId: number): Promise<{ success: boolean; message: string; clinic_id: number; removed_phone_id: string }> {
  const response = await api.post('/api/admin/resources/whatsapp/unassign', { clinic_id: clinicId });
  return response.data;
}

export interface OfficialWhatsAppConfig {
  has_official: boolean;
  mode?: 'embedded_signup' | 'legacy_managed' | null;
  status?: 'not_connected' | 'provisioning' | 'connected' | 'action_required' | 'error' | 'legacy_connected' | 'disconnecting' | 'disconnect_error' | 'disconnected';
  business_id?: string;
  phone_id?: string;
  waba_id?: string;
  display_phone_number?: string;
  verified_name?: string;
  quality_rating?: string;
  platform_type?: string;
  // Business profile fields
  profile_picture_url?: string | null;
  about?: string;
  description?: string;
  address?: string;
  email?: string;
  vertical?: string;
  websites?: string[];
  provisioning_step?: string;
  token_expires_at?: string;
  last_checked_at?: string;
  last_error_code?: string;
  last_error?: string;
}

export async function getOfficialWhatsAppConfig(): Promise<OfficialWhatsAppConfig> {
  const response = await api.get('/api/integrations/whatsapp-official/connection');
  return response.data;
}

export interface WhatsAppEmbeddedSignupConfig {
  app_id: string;
  config_id: string;
  graph_api_version: string;
  version?: number;
  session_info_version?: number;
  feature?: string;
  feature_type?: string | null;
  setup?: Record<string, unknown>;
}

export interface WhatsAppOnboardingSession {
  session_id: string;
  session_token: string;
  expires_at: string;
}

export async function getWhatsAppEmbeddedSignupConfig(
  signal?: AbortSignal
): Promise<WhatsAppEmbeddedSignupConfig> {
  const response = await api.get('/api/integrations/whatsapp-official/config', { signal });
  return response.data;
}

export async function createWhatsAppOnboardingSession(
  signal?: AbortSignal
): Promise<WhatsAppOnboardingSession> {
  const response = await api.post(
    '/api/integrations/whatsapp-official/onboarding-session',
    {},
    { signal }
  );
  return response.data;
}

export async function completeWhatsAppEmbeddedSignup(payload: {
  session_id: string;
  session_token: string;
  code: string;
  business_id?: string;
  waba_id: string;
  phone_number_id: string;
}, signal?: AbortSignal): Promise<OfficialWhatsAppConfig> {
  const response = await api.post(
    '/api/integrations/whatsapp-official/complete',
    payload,
    { signal }
  );
  return response.data;
}

export async function refreshOfficialWhatsAppConnection(): Promise<OfficialWhatsAppConfig> {
  const response = await api.post('/api/integrations/whatsapp-official/refresh', {});
  return response.data;
}

export async function disconnectOfficialWhatsAppConnection(): Promise<void> {
  await api.post('/api/integrations/whatsapp-official/disconnect', {});
}

// ================================================================
// 📋 WHATSAPP TEMPLATES MANAGEMENT
// ================================================================

export interface WhatsAppTemplate {
  id: number;
  meta_template_id: string;
  name: string;
  category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
  language: string;
  status: 'APPROVED' | 'PENDING' | 'REJECTED' | 'PAUSED' | 'DISABLED' | 'DELETED' | 'PENDING_DELETION' | 'IN_APPEAL';
  components: any[];
  rejection_reason?: string;
  created_at?: string;
  updated_at?: string;
  synced_at?: string;
  use_case?: string;
}

export interface CreateTemplateData {
  name: string;
  category: string;
  language: string;
  components: any[];
  use_case?: string;
}

export interface SyncTemplatesResult {
  success: boolean;
  message: string;
  stats: {
    total?: number;
    added?: number;
    updated: number;
    not_found_in_meta?: number;
    category_changes?: Array<{
      name: string;
      old: string;
      new: string;
    }>;
  };
}

/**
 * Lista todos os templates de WhatsApp de uma clínica
 */
export async function listWhatsAppTemplates(
  clinicId: number,
  statusFilter?: string,
  useCaseFilter?: string,
): Promise<WhatsAppTemplate[]> {
  const params: Record<string, any> = { clinic_id: clinicId };
  if (statusFilter) {
    params.status_filter = statusFilter;
  }
  if (useCaseFilter) {
    params.use_case_filter = useCaseFilter;
  }
  const response = await api.get('/api/official/templates-official', { params });
  return response.data.templates || [];
}

/**
 * Cria um novo template de WhatsApp
 */
export async function createWhatsAppTemplate(
  clinicId: number,
  data: CreateTemplateData
): Promise<any> {
  const response = await api.post('/api/official/templates-official', data, {
    params: { clinic_id: clinicId }
  });
  return response.data;
}

/** Edita componentes sem apagar o template ou alterar sua identidade na Meta. */
export async function updateWhatsAppTemplate(
  clinicId: number,
  templateId: number,
  data: CreateTemplateData,
): Promise<any> {
  const response = await api.put(`/api/official/templates-official/${templateId}`, data, {
    params: { clinic_id: clinicId },
  });
  return response.data;
}

/** Amostra de aprovação: o handle retornado não é um media_id de envio. */
export async function uploadWhatsAppTemplateSample(
  clinicId: number,
  file: File,
): Promise<{ handle: string }> {
  const data = new FormData();
  data.append('file', file);
  const response = await api.post('/api/official/templates-official/sample', data, {
    params: { clinic_id: clinicId },
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

/**
 * Deleta um template de WhatsApp
 */
export async function deleteWhatsAppTemplate(
  clinicId: number,
  templateName: string,
  templateId?: number,
): Promise<void> {
  await api.delete(`/api/official/templates-official/${encodeURIComponent(templateName)}`, {
    params: { clinic_id: clinicId, template_id: templateId }
  });
}

/**
 * Sincroniza templates do Meta com o cache local
 */
export async function syncWhatsAppTemplates(
  clinicId: number
): Promise<SyncTemplatesResult> {
  const response = await api.post('/api/official/templates-official/sync', null, {
    params: { clinic_id: clinicId }
  });
  return response.data;
}

export type GoalMode = 'absolute' | 'percentage';
export type GoalCalculationBasis = 'revenue' | 'leads' | 'agendamentos' | 'comparecimentos' | 'vendas';
export type GoalPeriodType = 'monthly' | 'weekly' | 'daily';

export interface ClinicGoal {
  id: number;
  clinic_id: number;
  name: string;
  period_type: GoalPeriodType;
  start_date: string;
  end_date: string;
  mode: GoalMode;
  calculation_basis: GoalCalculationBasis;
  target_revenue?: number | null;
  target_leads?: number | null;
  target_agendamentos?: number | null;
  target_comparecimentos?: number | null;
  target_vendas?: number | null;
  target_percent_agendamentos?: number | null;
  target_percent_comparecimentos?: number | null;
  target_percent_vendas?: number | null;
  target_ticket_medio?: number | null;
  source_filter?: string | null;
  tag_ids: number[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ClinicGoalPayload {
  name: string;
  period_type: GoalPeriodType;
  start_date: string;
  end_date: string;
  mode: GoalMode;
  calculation_basis: GoalCalculationBasis;
  target_revenue?: number | null;
  target_leads?: number | null;
  target_agendamentos?: number | null;
  target_comparecimentos?: number | null;
  target_vendas?: number | null;
  target_percent_agendamentos?: number | null;
  target_percent_comparecimentos?: number | null;
  target_percent_vendas?: number | null;
  target_ticket_medio?: number | null;
  source_filter?: string | null;
  tag_ids: number[];
  active?: boolean;
}

export interface ClinicGoalListFilters {
  activeOnly?: boolean;
  startDate?: string;
  endDate?: string;
}

export async function getClinicGoals(filters: ClinicGoalListFilters = {}): Promise<ClinicGoal[]> {
  const params = new URLSearchParams();
  params.append('active_only', String(filters.activeOnly ?? true));
  if (filters.startDate) params.append('start_date', filters.startDate);
  if (filters.endDate) params.append('end_date', filters.endDate);

  const url = `/api/goals?${params.toString()}`;
  console.log("[getClinicGoals] Buscando metas:");

  try {
    const response = await api.get<ClinicGoal[]>(url);
    console.log("[getClinicGoals] Metas recebidas:");
    return response.data;
  } catch (error) {
    console.error("[getClinicGoals] Erro ao buscar metas:");
    throw error;
  }
}

export async function createClinicGoal(payload: ClinicGoalPayload): Promise<ClinicGoal> {
  console.log("[createClinicGoal] Criando meta:");

  try {
    const response = await api.post<ClinicGoal>('/api/goals', payload);
    console.log("[createClinicGoal] Meta criada:");
    return response.data;
  } catch (error) {
    console.error("[createClinicGoal] Erro ao criar meta:");
    throw error;
  }
}

export async function updateClinicGoal(goalId: number, payload: Partial<ClinicGoalPayload>): Promise<ClinicGoal> {
  console.log("[updateClinicGoal] Atualizando meta:");

  try {
    const response = await api.put<ClinicGoal>(`/api/goals/${goalId}`, payload);
    console.log("[updateClinicGoal] Meta atualizada:");
    return response.data;
  } catch (error) {
    console.error("[updateClinicGoal] Erro ao atualizar meta:");
    throw error;
  }
}

export async function deleteClinicGoal(goalId: number): Promise<void> {
  console.log("[deleteClinicGoal] Arquivando meta:");

  try {
    await api.delete(`/api/goals/${goalId}`);
    console.log("[deleteClinicGoal] Meta arquivada:");
  } catch (error) {
    console.error("[deleteClinicGoal] Erro ao arquivar meta:");
    throw error;
  }
}
